import { apiGet, apiDelete, apiDeleteBatch, apiPost, apiPostBatch, apiPatch } from './api.js';
import { wf, initViewState, saveViewState, loadViewState, isEmbedded } from './state.js';
import { zoomObjFromAttrs, zoomObjFromKey, buildSplitUrl } from '../components/splitView.js';
import { resolveCurrentMember } from '../services/members.js';
import { getProjectMilestonesSupabase, isMilestoneSupabaseRead } from '../services/milestones.js';
import { refreshMilestoneMirror } from '../services/schedule.js';
import { getJourneyEntriesForMilestone, deleteJourneyEntriesForMilestone } from '../services/journey.js';
import { resetMilestoneIssuance } from '../services/sheetIssuance.js';
import { BASE, TBL_DEL, TBL_PH, TBL_CL, TBL_CLT, TBL_TPL, TBL_BIM, TBL_SHEET, TBL_REV, TBL_CONCEPT, TBL_CONCEPT_UNIT, TBL_CONCEPT_LEVEL, TBL_ROOMS, TBL_ROOM_TPL, TBL_PROJ, TBL_CONTACTS, TBL_TIME, TBL_AGENCY_APPROVALS, TBL_DELTA_REVISIONS, INTERIOR_CONCEPT_TYPE_ID, DAPC_DRAWINGS_MS_TYPE_ID, PRE_DESIGN_RESEARCH_TYPE_ID, HOMEWORK_TYPE_ID, PRESENTATION_TYPE_IDS, PRESENTATION_TYPE_KEY, DRB_PRESENTATION_MS_TYPE_IDS, BIM_MS_TYPE_IDS, POST_PERMIT_TYPE_IDS, POST_REVISION_TYPE_IDS, SPEC_RELEASE_MS_TYPE_IDS, BID_CAMPAIGN_MS_TYPE_ID, VALUE_ENGINEERING_MS_TYPE_ID, NOT_REQUIRED_REASONS, CAT_SORT_ORDER, CAT_ORDER, catLabel, HIDDEN_CL_CATEGORIES, SCOPE_TYPE_OPTIONS, UNIT_TYPE_OPTIONS, SPOKES, PHASE_NAME_TO_TPL, ICO_TIME, ICO_LINK, ICO_EDIT, ICO_ADD, ICO_DATE, ICO_CHECK, ICO_SELECT, ICO_SKIP, ICO_EXPAND, ICO_COLLAPSE, ICO_SLACK, ICO_COPY, ICO_PIN, ICO_TEMPLATE, ICO_INFO, REFERENCE_ROOTS } from './constants.js';
import { fmt, daysFrom, money, initials, getFirst, ckSvg, lkSvg, todayStr, hlText, phKey, msKey, catKey, itemKey, sectionKey, rootSectionKey, showPill, isNodeCollapsed, getMemberName } from './utils.js';
import { ctx } from './ctx.js';
import { placeMenu } from '../lib/menuPlacement.js';
import { sweepSubtree } from './subtreeSweep.js';
import { isBlocked, blockedMessage, waitingOnText } from './blocked.js';
import { hasModule } from '../services/tenancy.js';

// A milestone waiting on a predecessor is read-only (Matt, 2026-09-24; outline/blocked.js). Every
// checklist write path below calls this first; true = refused (and the reason shown).
function refuseBlocked(msId) {
  if (msId && isBlocked(msId)) { showPill(blockedMessage(msId), true); return true; }
  return false;
}
import { buildSectionNode, buildRootSection } from './builders.js';
import { buildProductionChildren, loadProjectSheets, loadBim, buildBimRow, buildSheetRow } from './production.js';
import { buildDesignChildren, buildConceptRow, buildUnitRow, buildLevelRow, buildRoomRow, loadConcepts, loadUnits, loadLevels, loadRooms, loadUnassignedRooms } from './design.js';
import { getMsById, getPhaseById, getMsPhase, getMilestonesForPhase, buildScheduleChildren, loadSheets, buildPhaseZoom, buildMsZoom, buildPhaseNode, buildMsNode } from './schedule.js';
import { alertDialog, promptDialog, confirmDialog } from '../components/Dialog.jsx';
import { pickFiles, uploadReferenceDocument, announceReferenceDocumentsChanged } from '../services/referenceDocuments.js';
import { isMilestonePosted } from '../journey/eligibility.js';
import { submittalLabel } from '../services/submittals.js';
import { enrichPhaseRecords } from '../services/phaseFinancials.js';
import { getClosedProjects } from '../services/projectCloseout.js';
import { renameConcept, findConceptByName, getSelectedConceptId } from '../services/design.js';
import { getOpenProjects } from '../services/projects.js';
import { relinkProjectPdfInserts } from '../services/pdfInserts.js';

/* ── Multi-project caches ────────────────────────────────────────────────── */
var projectsCache      = null;  /* null=loading, []=loaded empty, [...]=loaded */
var phasesCache        = {};    /* phasesCache[projectId] = phases array        */
var milestonesCache    = {};    /* milestonesCache[projectId] = milestones array */

/* ── Per-project caches — reset on each initOutline call ────────────────── */
var contactsCache      = null;
var timeCache          = null;
var clCache            = {};
var conceptCache       = null;
var unitCache          = {};
var levelCache         = {};
var roomCache          = {};
var unassignedRoomsCache = {};
var criteriaCache      = null;
var parcelsCache       = null;
var specsCache         = null;
var projSheetCache     = null;
var bimCache           = null;
var sheetCache         = {};
var sheetRevCache      = {};
var sheetClCache       = {};
var teamContacts       = [];
var membersCache       = null;
var currentMemberCache = null;
var docCache           = {};
var gateCache          = {};   // sheet 3 gates — MilestoneGateSection.jsx (undefined/null/false/{...})

function resetCaches() {
  projectsCache      = null;
  ctx.projectsCache  = null;
  ctx.showClosedProjects  = false;
  ctx.closedProjectsCache = undefined;
  ctx.showOnHoldProjects  = false;
  phasesCache        = {};
  milestonesCache    = {};
  contactsCache      = null;
  timeCache          = null;
  clCache            = {};
  conceptCache       = null;
  unitCache          = {};
  levelCache         = {};
  roomCache          = {};
  unassignedRoomsCache = {};
  criteriaCache      = null;
  parcelsCache       = null;
  specsCache         = null;
  projSheetCache     = null;
  bimCache           = null;
  sheetCache         = {};
  sheetRevCache      = {};
  sheetClCache       = {};
  teamContacts       = [];
  membersCache       = null;
  currentMemberCache = null;
  docCache           = {};
  gateCache          = {};
  ctx.sketchRibbonCache = {}; // milestone Documents' sketch ribbons (MilestoneSections.jsx)
  if (ctx.resetLoader) ctx.resetLoader(); // wipe DataLoader state on full reset
}

/* ── Wire cache getter/setters onto ctx for domain modules ─────────────── */
(function() {
  var _pairs = [
    ['projectsCache',      function() { return projectsCache; },      function(v) { projectsCache = v; }],
    ['phasesCache',        function() { return phasesCache; },        function(v) { phasesCache = v; }],
    ['milestonesCache',    function() { return milestonesCache; },    function(v) { milestonesCache = v; }],
    ['contactsCache',       function() { return contactsCache; },       function(v) { contactsCache = v; }],
    ['timeCache',           function() { return timeCache; },           function(v) { timeCache = v; }],
    ['clCache',             function() { return clCache; },             function(v) { clCache = v; }],
    ['conceptCache',        function() { return conceptCache; },        function(v) { conceptCache = v; }],
    ['unitCache',           function() { return unitCache; },           function(v) { unitCache = v; }],
    ['levelCache',          function() { return levelCache; },          function(v) { levelCache = v; }],
    ['roomCache',           function() { return roomCache; },           function(v) { roomCache = v; }],
    ['unassignedRoomsCache',function() { return unassignedRoomsCache; },function(v) { unassignedRoomsCache = v; }],
    ['criteriaCache',       function() { return criteriaCache; },       function(v) { criteriaCache = v; }],
    ['parcelsCache',        function() { return parcelsCache; },        function(v) { parcelsCache = v; }],
    ['specsCache',          function() { return specsCache; },          function(v) { specsCache = v; }],
    ['projSheetCache',      function() { return projSheetCache; },      function(v) { projSheetCache = v; }],
    ['bimCache',            function() { return bimCache; },            function(v) { bimCache = v; }],
    ['sheetCache',          function() { return sheetCache; },          function(v) { sheetCache = v; }],
    ['sheetRevCache',       function() { return sheetRevCache; },       function(v) { sheetRevCache = v; }],
    ['sheetClCache',        function() { return sheetClCache; },        function(v) { sheetClCache = v; }],
    ['teamContacts',        function() { return teamContacts; },        function(v) { teamContacts = v; }],
    ['membersCache',        function() { return membersCache; },        function(v) { membersCache = v; }],
    ['currentMemberCache',  function() { return currentMemberCache; },  function(v) { currentMemberCache = v; }],
    ['docCache',            function() { return docCache; },            function(v) { docCache = v; }],
    ['gateCache',           function() { return gateCache; },           function(v) { gateCache = v; }]
  ];
  for (var _i = 0; _i < _pairs.length; _i++) {
    Object.defineProperty(ctx, _pairs[_i][0], {
      get: _pairs[_i][1], set: _pairs[_i][2], enumerable: true, configurable: true
    });
  }
})();

/* Project ⋯ → "Open Active Concept in AlderCAD" (Brandon, 2026-11-02): straight into the Selected
   Concept's model without walking to Reference > Design > Concepts. The tab opens synchronously
   so the popup blocker allows it, then goes to the model once the concept is known. */
function openActiveConceptModeler(projId) {
  if (!projId) return;
  var win = window.open('', '_blank');
  getSelectedConceptId(projId).then(function (conceptId) {
    if (!conceptId) {
      if (win) win.close();
      alertDialog({ title: 'No active concept', message: 'This project has no Selected Concept yet. Mark one "Set as selected" under Reference > Design > Concepts, then try again.' });
      return;
    }
    var url = '/modeler?conceptId=' + encodeURIComponent(conceptId) + '&projectId=' + encodeURIComponent(projId);
    if (win) win.location.href = url; else window.open(url, '_blank');
  }).catch(function () {
    if (win) win.close();
    alertDialog({ title: 'Could not open AlderCAD', message: 'The concepts for this project could not be read. Try again in a moment.' });
  });
}

export function initOutline(recordId, userEmail) {

  var rootEl = document.getElementById('aj-sc-root');

  var state = { phases:[], milestones:[] };

  initViewState();
  loadViewState(); // populate wf.collapsed before React components mount
  resetCaches();

  /* Backward compat: if a recordId is passed (direct /project?recordId=… link),
     treat it as an immediate project zoom — URL wins over saved state */
  if (recordId) {
    wf.zoomProjectId = recordId;
  }

  /* Deep link: a ?z=<base64 zoom> param targets a specific node — apply it on arrival, overriding
     the restored session state, so a shared URL lands on exactly that view. */
  try {
    var _zParam = new URLSearchParams(window.location.search).get('z');
    if (_zParam) applyEncodedZoom(_zParam);
  } catch (e) {}

  /* Peek/split iframes (EMBED — ?split=1) never load a collapse snapshot (see state.js's EMBED
     note), so wf.collapsed starts truly empty and every section defaults collapsed. That's the
     right default for a general split pane, but landing directly on a thread-detail zoom this way
     — e.g. the Triage Gate's "Open" button, EntityPeekModal in FlightPlanTab.jsx/TowerTab.jsx —
     is meant to show the thread's own content at a glance, not a stack of closed triangles (Matt,
     2026-09-10: "it takes me to an iframe showing the thread with the outline nodes collapsed").
     Pre-expand its known branches before the first render; harmless if a branch isn't rendered
     for this particular thread (THREAD_SUBTASKS_ENABLED, etc.) since nothing reads a stray key. */
  /* ?x=key1,key2 (embedded only): rows to open on arrival, the last one scrolled into view — On Deck's
     "Open" lands on the project's whole Schedule with the chosen milestone's phase and the milestone
     itself expanded (Matt, 2026-09-24: work the whole schedule from the lockout, Gantt included). */
  if (isEmbedded()) {
    try {
      var _xParam = new URLSearchParams(window.location.search).get('x');
      if (_xParam) {
        var _xKeys = _xParam.split(',').filter(Boolean);
        _xKeys.forEach(function (k) { wf.collapsed[k] = false; });
        var _last = _xKeys[_xKeys.length - 1], _tries = 0;
        var _scrollT = setInterval(function () {
          _tries++;
          var el = document.querySelector('.wf-node[data-key="' + _last.replace(/"/g, '') + '"]');
          if (el) { clearInterval(_scrollT); el.scrollIntoView({ block: 'center' }); }
          else if (_tries > 60) clearInterval(_scrollT);
        }, 250);
      }
    } catch (e) {}
  }

  if (isEmbedded() && wf.zoomSection && wf.zoomSection.indexOf('thread-detail:') === 0) {
    var _peekThreadId = wf.zoomSection.slice('thread-detail:'.length);
    wf.collapsed['thread-subtasks:' + _peekThreadId] = false;
    wf.collapsed['thread-acts:' + _peekThreadId] = false;
    wf.collapsed['thread-atts:' + _peekThreadId] = false;
    wf.collapsed['worknotes:thread:' + _peekThreadId] = false;
  }

  ctx.recordId = recordId || null;
  ctx.state = state;
  ctx.render = render;
  ctx.refreshSection = refreshSection;
  ctx.refreshRootChildren = refreshRootChildren;
  ctx.buildMenu = buildMenu;
  ctx.getCategoryNodes = getCategoryNodes;
  ctx.buildCatNode = buildCatNode;
  ctx.loadProjectData = loadProjectData;
  ctx.zoomToProject = zoomToProject;
  ctx.zoomToProjectList = zoomToProjectList;
  ctx.expandCollapseDescendants = expandCollapseDescendants;
  /* Lazy loads the engine's own toggleCollapse fires when a proj:/ms: node opens — the subtree sweep
     calls this for every node it opens so engine-owned children load like a hand click would. */
  ctx.ensureNodeLoaded = function(k) {
    if (!k) return;
    if (k.indexOf('proj:') === 0) { loadProjectData(k.substring(5)); return; }
    if (k.indexOf('ms:') === 0) {
      var m = k.substring(3);
      if (!clCache[m]) loadChecklist(m);
      if (bimCache === null) loadBim();
      if (sheetCache[m] === undefined) loadSheets(m);
    }
  };

  /* Global "refresh data" (/refresh command) — clears every module-level cache back to its
     sentinel value, reloads whatever the current view needs (project list, or the zoomed
     project's phases/milestones — which itself cascades into the zoomed milestone's
     checklist/sheets/bim per loadProjectData's _done()), and fires a remount event so
     self-contained React sections (which guard their own fetch with a mount-scoped ref, not a
     cache check) reset that guard and refetch too. This is the full set of state a page reload
     would rebuild, without the navigation/re-auth/JS-reload cost. */
  ctx.refreshAll = function() {
    resetCaches();
    if (wf.zoomProjectId) {
      loadProjectData(wf.zoomProjectId);
    } else {
      loadProjects();
    }
    render();
    window.dispatchEvent(new Event('aj-force-remount'));
  };

  ctx.refreshMsSheets = function(msId) {
    if (msId && sheetCache[msId] !== undefined) {
      delete sheetCache[msId];
      loadSheets(msId);
    }
  };

  /* Bust all project-scoped caches and reload — called by AddMilestoneWizard after creation */
  ctx.reloadProjectData = function(projId) {
    if (!projId) return;
    delete phasesCache[projId];
    delete milestonesCache[projId];
    loadProjectData(projId);
  };

  /* Member / team-contact rosters for React components (pickers, name resolution) */
  ctx.loadMembers         = loadMembers;
  ctx.loadTeamContacts    = loadTeamContacts;
  ctx.getCurrentMemberId  = function() { return getCurrentMemberId(); };

  ctx.zoomToRoot = function(name) {
    pushHistory();
    wf.zoomRoot = name;
    wf.zoomPhaseId = null; wf.zoomMsId = null; wf.zoomCat = null;
    wf.zoomItemId = null; wf.zoomConceptId = null; wf.zoomSection = null;
    render();
  };

  /**
   * Navigate to a section-level zoom key, pushing browser history so back/forward work.
   * Use this from React components and slash commands instead of manipulating wf directly.
   * @param {string} key — section key e.g. 'sec:prod:sheets', 'sec:prod:disc:Architectural'
   * @param {object} [opts]
   * @param {string} [opts.root='production'] — wf.zoomRoot to set
   * @param {string} [opts.phaseId=null]  — wf.zoomPhaseId to set (for milestone section zooms)
   */
  ctx.zoomToSection = function(key, opts) {
    pushHistory();
    opts = opts || {};
    wf.zoomRoot      = opts.root || 'production';
    wf.zoomSection   = key;
    wf.zoomPhaseId   = opts.phaseId || null;
    wf.zoomMsId      = null;
    wf.zoomCat       = null;
    wf.zoomItemId    = null;
    wf.zoomConceptId = null;
    saveViewState();
    render();
  };
  ctx.zoomTo = function(level, params) {
    pushHistory();
    if (level === 'concept-detail') {
      wf.zoomConceptDetailId = params.conceptId;
      wf.zoomSection = 'concept-detail:' + params.conceptId;
      wf.zoomRoot = 'design';
      wf.zoomPhaseId = null; wf.zoomMsId = null; wf.zoomCat = null; wf.zoomItemId = null;
    } else if (level === 'unit-detail') {
      wf.zoomSection = 'unit-detail:' + params.unitId + ':' + params.conceptId;
    } else if (level === 'level-detail') {
      wf.zoomSection = 'level-detail:' + params.levelId + ':' + params.unitId;
    } else if (level === 'parcel-detail') {
      wf.zoomSection = 'parcel-detail:' + params.parcelId;
      wf.zoomRoot = 'design';
      wf.zoomPhaseId = null; wf.zoomMsId = null; wf.zoomCat = null; wf.zoomItemId = null; wf.zoomConceptId = null;
    } else if (level === 'phase') {
      wf.zoomRoot = 'schedule';
      wf.zoomPhaseId = params.phId;
      wf.zoomMsId = null; wf.zoomCat = null; wf.zoomItemId = null; wf.zoomSection = null; wf.zoomConceptId = null;
    } else if (level === 'ms') {
      wf.zoomRoot = 'schedule';
      wf.zoomPhaseId = params.phId || wf.zoomPhaseId;
      wf.zoomMsId = params.msId;
      wf.zoomCat = null; wf.zoomItemId = null; wf.zoomSection = null; wf.zoomConceptId = null;
      if (!clCache[params.msId]) loadChecklist(params.msId);
    }
    render();
  };

  var timeOpen = false;
  var doneOpen = false;
  var primaryIdx = 0;
  var pendingFullExpand = {};
  var clOpen = false;
  /* ── New per-project lazy loaders ─────────────────────────────────────── */

  function syncProjectState(projectId) {
    state.phases    = phasesCache[projectId] || [];
    state.milestones = milestonesCache[projectId] || [];
    ctx.recordId    = projectId;
  }

  /* Set project context from a milestone ID when zooming in from home level (no wf.zoomProjectId). */
  function ensureProjectContext(msId) {
    if (wf.zoomProjectId || !msId) return;
    var mc = ctx.milestonesCache;
    if (!mc) return;
    var pids = Object.keys(mc);
    for (var i = 0; i < pids.length; i++) {
      var pid = pids[i];
      var pms = mc[pid] || [];
      for (var j = 0; j < pms.length; j++) {
        if (pms[j].id === msId) { syncProjectState(pid); wf.zoomProjectId = pid; return; }
      }
    }
  }

  function resetProjectScopedCaches() {
    bimCache = null; conceptCache = null;
    unitCache = {}; levelCache = {}; roomCache = {}; unassignedRoomsCache = {};
    criteriaCache = null; parcelsCache = null;
    specsCache = null; projSheetCache = null;
    sheetClCache = {};
    if (ctx.resetLoader) {             // wipe DataLoader sheet-scoped entries on project switch
      ctx.resetLoader('sheetCl:');
      ctx.resetLoader('sheetRev:');
      ctx.resetLoader('sheetTs:');
    }
  }

  function loadProjects() {
    projectsCache = null;
    // On Hold is still an active project — just paused (see docs/PROJECT_HOLD.md) — and its own
    // "On hold" pill (ProjectRow) is meant to render inline in this same list, not hide the row.
    // Excluding it here (bug, fixed 2026-08) meant a held project vanished from the home list
    // entirely on the next page load, which read as "the hold status disappeared."
    // getOpenProjects() paginates properly (apiGetAll) instead of a capped single page — a fixed
    // maxRecords here previously truncated the list once the firm passed that count, silently
    // dropping whichever project sorted last (bug, fixed 2026-08 — see services/projects.js).
    getOpenProjects().then(function(records) {
      projectsCache = records || [];
      ctx.projectsCache = projectsCache;  // expose to React components (e.g. ZoomTitle)
      render();
      wireSearchInput();
    }).catch(function() {
      projectsCache = [];
      ctx.projectsCache = [];
      render();
      wireSearchInput();
    });
  }

  // "Show closed projects" toggle on the home Projects ⋯ menu (Close Out Project — see
  // docs/PROJECT_CLOSEOUT.md). Completed/Cancelled projects are otherwise invisible everywhere
  // (home list, search, /go) since loadProjects() above scopes strictly to Contract Stage =
  // 'Executed' — this is the one place they stay findable. Fetched once and cached; React
  // (ProjectListSection/ZoomTitle) reads ctx.showClosedProjects / ctx.closedProjectsCache directly.
  function toggleClosedProjectsView() {
    ctx.showClosedProjects = !ctx.showClosedProjects;
    if (ctx.showClosedProjects && ctx.closedProjectsCache === undefined) {
      ctx.closedProjectsCache = null; // loading sentinel
      getClosedProjects().then(function(records) {
        ctx.closedProjectsCache = records || [];
        render();
      }).catch(function() {
        ctx.closedProjectsCache = [];
        render();
      });
    }
    render();
  }

  // "Show/hide on-hold projects" toggle on the home Projects ⋯ menu. On-hold projects are already
  // in ctx.projectsCache (getOpenProjects() fetches Executed + On Hold together — see
  // docs/PROJECT_HOLD.md), so this is a pure client-side filter, no extra fetch: ProjectListSection
  // hides a row when ctx.projectHoldIds has it and ctx.showOnHoldProjects is falsy. Defaults to
  // hidden (undefined is falsy) — an on-hold project is paused work, not part of the everyday list.
  function toggleOnHoldProjectsView() {
    ctx.showOnHoldProjects = !ctx.showOnHoldProjects;
    render();
  }

  function loadProjectData(projectId) {
    if (phasesCache[projectId] !== undefined) {
      /* Already loading or loaded — if complete and this is the active project, sync */
      if (phasesCache[projectId] !== null && milestonesCache[projectId] !== null
          && wf.zoomProjectId === projectId) {
        syncProjectState(projectId);
      }
      return;
    }
    phasesCache[projectId] = null;
    milestonesCache[projectId] = null;
    var _pend = 2;
    function _done() {
      _pend--;
      if (_pend !== 0) return;
      if (wf.zoomProjectId === projectId) {
        syncProjectState(projectId);
      }
      render();
      wireSearchInput();
      if (wf.zoomProjectId === projectId) {
        loadExpandedData();
        if (wf.zoomMsId) {
          if (!clCache[wf.zoomMsId]) loadChecklist(wf.zoomMsId);
          if (sheetCache[wf.zoomMsId] === undefined) loadSheets(wf.zoomMsId);
          if (bimCache === null) loadBim();
        }
        if (wf.zoomCat && wf.zoomMsId && !clCache[wf.zoomMsId]) loadChecklist(wf.zoomMsId);
      }
    }
    apiGet(TBL_PH,
      'filterByFormula=' + encodeURIComponent('FIND("' + projectId + '",ARRAYJOIN({Project Record UID (from Project)},","))') +
      '&sort%5B0%5D%5Bfield%5D=Sort+Order&sort%5B0%5D%5Bdirection%5D=asc&maxRecords=20',
      function(d) {
        var recs = d ? d.records : [];
        /* Fee/Hours Expended (and everything derived from them — EBR, Fee Remaining) are corrected
           against live Supabase time entries before caching — the Airtable rollup they used to read
           is frozen since the entries cutover. See services/phaseFinancials.js. */
        enrichPhaseRecords(recs).then(function(enriched) {
          phasesCache[projectId] = enriched;
          _done();
        }).catch(function(e) {
          console.error('[loadProjectData] phase enrichment failed', e && e.message);
          phasesCache[projectId] = recs;
          _done();
        });
      });
    // Milestones: Supabase read-flip path (flagged, off by default) or the Airtable path. Both write the
    // same { id, fields } records into milestonesCache so every downstream consumer is unaffected.
    if (isMilestoneSupabaseRead()) {
      getProjectMilestonesSupabase(projectId)
        .then(function(recs) { milestonesCache[projectId] = recs || []; _done(); })
        .catch(function(e) { console.error('[loadProjectData] Supabase milestone read failed', e && e.message); milestonesCache[projectId] = []; _done(); });
    } else {
      apiGet(TBL_DEL,
        'filterByFormula=' + encodeURIComponent('FIND("' + projectId + '",ARRAYJOIN({Project Record UID from Project},","))') +
        '&sort%5B0%5D%5Bfield%5D=Latest+Finish+(LFT)&sort%5B0%5D%5Bdirection%5D=asc&maxRecords=200',
        function(d) { milestonesCache[projectId] = d ? d.records : []; _done(); });
    }
  }

  function zoomToProject(projectId) {
    pushHistory();
    resetProjectScopedCaches();
    wf.zoomProjectId = projectId;
    wf.zoomRoot = null; wf.zoomPhaseId = null; wf.zoomMsId = null;
    wf.zoomCat = null; wf.zoomItemId = null; wf.zoomSection = null; wf.zoomConceptId = null;
    /* Sync state immediately if already loaded, otherwise load */
    if (phasesCache[projectId] !== null && phasesCache[projectId] !== undefined
        && milestonesCache[projectId] !== null && milestonesCache[projectId] !== undefined) {
      syncProjectState(projectId);
    } else {
      state.phases = []; state.milestones = [];
      ctx.recordId = projectId;
      loadProjectData(projectId);
    }
    render();
    saveViewState();
  }

  function zoomToProjectList() {
    pushHistory();
    wf.zoomProjectId = null;
    wf.zoomRoot = null; wf.zoomPhaseId = null; wf.zoomMsId = null;
    wf.zoomCat = null; wf.zoomItemId = null; wf.zoomSection = null; wf.zoomConceptId = null;
    state.phases = []; state.milestones = [];
    ctx.recordId = null;
    render();
    saveViewState();
  }

  function wireCheckboxDelegation() {
    document.addEventListener('change', function(e) {
      if (!e.target || !e.target.classList.contains('wf-item-cb')) return;
      var cb = e.target;
      var itemId = cb.getAttribute('data-itemid');
      var msId = cb.getAttribute('data-msid');
      var idx = wf.selected.indexOf(itemId);
      if (cb.checked && idx === -1) wf.selected.push(itemId);
      else if (!cb.checked && idx !== -1) wf.selected.splice(idx, 1);
      refreshCats(msId);
      updateSelectFloatBar();
    });
  }

  function wireSearchInput() {
    /* Attach directly to current element - called after every render */
    var si = document.getElementById("wf-search-inp");
    if (!si || si._searchWired) return;
    si._searchWired = true;
    si.addEventListener("input", function() {
      wf.searchQuery = si.value;
      clearTimeout(wf._searchTimer);
      wf._searchTimer = setTimeout(function() {
        renderOutlineOnly();
        /* Re-focus and re-wire since outline rebuilt */
        var inp = document.getElementById("wf-search-inp");
        if (inp) { inp.focus(); }
        wireSearchInput();
      }, 500);
    });
    si.addEventListener("keydown", function(e) { e.stopPropagation(); });
  }

  /* Resolve current Clerk user → Airtable Member record.
   * Runs concurrently with loadProjects — does not block the initial render.
   * Once resolved, wf.currentMemberId and ctx.currentMember are available
   * everywhere (My Work / crew chips, slash commands, personalized filtering). */
  if (userEmail) {
    resolveCurrentMember(userEmail).then(function(member) {
      if (member) {
        wf.currentMemberId = member.id;
        ctx.currentMember  = member;
        render();
      }
    });
  }

  /* Wire checkbox delegation immediately (doesn't need data) */
  wireCheckboxDelegation();
  loadMembers(function(){});
  loadTeamContacts(function(){});

  /* Load projects list — renders project-list view when done */
  loadProjects();

  /* If a project is active (from URL or saved state), load its data */
  if (wf.zoomProjectId) {
    ctx.recordId = wf.zoomProjectId;
    loadProjectData(wf.zoomProjectId);
  }

  function computeBanner() {
    var ms = state.milestones[primaryIdx];
    if (!ms) return null;
    var f=ms.fields, urgency=f['Urgency Message']||'', lft=f['Latest Finish (LFT)']||'', da2=daysFrom(lft);
    var completed=parseInt(f['Completed Checklist Items']||0), incomplete=parseInt(f['Incomplete Checklist Items']||0), total=completed+incomplete;
    if (urgency.indexOf('IMPOSSIBLE')!==-1) return { level:'red', eyebrow:'Course correct', text:'This milestone cannot be completed as scheduled.', sub:'Reschedule or reassign before continuing.', btn:'Open record', btnUrl:f['Open on App']||'#' };
    if (da2!==null&&da2<0) return { level:'red', eyebrow:'Overdue', text:(f['Full Deliverable Name']||'Milestone')+' is '+Math.abs(da2)+' days past due.', sub:completed+' of '+total+' checklist items complete.', btn:'Work checklist', btnAction:'openChecklist' };
    if (urgency.indexOf('URGENT')!==-1||(da2!==null&&da2<=2)) return { level:'amber', eyebrow:'Due soon', text:(f['Full Deliverable Name']||'Milestone')+' is due '+(da2===0?'today':'in '+da2+' days')+'.', sub:completed+' of '+total+' checklist items complete.', btn:'Work checklist', btnAction:'openChecklist' };
    if (urgency.indexOf('HEATING')!==-1) return { level:'amber', eyebrow:'Heating up', text:(f['Full Deliverable Name']||'Milestone')+' is due '+fmt(lft)+'.', sub:'Keep it moving.', btn:'Work checklist', btnAction:'openChecklist' };
    return null;
  }

  /*                                           
     SIGNAL + RENDER ENGINE
                                             */

  var focusMsIdx = null;  /* null = show signal feed; number = show focus card */
  var planningOpen = false;
  var capacityOpen = false;
  var selectedItemIds = [];
  var capacityData = null;

  /*    SIGNAL COMPUTATION    */
  function computeSignals() {
    var signals = [];

    /* Phase mismatches */
    state.phases.forEach(function(ph) {
      var f = ph.fields;
      var allowTime = f['Allow Time Entries'] === true;
      var openMs = parseInt(f['Open Milestones'] || 0);
      var name = f['Name'] || f['Phase Name'] || 'Phase';
      if (allowTime && openMs === 0) {
        signals.push({ level: 'red', type: 'phase', eyebrow: 'Billing risk', text: name + ' has time entries open but no active milestones.', sub: 'Staff may be recording to a finished phase.', action: 'togglePhase', phaseId: ph.id, phaseName: name, allowTime: true });
      }
      if (!allowTime && openMs > 0) {
        signals.push({ level: 'amber', type: 'phase', eyebrow: 'Time not captured', text: name + ' has ' + openMs + ' active milestone' + (openMs > 1 ? 's' : '') + ' but time entries are off.', sub: 'Work may be happening without billing records.', action: 'togglePhase', phaseId: ph.id, phaseName: name, allowTime: false });
      }
    });

    /* Milestone signals */
    state.milestones.forEach(function(ms, idx) {
      var f = ms.fields;
      var urgency = f['Urgency Message'] || '';
      var lft = f['Latest Finish (LFT)'] || '';
      var da = daysFrom(lft);
      var name = f['Full Deliverable Name'] || f['Name'] || 'Milestone';
      var resp = f['Responsible (Text)'] || '';
      var completed = parseInt(f['Completed Checklist Items'] || 0);
      var total = completed + parseInt(f['Incomplete Checklist Items'] || 0);

      if (urgency.indexOf('IMPOSSIBLE') !== -1) {
        signals.push({ level: 'red', type: 'milestone', eyebrow: 'Impossible', text: name + ' cannot be completed as scheduled.', sub: 'Reschedule or reassign.', action: 'focusMs', msIdx: idx });
      } else if (da !== null && da < 0) {
        signals.push({ level: 'red', type: 'milestone', eyebrow: 'Overdue', text: name + ' is ' + Math.abs(da) + ' day' + (Math.abs(da) > 1 ? 's' : '') + ' past due.', sub: completed + ' of ' + total + ' checklist items complete.', action: 'focusMs', msIdx: idx });
      } else if (urgency.indexOf('URGENT') !== -1 || (da !== null && da <= 2)) {
        signals.push({ level: 'amber', type: 'milestone', eyebrow: 'Due soon', text: name + ' is due ' + (da === 0 ? 'today' : 'in ' + da + ' days') + '.', sub: completed + ' of ' + total + ' checklist items complete.', action: 'focusMs', msIdx: idx });
      }
      /* No "unassigned" signal: a queued milestone has no owner by design — the tower crews it
         at launch on the Flight Board (docs/FLOW_RECONCILIATION_PLAN.md). */
    });


    /* Sort: red first, then amber, then blue */
    signals.sort(function(a, b) {
      var ord = { red: 0, amber: 1, blue: 2 };
      return (ord[a.level] || 9) - (ord[b.level] || 9);
    });

    return signals;
  }


  /*                                           
     OUTLINE ENGINE
                                             */

  /* Outline state */
  var outlineState = {
    focusPhaseId: null,    /* null = show all phases */
    focusMsId: null,       /* null = show all milestones in phase */
    expandedPhases: {},    /* phaseId -> bool */
    expandedMs: {},        /* msId -> bool */
    expandedCats: {},      /* msId+cat -> bool */
    expandedItems: {},     /* itemId -> bool (shows inline actions) */
    editingLft: null,      /* msId being date-edited */
    resolvedPhases: {},    /* phaseId -> timeout handle (for undo) */
    dirtyMs: {},           /* msId -> bool (touched this session) */
    selectMode: null,      /* msId in select mode, or null */
    selectedItems: [],
    capacityMsId: null
  };

  /* PROJECT OUTLINE ENGINE */

  /*    RENDER    */
  function render() {
    /* Global "Hide done" filter → body class drives CSS that hides every complete/posted/skipped node
       (incl. checklist items) anywhere in the outline. Kept in sync on every render. */
    try { document.body.classList.toggle('aj-hide-done', !!(wf.hideDoneKeys && wf.hideDoneKeys.global)); } catch (e) {}
    /* Push initial state on very first render */
    var h = '<div class="wf-wrap' + (wf.suppressAnim ? ' wf-no-anim' : '') + '">';
    /* ZoomTitle is now a React component portaled into #aj-sc-zoom-title */
    h += '<div id="wf-outline-body">' + buildOutline() + '</div>';
    h += '</div>';
    clearAllKbHighlight(); /* clear before innerHTML so React section rows lose stale class */
    /* Keep the selection anchored by KEY across the rebuild: an explicit saved key (Enter/menu, ← back)
       wins, otherwise persist the current selection so plain arrowing survives a re-render. reapplyKb
       (on the outline-render we dispatch below) re-applies it once the new DOM — incl. React sections — settles. */
    wf.kbKey = _kbSavedKey || wf.kbKey || null;
    _kbSavedKey = null;
    wf.kbIdx = -1;
    rootEl.innerHTML = h;
    /* Render sticky chrome separately so it survives renderOutlineOnly(). A peek/split iframe
       (?split=1) shows only the outline itself — no breadcrumb trail — since its whole point is
       a bare, no-chrome glance at one node (Matt: "don't show the breadcrumb trail at the top").
       Hidden outright (not just emptied) so its own border-bottom doesn't leave a stray hairline. */
    var chromeEl = document.getElementById('aj-sc-chrome');
    if (chromeEl) {
      if (isEmbedded()) { chromeEl.style.display = 'none'; }
      else { chromeEl.style.display = ''; chromeEl.innerHTML = buildBreadcrumb(); }
    }
    wireOutline();
    wireSearchInput();
    saveViewState();
    syncUrl();
    window.dispatchEvent(new CustomEvent('outline-render'));
  }

  function renderOutlineOnly() {
    /* Surgical update - keeps chrome/search input untouched */
    var ob = document.getElementById('wf-outline-body');
    if (!ob) { if (!wf.searchQuery) render(); return; }
    clearAllKbHighlight();
    wf.kbKey = _kbSavedKey || wf.kbKey || null;   // persist/restore selection by key; reapplyKb re-applies
    _kbSavedKey = null;
    wf.kbIdx = -1;
    ob.innerHTML = buildOutline();
    wireOutlineBody();
    wireSearchInput();
    saveViewState();
    syncUrl();
    window.dispatchEvent(new CustomEvent('outline-render'));
  }

  /* Milestone-context intermediate crumb for a Design entity (concept/unit/level/room) zoom:
     DAPC Drawings milestones show "Design" (sec:<msId>:design-concept); every other milestone
     type (e.g. ICP) shows "Concepts" (sec:<msId>:concepts). Used by the concept-detail/unit-detail/
     level-detail/room-detail breadcrumb blocks below so the crumb matches whichever section the
     milestone's Design node actually renders. */
  function designMsSectionCrumb(msRecord) {
    var delTypeId = msRecord ? getFirst(msRecord.fields['Deliverable']) : '';
    var isDapc = delTypeId === DAPC_DRAWINGS_MS_TYPE_ID;
    return {
      label: isDapc ? 'Design' : 'Concepts',
      key: isDapc ? 'sec:' + wf.zoomMsId + ':design-concept' : 'sec:' + wf.zoomMsId + ':concepts',
    };
  }

  /*    BREADCRUMB    */
  function buildBreadcrumb() {
    var h = '<div class="wf-chrome">';

    /* Site nav toggle — far left */
    h += '<button class="wf-nav-btn wf-sitenav-btn" data-fn="toggleSiteNav" title="Menu">';
    h += '<svg width="14" height="14" viewBox="0 0 16 16" fill="none">';
    h += '<rect x="1" y="3.5" width="14" height="1.5" rx="0.75" fill="currentColor"/>';
    h += '<rect x="1" y="7.25" width="14" height="1.5" rx="0.75" fill="currentColor"/>';
    h += '<rect x="1" y="11" width="14" height="1.5" rx="0.75" fill="currentColor"/>';
    h += '</svg></button>';
    h += '<span class="wf-chrome-div"></span>';

    /* Nav controls */
    h += '<div class="wf-chrome-left">';
    h += '<button class="wf-nav-btn wf-nav-btn--back" data-fn="navBack" title="Back"><svg width="14" height="14" viewBox="0 0 16 16" fill="none"><polyline points="10,3 5,8 10,13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></button>';
    h += '<button class="wf-nav-btn wf-nav-btn--fwd" data-fn="navForward" title="Forward"><svg width="14" height="14" viewBox="0 0 16 16" fill="none"><polyline points="6,3 11,8 6,13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg></button>';
    h += '<button class="wf-nav-btn" data-fn="zoomTo" data-level="top" title="Home"><svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M2 7L8 2L14 7V14H10V10H6V14H2V7Z" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/></svg></button>';
    h += '<span class="wf-chrome-div"></span>';

    /* Breadcrumb - build ordered crumb array then render */
    var crumbs = [];

    /* Project name as the first crumb (the Home icon already covers "go to projects").
       Always shown + non-truncating so the user knows which project they're in. */
    if (wf.zoomProjectId) {
      var _projRec = null;
      if (projectsCache) {
        for (var _pi3 = 0; _pi3 < projectsCache.length; _pi3++) {
          if (projectsCache[_pi3].id === wf.zoomProjectId) { _projRec = projectsCache[_pi3]; break; }
        }
      }
      var _projLabel = _projRec
        ? (((_projRec.fields['Project Number'] || '') + ' ' + (_projRec.fields['Project Name'] || '')).trim() || 'Project')
        : 'Project';
      var _inProject = !!(wf.zoomRoot || wf.zoomPhaseId || wf.zoomMsId || wf.zoomSection || wf.zoomConceptId);
      if (_inProject) {
        crumbs.push({ label: _projLabel, fn: 'data-fn="zoomTo" data-level="project-home" data-projid="' + wf.zoomProjectId + '"', cls: 'wf-bc-proj' });
      } else {
        crumbs.push({ label: _projLabel, fn: null, cls: 'wf-bc-proj' });
      }
    } else if (wf.zoomRoot || wf.zoomConceptId || wf.zoomPhaseId || wf.zoomSection) {
      /* Fallback for no-project context (shouldn't normally occur) */
      crumbs.push({ label: 'Home', fn: 'data-fn="zoomTo" data-level="top"' });
    } else {
      /* Home — reads as a zoom into the Projects collection */
      crumbs.push({ label: 'Projects', fn: null });
    }

    /* Root section (Schedule / Design / Production) */
    /* Determine section parent when zoomed into a section */
    var secParent = null; /* 'schedule', 'design', 'production', or null if under a milestone */
    var secTypeLabel = '';
    /* Drawing-sheet zoom detection — populated below if zoomSection is a sheet key */
    var _shZoom = null; /* { owner, sheetId, isCl, name } when in a sheet zoom */
    if (wf.zoomSection) {
      /* Thread category zoom: Project > Reference > Threads > <Type>
         (the Reference crumb is auto-spliced before the Threads root crumb) */
      if (wf.zoomSection.indexOf('sec:threads:cat:') === 0) {
        var _tcType = wf.zoomSection.slice('sec:threads:cat:'.length);
        crumbs.push({ label: 'Threads', fn: 'data-fn="zoomTo" data-level="root" data-rootname="threads"' });
        crumbs.push({ label: _tcType, fn: null });
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('thread-detail:') === 0) {
        /* Individual thread: Project > Reference > Threads > <Type> > <Title>
           (the Reference crumb is auto-spliced before the Threads root crumb) */
        var _thId = wf.zoomSection.slice('thread-detail:'.length);
        var _thRec = (ctx.threadCache || []).filter(function(x){ return x.id === _thId; })[0];
        var _thType = _thRec ? (_thRec.fields['Type'] || 'Misc. Items') : '';
        crumbs.push({ label: 'Threads', fn: 'data-fn="zoomTo" data-level="root" data-rootname="threads"' });
        if (_thType) crumbs.push({ label: _thType, fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:threads:cat:' + _thType + '"' });
        crumbs.push({ label: _thRec ? (_thRec.fields['Title'] || 'Thread') : 'Thread', fn: null });
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('meeting-detail:') === 0) {
        /* Meeting minutes: Project > Reference > Meetings > <Type · Date>
           (the Reference crumb is auto-spliced before the Meetings root crumb) */
        var _mtgId = wf.zoomSection.slice('meeting-detail:'.length);
        var _mtgRec = (ctx.meetingCache || []).filter(function(x){ return x.id === _mtgId; })[0];
        var _mtgLabel = 'Meeting';
        if (_mtgRec) {
          var _mtgType = _mtgRec.fields['Type'] || 'Meeting';
          var _mtgDate = _mtgRec.fields['Meeting Date String'] || '';
          _mtgLabel = _mtgType + (_mtgDate ? ' · ' + _mtgDate : '');
        }
        crumbs.push({ label: 'Meetings', fn: 'data-fn="zoomTo" data-level="root" data-rootname="meetings"' });
        crumbs.push({ label: _mtgLabel, fn: null });
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('refdoc-grp:') === 0) {
        /* Reference Documents group: Project > Reference > Documents > <kit item | Other files> */
        var _rdgId = wf.zoomSection.slice('refdoc-grp:'.length);
        var _rdgC = ctx.refDocCache && ctx.refDocCache[wf.zoomProjectId];
        var _rdgDoc = ((_rdgC && _rdgC.documents) || []).filter(function(d){ return (d.fact_id || 'none') === _rdgId; })[0];
        var _rdgLabel = _rdgId === 'none' ? 'Other files' : (_rdgDoc && _rdgDoc.fact_name) || 'Kit item';
        if (_rdgLabel.length > 60) _rdgLabel = _rdgLabel.slice(0, 58) + '…';
        crumbs.push({ label: 'Documents', fn: 'data-fn="zoomTo" data-level="root" data-rootname="documents"' });
        crumbs.push({ label: _rdgLabel, fn: null });
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('meeting-cat:') === 0) {
        /* Meeting category focus: Project > Reference > Meetings > <Type> */
        var _mcType = wf.zoomSection.slice('meeting-cat:'.length);
        crumbs.push({ label: 'Meetings', fn: 'data-fn="zoomTo" data-level="root" data-rootname="meetings"' });
        crumbs.push({ label: _mcType, fn: null });
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('sec:team:group:') === 0) {
        /* Team group focus: Project > Reference > Team > <Alder James|Client|Subconsultants> */
        var _tgKey = wf.zoomSection.slice('sec:team:group:'.length);
        var _tgLabel = _tgKey === 'alder-james' ? 'Alder James' : _tgKey === 'client' ? 'Client' : _tgKey === 'general-contractor' ? 'General Contractor' : _tgKey === 'local-resources' ? 'Local Resources' : 'Subconsultants';
        crumbs.push({ label: 'Team', fn: 'data-fn="zoomTo" data-level="root" data-rootname="team"' });
        crumbs.push({ label: _tgLabel, fn: null });
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('sec:team:shop:') === 0 || wf.zoomSection.indexOf('sec:team:printorder:') === 0) {
        /* Print shop / print order focus: Project > Reference > Team > Local Resources > <Shop> [> <order>] */
        var _poOrders = ctx.printOrderCache || [];
        var _poOrder = null;
        var _poShopId = '';
        if (wf.zoomSection.indexOf('sec:team:printorder:') === 0) {
          var _poId = wf.zoomSection.slice('sec:team:printorder:'.length);
          for (var _poI = 0; _poI < _poOrders.length; _poI++) { if (_poOrders[_poI].id === _poId) { _poOrder = _poOrders[_poI]; break; } }
          _poShopId = _poOrder ? _poOrder.org_airtable_id : '';
        } else {
          _poShopId = wf.zoomSection.slice('sec:team:shop:'.length);
        }
        var _poShopName = _poOrder ? (_poOrder.org_name || 'Print shop') : 'Print shop';
        var _poOrgs = ctx.teamOrganizations || [];
        for (var _poJ = 0; _poJ < _poOrgs.length; _poJ++) { if (_poOrgs[_poJ].id === _poShopId) { _poShopName = _poOrgs[_poJ].fields['Organization Name'] || _poShopName; break; } }
        crumbs.push({ label: 'Team', fn: 'data-fn="zoomTo" data-level="root" data-rootname="team"' });
        crumbs.push({ label: 'Local Resources', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:team:group:local-resources"' });
        if (_poOrder) {
          crumbs.push({ label: _poShopName, fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:team:shop:' + _poShopId + '"' });
          crumbs.push({ label: 'Order ' + (_poOrder.sent_at ? String(_poOrder.sent_at).slice(0, 10) : ''), fn: null });
        } else {
          crumbs.push({ label: _poShopName, fn: null });
        }
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('sec:team:org:') === 0) {
        /* Subconsultant org focus: Project > Reference > Team > Subconsultants > <Org> */
        var _toOrgId = wf.zoomSection.slice('sec:team:org:'.length);
        var _toOrgs = (ctx.teamOrganizations || []);
        var _toOrgRec = null;
        for (var _toI = 0; _toI < _toOrgs.length; _toI++) { if (_toOrgs[_toI].id === _toOrgId) { _toOrgRec = _toOrgs[_toI]; break; } }
        var _toLabel = _toOrgRec ? (_toOrgRec.fields['Organization Name'] || 'Organization') : 'Organization';
        crumbs.push({ label: 'Team', fn: 'data-fn="zoomTo" data-level="root" data-rootname="team"' });
        crumbs.push({ label: 'Subconsultants', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:team:group:subconsultants"' });
        crumbs.push({ label: _toLabel, fn: null });
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('candidate-detail:') === 0 ||
                 wf.zoomSection.indexOf('rfp-detail:') === 0 ||
                 wf.zoomSection.indexOf('proposal-detail:') === 0) {
        /* Consultant candidate / RFP round / proposal focus:
           Project > Reference > Team > Subconsultants > Candidates > <candidate> [> RFP sent <date> [> Proposal – $<fee>]] */
        var _cRfpData = ctx.consultantRfpCache || { rfps: [], proposals: [] };
        var _cResolveCandidate = function(pcId) {
          var _pc = null, _pcs = ctx.teamProjectConsultants || [];
          for (var _pci = 0; _pci < _pcs.length; _pci++) { if (_pcs[_pci].id === pcId) { _pc = _pcs[_pci]; break; } }
          if (!_pc) return { id: pcId, label: 'Candidate' };
          var _pcOrg = null, _orgs = ctx.teamOrganizations || [];
          for (var _oi = 0; _oi < _orgs.length; _oi++) { if (_orgs[_oi].id === _pc.orgId) { _pcOrg = _orgs[_oi]; break; } }
          var _pcLabel = (_pc.consultantService || 'Consultant') + ' - ' + (_pcOrg ? (_pcOrg.fields['Organization Name'] || 'Unknown') : 'Unknown');
          return { id: pcId, label: _pcLabel };
        };
        crumbs.push({ label: 'Team', fn: 'data-fn="zoomTo" data-level="root" data-rootname="team"' });
        crumbs.push({ label: 'Subconsultants', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:team:group:subconsultants"' });
        crumbs.push({ label: 'Candidates', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:team:group:subconsultants"' });

        if (wf.zoomSection.indexOf('candidate-detail:') === 0) {
          var _cdId = wf.zoomSection.slice('candidate-detail:'.length);
          crumbs.push({ label: _cResolveCandidate(_cdId).label, fn: null });
        } else if (wf.zoomSection.indexOf('rfp-detail:') === 0) {
          var _rdId = wf.zoomSection.slice('rfp-detail:'.length);
          var _rdRfp = null, _rdRfps = _cRfpData.rfps || [];
          for (var _rdi = 0; _rdi < _rdRfps.length; _rdi++) { if (_rdRfps[_rdi].id === _rdId) { _rdRfp = _rdRfps[_rdi]; break; } }
          var _rdCand = _cResolveCandidate(_rdRfp ? _rdRfp.project_consultant_airtable_id : '');
          crumbs.push({ label: _rdCand.label, fn: 'data-fn="zoomTo" data-level="candidate-detail" data-candidateid="' + _rdCand.id + '"' });
          crumbs.push({ label: 'RFP sent ' + (_rdRfp && _rdRfp.sent_at ? String(_rdRfp.sent_at).slice(0, 10) : ''), fn: null });
        } else {
          var _pdId = wf.zoomSection.slice('proposal-detail:'.length);
          var _pdProposal = null, _pdProposals = _cRfpData.proposals || [];
          for (var _pdi = 0; _pdi < _pdProposals.length; _pdi++) { if (_pdProposals[_pdi].id === _pdId) { _pdProposal = _pdProposals[_pdi]; break; } }
          var _pdCand = _cResolveCandidate(_pdProposal ? _pdProposal.project_consultant_airtable_id : '');
          crumbs.push({ label: _pdCand.label, fn: 'data-fn="zoomTo" data-level="candidate-detail" data-candidateid="' + _pdCand.id + '"' });
          if (_pdProposal && _pdProposal.rfp_id) {
            var _pdRfp = null, _pdRfps = _cRfpData.rfps || [];
            for (var _pri = 0; _pri < _pdRfps.length; _pri++) { if (_pdRfps[_pri].id === _pdProposal.rfp_id) { _pdRfp = _pdRfps[_pri]; break; } }
            crumbs.push({ label: 'RFP sent ' + (_pdRfp && _pdRfp.sent_at ? String(_pdRfp.sent_at).slice(0, 10) : ''), fn: 'data-fn="zoomTo" data-level="rfp-detail" data-rfpid="' + _pdProposal.rfp_id + '"' });
          }
          var _pdFee = _pdProposal && _pdProposal.proposed_fee != null ? '$' + Number(_pdProposal.proposed_fee).toLocaleString('en-US', { maximumFractionDigits: 0 }) : 'Proposal';
          crumbs.push({ label: _pdFee, fn: null });
        }
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('sec:meeting:') === 0) {
        /* Meeting sub-section: Project > Reference > Meetings > <meeting> > <Meeting Details|Threads> */
        var _smParts = wf.zoomSection.split(':');           /* ['sec','meeting',<id>,<sub>] */
        var _smId = _smParts[2] || '', _smSub = _smParts[3] || '';
        var _smRec = (ctx.meetingCache || []).filter(function(x){ return x.id === _smId; })[0];
        var _smLabel = 'Meeting';
        if (_smRec) { var _smT = _smRec.fields['Type'] || 'Meeting', _smD = _smRec.fields['Meeting Date String'] || ''; _smLabel = _smT + (_smD ? ' · ' + _smD : ''); }
        crumbs.push({ label: 'Meetings', fn: 'data-fn="zoomTo" data-level="root" data-rootname="meetings"' });
        crumbs.push({ label: _smLabel, fn: 'data-fn="zoomTo" data-level="meeting-detail" data-meetingid="' + _smId + '"' });
        crumbs.push({ label: _smSub === 'details' ? 'Meeting Details' : 'Threads', fn: null });
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('concept-detail:') === 0) {
        var _cdBcId = wf.zoomSection.split(':')[1] || '';
        var _cdBcConcept = null;
        for (var _cdBci = 0; _cdBci < (conceptCache||[]).length; _cdBci++) {
          if (conceptCache[_cdBci].id === _cdBcId) { _cdBcConcept = conceptCache[_cdBci]; break; }
        }
        var _cdBcName = _cdBcConcept ? (_cdBcConcept.fields['Parti Pris Title'] || 'Concept') : 'Concept';
        if (wf.zoomMsId) {
          var _cdBcMs = getMsById(wf.zoomMsId);
          var _cdBcMsName = _cdBcMs ? (_cdBcMs.fields['Full Deliverable Name'] || _cdBcMs.fields['Name'] || 'Milestone') : 'Milestone';
          var _cdBcSec = designMsSectionCrumb(_cdBcMs);
          crumbs.push({ label: 'Schedule', fn: 'data-fn="zoomTo" data-level="root" data-rootname="schedule"' });
          crumbs.push({ label: _cdBcMsName, fn: 'data-fn="zoomTo" data-level="ms" data-msid="' + wf.zoomMsId + '" data-pid="' + (wf.zoomPhaseId||'') + '"' });
          crumbs.push({ label: _cdBcSec.label, fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="' + _cdBcSec.key + '"' });
        } else {
          crumbs.push({ label: 'Design', fn: 'data-fn="zoomTo" data-level="root" data-rootname="design"' });
          crumbs.push({ label: 'Concepts', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:design:concepts"' });
        }
        crumbs.push({ label: _cdBcName, fn: null });
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('room-detail:') === 0) {
        var _rdBcParts = wf.zoomSection.split(':');
        var _rdBcRoomId = _rdBcParts[1] || '';
        var _rdBcLevelId = _rdBcParts[2] || '';
        var _rdBcRoom = (roomCache[_rdBcLevelId] || []).filter(function(r) { return r.id === _rdBcRoomId; })[0];
        var _rdBcLevel = null;
        Object.keys(levelCache).forEach(function(uid) {
          (levelCache[uid]||[]).forEach(function(l) { if (l.id === _rdBcLevelId) _rdBcLevel = l; });
        });
        var _rdBcUnit = null, _rdBcConceptId = '';
        Object.keys(unitCache).forEach(function(cid) {
          (unitCache[cid]||[]).forEach(function(u) {
            (levelCache[u.id]||[]).forEach(function(l) { if (l.id === _rdBcLevelId) { _rdBcUnit = u; _rdBcConceptId = cid; } });
          });
        });
        var _rdBcConcept = null;
        for (var _rdBci = 0; _rdBci < (conceptCache||[]).length; _rdBci++) {
          if (conceptCache[_rdBci].id === _rdBcConceptId) { _rdBcConcept = conceptCache[_rdBci]; break; }
        }
        var _rdBcRoomName = _rdBcRoom ? (_rdBcRoom.fields['Room'] || _rdBcRoom.fields['Room Number and Name'] || 'Room') : 'Room';
        var _rdBcLevelName = _rdBcLevel ? (_rdBcLevel.fields['Name'] || 'Level') : 'Level';
        var _rdBcUnitName = _rdBcUnit ? (_rdBcUnit.fields['Unit Type'] || 'Unit') : 'Unit';
        var _rdBcConceptName = _rdBcConcept ? (_rdBcConcept.fields['Parti Pris Title'] || 'Concept') : 'Concept';
        if (wf.zoomMsId) {
          var _rdBcMs = getMsById(wf.zoomMsId);
          var _rdBcMsName = _rdBcMs ? (_rdBcMs.fields['Full Deliverable Name'] || 'Milestone') : 'Milestone';
          var _rdBcSec = designMsSectionCrumb(_rdBcMs);
          crumbs.push({ label: 'Schedule', fn: 'data-fn="zoomTo" data-level="root" data-rootname="schedule"' });
          crumbs.push({ label: _rdBcMsName, fn: 'data-fn="zoomTo" data-level="ms" data-msid="' + wf.zoomMsId + '" data-pid="' + (wf.zoomPhaseId||'') + '"' });
          crumbs.push({ label: _rdBcSec.label, fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="' + _rdBcSec.key + '"' });
        } else {
          crumbs.push({ label: 'Design', fn: 'data-fn="zoomTo" data-level="root" data-rootname="design"' });
          crumbs.push({ label: 'Concepts', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:design:concepts"' });
        }
        crumbs.push({ label: _rdBcConceptName, fn: 'data-fn="zoomTo" data-level="concept-detail" data-conceptid="' + _rdBcConceptId + '"' });
        crumbs.push({ label: 'Units', fn: 'data-fn="zoomTo" data-level="concept-units" data-conceptid="' + _rdBcConceptId + '"' });
        crumbs.push({ label: _rdBcUnitName, fn: 'data-fn="zoomTo" data-level="unit-detail" data-unitid="' + (_rdBcUnit ? _rdBcUnit.id : '') + '" data-conceptid="' + _rdBcConceptId + '"' });
        crumbs.push({ label: 'Levels', fn: 'data-fn="zoomTo" data-level="unit-levels" data-unitid="' + (_rdBcUnit ? _rdBcUnit.id : '') + '"' });
        crumbs.push({ label: _rdBcLevelName, fn: 'data-fn="zoomTo" data-level="level-detail" data-levelid="' + _rdBcLevelId + '" data-unitid="' + (_rdBcUnit ? _rdBcUnit.id : '') + '"' });
        crumbs.push({ label: 'Rooms', fn: 'data-fn="zoomTo" data-level="level-rooms" data-levelid="' + _rdBcLevelId + '"' });
        crumbs.push({ label: _rdBcRoomName, fn: null });
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('sec:concept:') === 0) {
        var _scParts = wf.zoomSection.split(':');
        var _scConceptId = _scParts[2] || '';
        var _scType = _scParts[3] || 'units';
        var _scConcept = null;
        for (var _sci = 0; _sci < (conceptCache||[]).length; _sci++) {
          if (conceptCache[_sci].id === _scConceptId) { _scConcept = conceptCache[_sci]; break; }
        }
        var _scConceptName = _scConcept ? (_scConcept.fields['Parti Pris Title'] || 'Concept') : 'Concept';
        if (wf.zoomMsId) {
          var _scMs = getMsById(wf.zoomMsId);
          var _scMsName = _scMs ? (_scMs.fields['Full Deliverable Name'] || 'Milestone') : 'Milestone';
          var _scSec = designMsSectionCrumb(_scMs);
          crumbs.push({ label: 'Schedule', fn: 'data-fn="zoomTo" data-level="root" data-rootname="schedule"' });
          crumbs.push({ label: _scMsName, fn: 'data-fn="zoomTo" data-level="ms" data-msid="' + wf.zoomMsId + '" data-pid="' + (wf.zoomPhaseId||'') + '"' });
          crumbs.push({ label: _scSec.label, fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="' + _scSec.key + '"' });
        } else {
          crumbs.push({ label: 'Design', fn: 'data-fn="zoomTo" data-level="root" data-rootname="design"' });
          crumbs.push({ label: 'Concepts', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:design:concepts"' });
        }
        crumbs.push({ label: _scConceptName, fn: 'data-fn="zoomTo" data-level="concept-detail" data-conceptid="' + _scConceptId + '"' });
        var _scTypeLabels = { units: 'Units', metrics: 'Development Metrics', demolition: 'Demolition', grading: 'Grading & Paving', pools: 'Pools and Spas', sitescope: 'Site Scope' };
        crumbs.push({ label: _scTypeLabels[_scType] || 'Units', fn: null });
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('sec:unit:') === 0) {
        var _ulParts = wf.zoomSection.split(':');
        var _ulUnitId = _ulParts[2] || '';
        var _ulUnit = null, _ulConceptId = '';
        Object.keys(unitCache).forEach(function(cid) {
          (unitCache[cid]||[]).forEach(function(u) { if (u.id === _ulUnitId) { _ulUnit = u; _ulConceptId = cid; } });
        });
        var _ulConcept = null;
        for (var _uli = 0; _uli < (conceptCache||[]).length; _uli++) {
          if (conceptCache[_uli].id === _ulConceptId) { _ulConcept = conceptCache[_uli]; break; }
        }
        var _ulUnitName = _ulUnit ? (_ulUnit.fields['Unit Type'] || 'Unit') : 'Unit';
        var _ulConceptName = _ulConcept ? (_ulConcept.fields['Parti Pris Title'] || 'Concept') : 'Concept';
        if (wf.zoomMsId) {
          var _ulMs = getMsById(wf.zoomMsId);
          var _ulMsName = _ulMs ? (_ulMs.fields['Full Deliverable Name'] || 'Milestone') : 'Milestone';
          var _ulSec = designMsSectionCrumb(_ulMs);
          crumbs.push({ label: 'Schedule', fn: 'data-fn="zoomTo" data-level="root" data-rootname="schedule"' });
          crumbs.push({ label: _ulMsName, fn: 'data-fn="zoomTo" data-level="ms" data-msid="' + wf.zoomMsId + '" data-pid="' + (wf.zoomPhaseId||'') + '"' });
          crumbs.push({ label: _ulSec.label, fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="' + _ulSec.key + '"' });
        } else {
          crumbs.push({ label: 'Design', fn: 'data-fn="zoomTo" data-level="root" data-rootname="design"' });
          crumbs.push({ label: 'Concepts', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:design:concepts"' });
        }
        crumbs.push({ label: _ulConceptName, fn: 'data-fn="zoomTo" data-level="concept-detail" data-conceptid="' + _ulConceptId + '"' });
        crumbs.push({ label: 'Units', fn: 'data-fn="zoomTo" data-level="concept-units" data-conceptid="' + _ulConceptId + '"' });
        crumbs.push({ label: _ulUnitName, fn: 'data-fn="zoomTo" data-level="unit-detail" data-unitid="' + _ulUnitId + '" data-conceptid="' + _ulConceptId + '"' });
        crumbs.push({ label: 'Levels', fn: null });
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('sec:level:') === 0) {
        var _lrParts = wf.zoomSection.split(':');
        var _lrLevelId = _lrParts[2] || '';
        var _lrLevel = null;
        Object.keys(levelCache).forEach(function(uid) {
          (levelCache[uid]||[]).forEach(function(l) { if (l.id === _lrLevelId) _lrLevel = l; });
        });
        var _lrUnit = null, _lrConceptId = '';
        Object.keys(unitCache).forEach(function(cid) {
          (unitCache[cid]||[]).forEach(function(u) {
            (levelCache[u.id]||[]).forEach(function(l) { if (l.id === _lrLevelId) { _lrUnit = u; _lrConceptId = cid; } });
          });
        });
        var _lrConcept = null;
        for (var _lri = 0; _lri < (conceptCache||[]).length; _lri++) {
          if (conceptCache[_lri].id === _lrConceptId) { _lrConcept = conceptCache[_lri]; break; }
        }
        var _lrLevelName = _lrLevel ? (_lrLevel.fields['Name'] || 'Level') : 'Level';
        var _lrUnitName = _lrUnit ? (_lrUnit.fields['Unit Type'] || 'Unit') : 'Unit';
        var _lrConceptName = _lrConcept ? (_lrConcept.fields['Parti Pris Title'] || 'Concept') : 'Concept';
        if (wf.zoomMsId) {
          var _lrMs = getMsById(wf.zoomMsId);
          var _lrMsName = _lrMs ? (_lrMs.fields['Full Deliverable Name'] || 'Milestone') : 'Milestone';
          var _lrSec = designMsSectionCrumb(_lrMs);
          crumbs.push({ label: 'Schedule', fn: 'data-fn="zoomTo" data-level="root" data-rootname="schedule"' });
          crumbs.push({ label: _lrMsName, fn: 'data-fn="zoomTo" data-level="ms" data-msid="' + wf.zoomMsId + '" data-pid="' + (wf.zoomPhaseId||'') + '"' });
          crumbs.push({ label: _lrSec.label, fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="' + _lrSec.key + '"' });
        } else {
          crumbs.push({ label: 'Design', fn: 'data-fn="zoomTo" data-level="root" data-rootname="design"' });
          crumbs.push({ label: 'Concepts', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:design:concepts"' });
        }
        crumbs.push({ label: _lrConceptName, fn: 'data-fn="zoomTo" data-level="concept-detail" data-conceptid="' + _lrConceptId + '"' });
        crumbs.push({ label: 'Units', fn: 'data-fn="zoomTo" data-level="concept-units" data-conceptid="' + _lrConceptId + '"' });
        crumbs.push({ label: _lrUnitName, fn: 'data-fn="zoomTo" data-level="unit-detail" data-unitid="' + (_lrUnit ? _lrUnit.id : '') + '" data-conceptid="' + _lrConceptId + '"' });
        crumbs.push({ label: 'Levels', fn: 'data-fn="zoomTo" data-level="unit-levels" data-unitid="' + (_lrUnit ? _lrUnit.id : '') + '"' });
        crumbs.push({ label: _lrLevelName, fn: 'data-fn="zoomTo" data-level="level-detail" data-levelid="' + _lrLevelId + '" data-unitid="' + (_lrUnit ? _lrUnit.id : '') + '"' });
        crumbs.push({ label: 'Rooms', fn: null });
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('parcel-detail:') === 0) {
        var _pcdParts = wf.zoomSection.split(':');
        var _pcdId = _pcdParts[1] || '';
        var _pcdSub = _pcdParts[2] || '';
        var _pcdRec = (ctx.parcelDetailCache && ctx.parcelDetailCache[_pcdId] && ctx.parcelDetailCache[_pcdId].parcel)
          || (parcelsCache || []).filter(function(x){ return x.id === _pcdId; })[0];
        var _pcdName = _pcdRec ? (_pcdRec.fields['APN'] || _pcdRec.fields['Name'] || 'Parcel') : 'Parcel';
        crumbs.push({ label: 'Design', fn: 'data-fn="zoomTo" data-level="root" data-rootname="design"' });
        crumbs.push({ label: 'Parcels', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:design:parcels"' });
        if (_pcdSub === 'agencies') {
          crumbs.push({ label: _pcdName, fn: 'data-fn="zoomTo" data-level="parcel-detail" data-parcelid="' + _pcdId + '"' });
          crumbs.push({ label: 'Agencies', fn: null });
        } else {
          crumbs.push({ label: _pcdName, fn: null });
        }
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('unit-detail:') === 0) {
        var _udBcParts = wf.zoomSection.split(':');
        var _udBcUnitId = _udBcParts[1] || '';
        var _udBcConceptId = _udBcParts[2] || '';
        var _udBcUnit = null;
        Object.keys(unitCache).forEach(function(cid) {
          (unitCache[cid]||[]).forEach(function(u) { if (u.id === _udBcUnitId) _udBcUnit = u; });
        });
        var _udBcConcept = null;
        for (var _udBci = 0; _udBci < (conceptCache||[]).length; _udBci++) {
          if (conceptCache[_udBci].id === _udBcConceptId) { _udBcConcept = conceptCache[_udBci]; break; }
        }
        var _udBcUnitName = _udBcUnit ? (_udBcUnit.fields['Unit Type'] || 'Unit') : 'Unit';
        var _udBcConceptName = _udBcConcept ? (_udBcConcept.fields['Parti Pris Title'] || 'Concept') : 'Concept';
        if (wf.zoomMsId) {
          var _udBcMs = getMsById(wf.zoomMsId);
          var _udBcMsName = _udBcMs ? (_udBcMs.fields['Full Deliverable Name'] || 'Milestone') : 'Milestone';
          var _udBcSec = designMsSectionCrumb(_udBcMs);
          crumbs.push({ label: 'Schedule', fn: 'data-fn="zoomTo" data-level="root" data-rootname="schedule"' });
          crumbs.push({ label: _udBcMsName, fn: 'data-fn="zoomTo" data-level="ms" data-msid="' + wf.zoomMsId + '" data-pid="' + (wf.zoomPhaseId||'') + '"' });
          crumbs.push({ label: _udBcSec.label, fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="' + _udBcSec.key + '"' });
        } else {
          crumbs.push({ label: 'Design', fn: 'data-fn="zoomTo" data-level="root" data-rootname="design"' });
          crumbs.push({ label: 'Concepts', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:design:concepts"' });
        }
        crumbs.push({ label: _udBcConceptName, fn: 'data-fn="zoomTo" data-level="concept-detail" data-conceptid="' + _udBcConceptId + '"' });
        crumbs.push({ label: 'Units', fn: 'data-fn="zoomTo" data-level="concept-units" data-conceptid="' + _udBcConceptId + '"' });
        crumbs.push({ label: _udBcUnitName, fn: null });
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('level-detail:') === 0) {
        var _ldBcParts = wf.zoomSection.split(':');
        var _ldBcLevelId = _ldBcParts[1] || '';
        var _ldBcUnitId = _ldBcParts[2] || '';
        var _ldBcLevel = null;
        Object.keys(levelCache).forEach(function(uid) {
          (levelCache[uid]||[]).forEach(function(l) { if (l.id === _ldBcLevelId) _ldBcLevel = l; });
        });
        var _ldBcUnit = null;
        var _ldBcConceptId = '';
        Object.keys(unitCache).forEach(function(cid) {
          (unitCache[cid]||[]).forEach(function(u) { if (u.id === _ldBcUnitId) { _ldBcUnit = u; _ldBcConceptId = cid; } });
        });
        var _ldBcConcept = null;
        for (var _ldBci = 0; _ldBci < (conceptCache||[]).length; _ldBci++) {
          if (conceptCache[_ldBci].id === _ldBcConceptId) { _ldBcConcept = conceptCache[_ldBci]; break; }
        }
        var _ldBcLevelName = _ldBcLevel ? (_ldBcLevel.fields['Name'] || 'Level') : 'Level';
        var _ldBcUnitName  = _ldBcUnit  ? (_ldBcUnit.fields['Unit Type'] || 'Unit') : 'Unit';
        var _ldBcConceptName = _ldBcConcept ? (_ldBcConcept.fields['Parti Pris Title'] || 'Concept') : 'Concept';
        if (wf.zoomMsId) {
          var _ldBcMs = getMsById(wf.zoomMsId);
          var _ldBcMsName = _ldBcMs ? (_ldBcMs.fields['Full Deliverable Name'] || 'Milestone') : 'Milestone';
          var _ldBcSec = designMsSectionCrumb(_ldBcMs);
          crumbs.push({ label: 'Schedule', fn: 'data-fn="zoomTo" data-level="root" data-rootname="schedule"' });
          crumbs.push({ label: _ldBcMsName, fn: 'data-fn="zoomTo" data-level="ms" data-msid="' + wf.zoomMsId + '" data-pid="' + (wf.zoomPhaseId||'') + '"' });
          crumbs.push({ label: _ldBcSec.label, fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="' + _ldBcSec.key + '"' });
        } else {
          crumbs.push({ label: 'Design', fn: 'data-fn="zoomTo" data-level="root" data-rootname="design"' });
          crumbs.push({ label: 'Concepts', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:design:concepts"' });
        }
        crumbs.push({ label: _ldBcConceptName, fn: 'data-fn="zoomTo" data-level="concept-detail" data-conceptid="' + _ldBcConceptId + '"' });
        crumbs.push({ label: 'Units', fn: 'data-fn="zoomTo" data-level="concept-units" data-conceptid="' + _ldBcConceptId + '"' });
        crumbs.push({ label: _ldBcUnitName, fn: 'data-fn="zoomTo" data-level="unit-detail" data-unitid="' + _ldBcUnitId + '" data-conceptid="' + _ldBcConceptId + '"' });
        crumbs.push({ label: 'Levels', fn: 'data-fn="zoomTo" data-level="unit-levels" data-unitid="' + _ldBcUnitId + '"' });
        crumbs.push({ label: _ldBcLevelName, fn: null });
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('permit-detail:') === 0) {
        var _pdId = wf.zoomSection.split(':')[1] || '';
        var _pdRec = (ctx.permitCache || []).filter(function(x){ return x.id === _pdId; })[0];
        crumbs.push({ label: 'Permits', fn: 'data-fn="zoomTo" data-level="root" data-rootname="permits"' });
        crumbs.push({ label: _pdRec ? (_pdRec.type || _pdRec.name || 'Permit') : 'Permit', fn: null });
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('review-detail:') === 0) {
        var _rdId = wf.zoomSection.split(':')[1] || '';
        var _rdRec = (ctx.permitReviewCache || []).filter(function(x){ return x.id === _rdId; })[0];
        var _rdPermit = _rdRec ? (ctx.permitCache || []).filter(function(x){ return x.id === _rdRec.permitId; })[0] : null;
        crumbs.push({ label: 'Permits', fn: 'data-fn="zoomTo" data-level="root" data-rootname="permits"' });
        if (_rdPermit) crumbs.push({ label: _rdPermit.type || _rdPermit.name || 'Permit', fn: 'data-fn="zoomTo" data-level="permit-detail" data-permitid="' + _rdPermit.id + '"' });
        crumbs.push({ label: _rdRec ? (_rdRec.agency || _rdRec.name || 'Agency Review') : 'Agency Review', fn: null });
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('campaign-detail:') === 0) {
        var _cmId = wf.zoomSection.split(':')[1] || '';
        var _cmRec = (ctx.bidCampaignCache || []).filter(function(x){ return x.id === _cmId; })[0];
        crumbs.push({ label: 'Construction', fn: 'data-fn="zoomTo" data-level="root" data-rootname="construction"' });
        crumbs.push({ label: 'Bid Campaigns', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:construction:bid"' });
        crumbs.push({ label: _cmRec ? ('Bidding Campaign No. ' + (_cmRec.round_number != null ? _cmRec.round_number : '')) : 'Bid Campaign', fn: null });
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('bid-detail:') === 0) {
        var _bdId = wf.zoomSection.split(':')[1] || '';
        /* Find the bid + its campaign across the cached campaign details. */
        var _bdBid = null, _bdCampId = '';
        var _bdCache = ctx.bidCampaignDetailCache || {};
        Object.keys(_bdCache).forEach(function(cid){
          var d = _bdCache[cid];
          if (d && d.bids) { d.bids.forEach(function(b){ if (b.id === _bdId) { _bdBid = b; _bdCampId = cid; } }); }
        });
        if (!_bdBid) { var _bdz = (ctx.caZoomCache||{})[_bdId]; if (_bdz && typeof _bdz==='object' && _bdz.id===_bdId) { _bdBid = _bdz; _bdCampId = _bdz.campaign_id || ''; } }
        var _bdCamp = _bdCampId ? (ctx.bidCampaignCache || []).filter(function(x){ return x.id === _bdCampId; })[0] : null;
        crumbs.push({ label: 'Construction', fn: 'data-fn="zoomTo" data-level="root" data-rootname="construction"' });
        crumbs.push({ label: 'Bid Campaigns', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:construction:bid"' });
        if (_bdCamp) crumbs.push({ label: 'Bidding Campaign No. ' + (_bdCamp.round_number != null ? _bdCamp.round_number : ''), fn: 'data-fn="zoomTo" data-level="campaign-detail" data-campaignid="' + _bdCampId + '"' });
        crumbs.push({ label: _bdBid ? (_bdBid.bidder_name || 'Bid') : 'Bid', fn: null });
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('bim-system-detail:') === 0) {
        var _bsId = wf.zoomSection.split(':')[1] || '';
        var _bsRec = (ctx.bimCache || []).filter(function(x){ return x.id === _bsId; })[0];
        crumbs.push({ label: 'Design', fn: 'data-fn="zoomTo" data-level="root" data-rootname="design"' });
        crumbs.push({ label: 'BIM Systems', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:design:bim"' });
        crumbs.push({ label: _bsRec ? (_bsRec.name || (_bsRec.fields && _bsRec.fields['Name']) || 'BIM System') : 'BIM System', fn: null });
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('bim-element-detail:') === 0) {
        var _beId = wf.zoomSection.split(':')[1] || '';
        /* Element + parent system resolve from the system's element loader cache, falling
           back to the by-id zoom cache (deep link). */
        var _beEl = null, _beSysId = (ctx.bimElementSystem || {})[_beId] || '';
        if (_beSysId && ctx.loader) {
          var _beEls = ctx.loader.get('bimElements:' + _beSysId);
          if (_beEls && _beEls.filter) _beEl = _beEls.filter(function(e){ return e.id === _beId; })[0] || null;
        }
        var _beZoom = (ctx.bimZoomCache || {})[_beId];
        if (!_beEl && _beZoom) _beEl = _beZoom.element || null;
        if (!_beSysId && _beZoom && _beZoom.system) _beSysId = _beZoom.system.id;
        var _beSys = _beSysId ? (ctx.bimCache || []).filter(function(x){ return x.id === _beSysId; })[0] : null;
        if (!_beSys && _beZoom && _beZoom.system) _beSys = _beZoom.system;
        crumbs.push({ label: 'Design', fn: 'data-fn="zoomTo" data-level="root" data-rootname="design"' });
        crumbs.push({ label: 'BIM Systems', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:design:bim"' });
        if (_beSys) crumbs.push({ label: _beSys.name || (_beSys.fields && _beSys.fields['Name']) || 'BIM System', fn: 'data-fn="zoomTo" data-level="bim-system-detail" data-bimsystemid="' + (_beSys.id || _beSysId) + '"' });
        crumbs.push({ label: _beEl ? (_beEl.name || 'Element') : 'Element', fn: null });
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('bim-phase-detail:') === 0) {
        var _bpParts = wf.zoomSection.split(':');
        var _bpElId = _bpParts[1] || '';
        var _bpPhase = _bpParts[2] || '';
        /* Same element+system resolution as bim-element-detail, one level up. */
        var _bpEl = null, _bpSysId = (ctx.bimElementSystem || {})[_bpElId] || '';
        if (_bpSysId && ctx.loader) {
          var _bpEls = ctx.loader.get('bimElements:' + _bpSysId);
          if (_bpEls && _bpEls.filter) _bpEl = _bpEls.filter(function(e){ return e.id === _bpElId; })[0] || null;
        }
        var _bpZoom = (ctx.bimZoomCache || {})[_bpElId];
        if (!_bpEl && _bpZoom) _bpEl = _bpZoom.element || null;
        if (!_bpSysId && _bpZoom && _bpZoom.system) _bpSysId = _bpZoom.system.id;
        var _bpSys = _bpSysId ? (ctx.bimCache || []).filter(function(x){ return x.id === _bpSysId; })[0] : null;
        if (!_bpSys && _bpZoom && _bpZoom.system) _bpSys = _bpZoom.system;
        var _bpPhaseLabels = { sd: 'SD', dd: 'DD', cd: 'CD', ia: 'IA' };
        crumbs.push({ label: 'Design', fn: 'data-fn="zoomTo" data-level="root" data-rootname="design"' });
        crumbs.push({ label: 'BIM Systems', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:design:bim"' });
        if (_bpSys) crumbs.push({ label: _bpSys.name || (_bpSys.fields && _bpSys.fields['Name']) || 'BIM System', fn: 'data-fn="zoomTo" data-level="bim-system-detail" data-bimsystemid="' + (_bpSys.id || _bpSysId) + '"' });
        crumbs.push({ label: _bpEl ? (_bpEl.name || 'Element') : 'Element', fn: 'data-fn="zoomTo" data-level="bim-element-detail" data-bimelementid="' + _bpElId + '"' });
        crumbs.push({ label: (_bpPhaseLabels[_bpPhase] || _bpPhase.toUpperCase()) + ' Criteria', fn: null });
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('ve-effort:') === 0) {
        var _veId = wf.zoomSection.split(':')[1] || '';
        var _veRec = (ctx.veEffortCache || []).filter(function(x){ return x.id === _veId; })[0];
        crumbs.push({ label: 'Construction', fn: 'data-fn="zoomTo" data-level="root" data-rootname="construction"' });
        crumbs.push({ label: 'Value Engineering', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:construction:ve"' });
        crumbs.push({ label: _veRec ? ('VE Effort No. ' + (_veRec.effort_number != null ? _veRec.effort_number : '')) : 'VE Effort', fn: null });
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('ve-item:') === 0) {
        var _viId = wf.zoomSection.split(':')[1] || '';
        var _viItem = null, _viEffId = '';
        (ctx.veEffortCache || []).forEach(function(e){
          (e.ve_items || []).forEach(function(it){ if (it.id === _viId) { _viItem = it; _viEffId = e.id; } });
        });
        var _viEff = _viEffId ? (ctx.veEffortCache || []).filter(function(x){ return x.id === _viEffId; })[0] : null;
        crumbs.push({ label: 'Construction', fn: 'data-fn="zoomTo" data-level="root" data-rootname="construction"' });
        crumbs.push({ label: 'Value Engineering', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:construction:ve"' });
        if (_viEff) crumbs.push({ label: 'VE Effort No. ' + (_viEff.effort_number != null ? _viEff.effort_number : ''), fn: 'data-fn="zoomTo" data-level="ve-effort" data-veid="' + _viEffId + '"' });
        crumbs.push({ label: _viItem ? (_viItem.title || 'VE Item') : 'VE Item', fn: null });
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('contract-detail:') === 0) {
        var _ctId = wf.zoomSection.split(':')[1] || '';
        var _ctRec = (ctx.contractCache || []).filter(function(x){ return x.id === _ctId; })[0];
        crumbs.push({ label: 'Construction', fn: 'data-fn="zoomTo" data-level="root" data-rootname="construction"' });
        crumbs.push({ label: 'Construction Contracts', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:construction:contracts"' });
        crumbs.push({ label: _ctRec ? (_ctRec.contractor_name || 'Contract') : 'Contract', fn: null });
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('rfi-detail:') === 0) {
        var _rfId = wf.zoomSection.split(':')[1] || '';
        var _rfRec = null, _rfContractId = '';
        var _rfCache = ctx.rfiCache || {};
        Object.keys(_rfCache).forEach(function(cid){
          var list = _rfCache[cid];
          if (Array.isArray(list)) list.forEach(function(r){ if (r.id === _rfId) { _rfRec = r; _rfContractId = cid; } });
        });
        if (!_rfRec) { /* RFI reached via Schedule > Threads is in the thread-mount cache, not rfiCache */
          var _rfTc = ctx.threadCaItemCache || {};
          Object.keys(_rfTc).forEach(function(tid){ var e=_rfTc[tid]; if (e && e.kind==='rfi' && e.item && e.item.id===_rfId){ _rfRec=e.item; _rfContractId=e.item.contract_id||''; } });
        }
        if (!_rfRec) { var _rfz = (ctx.caZoomCache||{})[_rfId]; if (_rfz && typeof _rfz==='object' && _rfz.id===_rfId) { _rfRec=_rfz; _rfContractId=_rfz.contract_id||''; } }
        var _rfContract = _rfContractId ? (ctx.contractCache || []).filter(function(x){ return x.id === _rfContractId; })[0] : null;
        crumbs.push({ label: 'Construction', fn: 'data-fn="zoomTo" data-level="root" data-rootname="construction"' });
        crumbs.push({ label: 'Construction Contracts', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:construction:contracts"' });
        if (_rfContract) crumbs.push({ label: _rfContract.contractor_name || 'Contract', fn: 'data-fn="zoomTo" data-level="contract-detail" data-contractid="' + _rfContractId + '"' });
        crumbs.push({ label: _rfRec ? ('RFI ' + (_rfRec.rfi_number != null ? _rfRec.rfi_number : '') + (_rfRec.subject ? ' — ' + _rfRec.subject : '')) : 'RFI', fn: null });
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('submittal-detail:') === 0) {
        var _smId = wf.zoomSection.split(':')[1] || '';
        var _smRec = null, _smContractId = '';
        var _smCache = ctx.submittalCache || {};
        Object.keys(_smCache).forEach(function(cid){
          var list = _smCache[cid];
          if (Array.isArray(list)) list.forEach(function(x){ if (x.id === _smId) { _smRec = x; _smContractId = cid; } });
        });
        if (!_smRec) { /* submittal reached via Schedule > Threads is in the thread-mount cache */
          var _smTc = ctx.threadCaItemCache || {};
          Object.keys(_smTc).forEach(function(tid){ var e=_smTc[tid]; if (e && e.kind==='submittal' && e.item && e.item.id===_smId){ _smRec=e.item; _smContractId=e.item.contract_id||''; } });
        }
        if (!_smRec) { var _smz = (ctx.caZoomCache||{})[_smId]; if (_smz && typeof _smz==='object' && _smz.id===_smId) { _smRec=_smz; _smContractId=_smz.contract_id||''; } }
        var _smContract = _smContractId ? (ctx.contractCache || []).filter(function(x){ return x.id === _smContractId; })[0] : null;
        var _smLabel = _smRec ? submittalLabel(_smRec) : 'Submittal';
        crumbs.push({ label: 'Construction', fn: 'data-fn="zoomTo" data-level="root" data-rootname="construction"' });
        crumbs.push({ label: 'Construction Contracts', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:construction:contracts"' });
        if (_smContract) crumbs.push({ label: _smContract.contractor_name || 'Contract', fn: 'data-fn="zoomTo" data-level="contract-detail" data-contractid="' + _smContractId + '"' });
        crumbs.push({ label: _smLabel, fn: null });
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('asi-detail:') === 0) {
        var _asId = wf.zoomSection.split(':')[1] || '';
        var _asRec = null, _asContractId = '';
        var _asCache = ctx.asiCache || {};
        Object.keys(_asCache).forEach(function(cid){
          var list = _asCache[cid];
          if (Array.isArray(list)) list.forEach(function(x){ if (x.id === _asId) { _asRec = x; _asContractId = cid; } });
        });
        if (!_asRec) { var _asz = (ctx.caZoomCache||{})[_asId]; if (_asz && typeof _asz==='object' && _asz.id===_asId) { _asRec=_asz; _asContractId=_asz.contract_id||''; } }
        var _asContract = _asContractId ? (ctx.contractCache || []).filter(function(x){ return x.id === _asContractId; })[0] : null;
        var _asLabel = _asRec ? ('ASI ' + (_asRec.asi_number != null ? _asRec.asi_number : '') + (_asRec.subject ? ' — ' + _asRec.subject : '')) : 'ASI';
        crumbs.push({ label: 'Construction', fn: 'data-fn="zoomTo" data-level="root" data-rootname="construction"' });
        crumbs.push({ label: 'Construction Contracts', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:construction:contracts"' });
        if (_asContract) crumbs.push({ label: _asContract.contractor_name || 'Contract', fn: 'data-fn="zoomTo" data-level="contract-detail" data-contractid="' + _asContractId + '"' });
        crumbs.push({ label: _asLabel, fn: null });
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('spec-section-detail:') === 0) {
        var _ssId = wf.zoomSection.split(':')[1] || '';
        var _ssRec = (ctx.specSectionCache || []).filter(function(x){ return x.id === _ssId; })[0];
        if (!_ssRec) { var _ssz = (ctx.caZoomCache||{})[_ssId]; if (_ssz && typeof _ssz==='object' && _ssz.id===_ssId) _ssRec = _ssz; }
        var _ssLabel = _ssRec ? ([_ssRec.section_number, _ssRec.section_name].filter(Boolean).join(' — ')) : 'Spec Section';
        crumbs.push({ label: 'Production', fn: 'data-fn="zoomTo" data-level="root" data-rootname="production"' });
        crumbs.push({ label: 'Specifications', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:prod:specs"' });
        crumbs.push({ label: _ssLabel, fn: null });
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('proposal-request-detail:') === 0) {
        var _prId = wf.zoomSection.split(':')[1] || '';
        var _prRec = null, _prContractId = '';
        var _prCache = ctx.proposalRequestCache || {};
        Object.keys(_prCache).forEach(function(cid){
          var list = _prCache[cid];
          if (Array.isArray(list)) list.forEach(function(x){ if (x.id === _prId) { _prRec = x; _prContractId = cid; } });
        });
        if (!_prRec) { var _prz = (ctx.caZoomCache||{})[_prId]; if (_prz && typeof _prz==='object' && _prz.id===_prId) { _prRec = _prz; _prContractId = _prz.contract_id || ''; } }
        var _prContract = _prContractId ? (ctx.contractCache || []).filter(function(x){ return x.id === _prContractId; })[0] : null;
        var _prLabel = _prRec ? ((_prRec.pr_number ? _prRec.pr_number + ' — ' : '') + (_prRec.subject || 'Proposal Request')) : 'Proposal Request';
        crumbs.push({ label: 'Construction', fn: 'data-fn="zoomTo" data-level="root" data-rootname="construction"' });
        crumbs.push({ label: 'Construction Contracts', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:construction:contracts"' });
        if (_prContract) crumbs.push({ label: _prContract.contractor_name || 'Contract', fn: 'data-fn="zoomTo" data-level="contract-detail" data-contractid="' + _prContractId + '"' });
        crumbs.push({ label: _prLabel, fn: null });
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('sec:contract:') === 0) {
        var _ccRest = wf.zoomSection.slice('sec:contract:'.length);
        var _ccType = _ccRest.slice(_ccRest.lastIndexOf(':') + 1);
        var _ccId = _ccRest.slice(0, _ccRest.lastIndexOf(':'));
        var _ccRec = (ctx.contractCache || []).filter(function(x){ return x.id === _ccId; })[0];
        var _ccLabel = _ccType === 'rfis' ? 'RFIs' : _ccType === 'submittals' ? 'Submittals' : _ccType === 'asis' ? 'ASIs' : _ccType === 'prs' ? 'Proposal Requests' : _ccType;
        crumbs.push({ label: 'Construction', fn: 'data-fn="zoomTo" data-level="root" data-rootname="construction"' });
        crumbs.push({ label: 'Construction Contracts', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:construction:contracts"' });
        if (_ccRec) crumbs.push({ label: _ccRec.contractor_name || 'Contract', fn: 'data-fn="zoomTo" data-level="contract-detail" data-contractid="' + _ccId + '"' });
        crumbs.push({ label: _ccLabel, fn: null });
        secParent = '__skip__';
      } else if (wf.zoomSection.indexOf('sec:construction:') === 0) {
        var _csT = wf.zoomSection.slice('sec:construction:'.length);
        var _csL = _csT === 'bid' ? 'Bid Campaigns' : _csT === 've' ? 'Value Engineering' : _csT === 'contracts' ? 'Construction Contracts' : _csT;
        crumbs.push({ label: 'Construction', fn: 'data-fn="zoomTo" data-level="root" data-rootname="construction"' });
        crumbs.push({ label: _csL, fn: null });
        secParent = '__skip__';
      }
      var _sp = wf.zoomSection.split(':');
      var _sowner = _sp[1] || ''; /* 'prod', 'design', or a msId */
      var _stype = wf.zoomSection.lastIndexOf(':') !== -1 ? wf.zoomSection.slice(wf.zoomSection.lastIndexOf(':') + 1) : '';
      secTypeLabel = _stype==='checklist'?'Checklist':_stype==='bim'?'BIM Systems':_stype==='sheets'?'Drawing Sheets':_stype==='concepts'?'Concepts':_stype==='design-concept'?'Design':_stype==='criteria'?'Criteria':_stype==='parcels'?'Parcels':_stype==='specs'?'Specifications':_stype==='documents'?'Documents':_stype==='gate'?'Gate':_stype==='feeder'?'Feeder':_stype==='spec-versions'?'Specifications':_stype==='schedule-versions'?'Schedules':_stype==='threads-riding'?'Threads':_stype==='inspiration'?'Client Inspiration':_stype;
      if (_sowner === 'prod') secParent = 'production';
      else if (_sowner === 'design') secParent = 'design';
      /* else it is a milestone ID - secParent stays null, handled via zoomMsId path */

      /* Drawing-sheet zoom: sec:{owner}:sheet:{sheetId}[:cl] — resolve the sheet's
         real display name so the breadcrumb shows it instead of the record ID. */
      if (_sp[2] === 'sheet') {
        var _shSheetId = _sp[3] || '';
        var _shName = '';
        if (_sowner === 'prod') {
          for (var _shi = 0; _shi < (projSheetCache || []).length; _shi++) {
            if (projSheetCache[_shi].id === _shSheetId) {
              _shName = projSheetCache[_shi].fields['Sheet Number and Title'] || '';
              break;
            }
          }
        } else {
          var _shRevs = sheetCache[_sowner] || [];
          for (var _shj = 0; _shj < _shRevs.length; _shj++) {
            if (getFirst(_shRevs[_shj].fields['Drawing Sheet']) === _shSheetId) {
              _shName = getFirst(_shRevs[_shj].fields['Sheet Number and Title (from Drawing Sheet)']) || '';
              break;
            }
          }
        }
        _shZoom = { owner: _sowner, sheetId: _shSheetId, isCl: _sp[4] === 'cl', name: _shName || 'Sheet' };
      }
    }

    if (secParent === '__skip__') {
      /* Already built crumbs for concept-detail — skip normal crumb building */
    } else {
      if (wf.zoomConceptId || wf.zoomPhaseId || wf.zoomSection || wf.zoomMsId) {
        /* Concepts always under Design */
        if (wf.zoomConceptId) {
          crumbs.push({ label: 'Design', fn: 'data-fn="zoomTo" data-level="root" data-rootname="design"' });
          crumbs.push({ label: 'Concepts', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:design:concepts"' });
        }
        /* Phase / Milestone always under Schedule */
        if (wf.zoomPhaseId || wf.zoomMsId) {
          crumbs.push({ label: 'Schedule', fn: 'data-fn="zoomTo" data-level="root" data-rootname="schedule"' });
        }
        /* Top-level section (under prod/design root, not under a milestone) */
        if (wf.zoomSection && secParent) {
          var parentLabel = secParent.charAt(0).toUpperCase() + secParent.slice(1);
          crumbs.push({ label: parentLabel, fn: 'data-fn="zoomTo" data-level="root" data-rootname="' + secParent + '"' });
          /* Discipline zoom: add "Drawing Sheets" intermediate crumb before the discipline name */
          if (wf.zoomSection.indexOf(':disc:') !== -1) {
            var _discSecKey = 'sec:' + (secParent === 'production' ? 'prod' : secParent) + ':sheets';
            crumbs.push({ label: 'Drawing Sheets', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="' + _discSecKey + '"' });
          }
          if (_shZoom) {
            /* Sheet zoom under prod/design: Drawing Sheets > [Sheet][ > Checklist] */
            crumbs.push({ label: 'Drawing Sheets', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:' + _shZoom.owner + ':sheets"' });
            if (_shZoom.isCl) {
              crumbs.push({ label: _shZoom.name, fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:' + _shZoom.owner + ':sheet:' + _shZoom.sheetId + '"' });
              crumbs.push({ label: 'Checklist', fn: null });
            } else {
              crumbs.push({ label: _shZoom.name, fn: null });
            }
          } else {
            crumbs.push({ label: secTypeLabel, fn: null });
          }
        }
      }
    }

    /* Root zoom only */
    if (wf.zoomRoot && !wf.zoomPhaseId && !wf.zoomMsId && !wf.zoomConceptId && !wf.zoomSection) {
      if (wf.zoomRoot !== 'reference' && REFERENCE_ROOTS.indexOf(wf.zoomRoot) !== -1) {
        crumbs.push({ label: 'Reference', fn: 'data-fn="zoomTo" data-level="root" data-rootname="reference"' });
      }
      var rLabel = wf.zoomRoot.charAt(0).toUpperCase() + wf.zoomRoot.slice(1);
      crumbs.push({ label: rLabel, fn: null });
    }

    /* Phase */
    if (wf.zoomPhaseId) {
      var ph = getPhaseById(wf.zoomPhaseId);
      var phName = ph ? (ph.fields['Phase Name'] || ph.fields['Name'] || 'Phase') : 'Phase';
      /* Phase sub-section zoom (sec:phase:{phId}:{milestones|threads}) */
      var _phSecZoom = !!(wf.zoomSection && wf.zoomSection.indexOf('sec:phase:') === 0);
      if (wf.zoomMsId || _phSecZoom) {
        crumbs.push({ label: phName, fn: 'data-fn="zoomTo" data-level="phase" data-pid="' + wf.zoomPhaseId + '"' });
      } else {
        crumbs.push({ label: phName, fn: null });
      }
      if (_phSecZoom) {
        if (wf.zoomSection.indexOf(':thcat:') !== -1) {
          /* Phase thread-category zoom: … > Phase > Threads > <Type> */
          crumbs.push({ label: 'Threads', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:phase:' + wf.zoomPhaseId + ':threads"' });
          crumbs.push({ label: wf.zoomSection.slice(wf.zoomSection.lastIndexOf(':') + 1), fn: null });
        } else {
          var _phSecType = wf.zoomSection.slice(wf.zoomSection.lastIndexOf(':') + 1);
          crumbs.push({ label: _phSecType === 'threads' ? 'Threads' : 'Milestones', fn: null });
        }
      } else if (wf.zoomMsId) {
        /* Milestone zoom lives under the phase's Milestones section */
        crumbs.push({ label: 'Milestones', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:phase:' + wf.zoomPhaseId + ':milestones"' });
      }
    }

    /* Milestone */
    if (wf.zoomMsId) {
      var msObj = getMsById(wf.zoomMsId);
      var msName = msObj ? (msObj.fields['Full Deliverable Name'] || msObj.fields['Name'] || 'Milestone') : 'Milestone';
      if (wf.zoomCat || wf.zoomItemId || wf.zoomSection) {
        crumbs.push({ label: msName, fn: 'data-fn="zoomTo" data-level="ms" data-msid="' + wf.zoomMsId + '" data-pid="' + (wf.zoomPhaseId||'')+'"' });
      } else {
        crumbs.push({ label: msName, fn: null });
      }
    }

    /* Section under milestone */
    if (wf.zoomSection && !secParent && wf.zoomMsId) {
      if (_shZoom) {
        /* Drawing sheet under milestone: Drawing Sheets > [Sheet][ > Checklist] */
        crumbs.push({ label: 'Drawing Sheets', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="' + sectionKey(wf.zoomMsId, 'sheets') + '"' });
        if (_shZoom.isCl) {
          crumbs.push({ label: _shZoom.name, fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="sec:' + wf.zoomMsId + ':sheet:' + _shZoom.sheetId + '"' });
          crumbs.push({ label: 'Checklist', fn: null });
        } else {
          crumbs.push({ label: _shZoom.name, fn: null });
        }
      } else {
        crumbs.push({ label: secTypeLabel, fn: null });
      }
    }
    /* Category */
    if (wf.zoomCat) {
      /* Insert "Checklist" intermediate segment — cat always lives under the checklist section */
      if (wf.zoomMsId) {
        var _clSecKey = sectionKey(wf.zoomMsId, 'checklist');
        crumbs.push({ label: 'Checklist', fn: 'data-fn="zoomTo" data-level="section" data-sectionkey="' + _clSecKey + '"' });
      }
      if (wf.zoomItemId) {
        crumbs.push({ label: catLabel(wf.zoomCat), fn: 'data-fn="zoomTo" data-level="cat" data-cat="' + wf.zoomCat + '" data-msid="' + (wf.zoomMsId||'')+'"' });
      } else {
        crumbs.push({ label: catLabel(wf.zoomCat), fn: null });
      }
    }

    /* Item */
    if (wf.zoomItemId) {
      var itItems = clCache[wf.zoomMsId] || [];
      var itName = 'Item';
      for (var ii=0; ii<itItems.length; ii++) { if (itItems[ii].id===wf.zoomItemId) { itName=getFirst(itItems[ii].fields['Name for List (from Deliverable Checklist Item Template)'])||'Item'; break; } }
      crumbs.push({ label: itName, fn: null });
    }

    /* Concept */
    if (wf.zoomConceptId && conceptCache) {
      var conRec2 = null;
      for (var ci3=0; ci3<conceptCache.length; ci3++) { if (conceptCache[ci3].id===wf.zoomConceptId) { conRec2=conceptCache[ci3]; break; } }
      var conLabel = conRec2 ? (conRec2.fields['Parti Pris Title'] || 'Unnamed Concept') : 'Concept';
      crumbs.push({ label: conLabel, fn: null });
    }

    /* Reference branch: insert a clickable Reference crumb before the first
       reference-root crumb (Team/Meetings/Threads/Design/Production/Permits/
       Construction) so deep zooms read Project > Reference > <Root> > … */
    for (var _rci = 0; _rci < crumbs.length; _rci++) {
      var _rcFn = crumbs[_rci].fn || '';
      var _rcM = _rcFn.match(/data-level="root" data-rootname="([a-z]+)"/);
      if (!_rcM) continue;
      if (_rcM[1] === 'reference') break; /* already present */
      if (REFERENCE_ROOTS.indexOf(_rcM[1]) !== -1) {
        crumbs.splice(_rci, 0, { label: 'Reference', fn: 'data-fn="zoomTo" data-level="root" data-rootname="reference"' });
      }
      break;
    }

    /* Render crumbs - truncate middle with ellipsis if > 4 levels */
    h += '<span class="wf-bc">';
    if (crumbs.length > 0) {
      var MAX = 4;
      var visible = crumbs;
      var truncated = false;
      if (crumbs.length > MAX) {
        /* Keep first + last (MAX-1) */
        visible = [crumbs[0]].concat(crumbs.slice(crumbs.length - (MAX - 1)));
        truncated = true;
      }
      /* Hover context (bug 4): each crumb gets a title of its own full label (covers CSS-truncated
         labels); the ellipsis gets a title listing the hidden middle crumbs (the full collapsed path). */
      var _bcEsc = function (s) { return String(s == null ? '' : s).replace(/"/g, '&quot;'); };
      var _hidden = truncated ? crumbs.slice(1, crumbs.length - (MAX - 1)).map(function (c) { return c.label; }).join('  ›  ') : '';
      var _fullPath = crumbs.map(function (c) { return c.label; }).join('  ›  ');
      for (var bi2=0; bi2<visible.length; bi2++) {
        var _xc = visible[bi2].cls ? (' ' + visible[bi2].cls) : '';
        var _ti = ' title="' + _bcEsc(visible[bi2].label) + '"';
        if (bi2 === 0 && truncated) {
          /* First crumb then ellipsis */
          h += visible[bi2].fn
            ? '<button class="wf-bc-item' + _xc + '"' + _ti + ' ' + visible[bi2].fn + '>' + visible[bi2].label + '</button>'
            : '<span class="wf-bc-cur' + _xc + '"' + _ti + '>' + visible[bi2].label + '</span>';
          h += '<span class="wf-bc-sep">&#8250;</span>';
          h += '<span class="wf-bc-sep" style="color:#c0d0d4;cursor:help;" title="' + _bcEsc(_hidden || _fullPath) + '">&#8943;</span>';
        } else {
          if (bi2 > 0 || (!truncated && bi2 === 0)) {
            if (bi2 > 0) h += '<span class="wf-bc-sep">&#8250;</span>';
          }
          var isLast = (bi2 === visible.length - 1);
          if (isLast || !visible[bi2].fn) {
            h += '<span class="wf-bc-cur' + _xc + '"' + _ti + '>' + visible[bi2].label + '</span>';
          } else {
            h += '<button class="wf-bc-item' + _xc + '"' + _ti + ' ' + visible[bi2].fn + '>' + visible[bi2].label + '</button>';
          }
        }
      }
    }
    h += '</span>';
    h += '</div>';

    /* Right: search */
    h += '<div class="wf-chrome-right">';
    h += '<span class="wf-chrome-div"></span>';
    /* Show-completed toggle — text label shows current state, not the action */
    var _hdGlobal = !!wf.hideDoneKeys['global'];
    h += '<button class="wf-sc-toggle' + (_hdGlobal ? ' wf-sc-toggle--active' : '') + '" data-fn="toggleShowCompleted" title="' + (_hdGlobal ? 'Showing active milestones only — click to show all' : 'Showing all milestones — click to hide completed') + '">';
    h += _hdGlobal ? 'Hide done' : 'All';
    h += '</button>';
    h += '<span class="wf-chrome-div"></span>';
    /* Outline search */
    h += '<div class="wf-search-wrap">';
    h += '<svg class="wf-search-icon" width="12" height="12" viewBox="0 0 16 16" fill="none"><circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" stroke-width="1.4"/><line x1="10" y1="10" x2="14" y2="14" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
    h += '<input class="wf-search-inp" id="wf-search-inp" type="text" placeholder="Search" value="' + (wf.searchQuery||'') + '">';
    if (wf.searchQuery) h += '<button class="wf-search-clear" data-fn="clearSearch">&#215;</button>';
    h += '</div>';
    h += '</div>';

    h += '</div>';
    return h;
  }

  function buildOutline() {
    /* Sync state arrays with the active project's caches on every render */
    if (wf.zoomProjectId) {
      var _ph = phasesCache[wf.zoomProjectId];
      var _ms = milestonesCache[wf.zoomProjectId];
      if (_ph !== undefined && _ph !== null) state.phases = _ph;
      if (_ms !== undefined && _ms !== null) state.milestones = _ms;
      ctx.recordId = wf.zoomProjectId;
    }
    /* Search results handled by React SearchResults component */
    wf._searchExpanded = {};
    if (wf.searchQuery) return '<div class="wf-list"></div>';
    if (wf.zoomItemId) return '<div class="wf-list"></div>';
    if (wf.zoomCat && wf.zoomMsId) return '<div class="wf-list"></div>';
    if (wf.zoomSection) {
      /* concept/unit/level/room-detail + the Units/Levels/Rooms category-wrapper zooms
         (sec:concept:<id>:units, sec:unit:<id>:levels, sec:level:<id>:rooms) all handled
         by DesignSection React component */
      if (wf.zoomSection.indexOf('concept-detail:') === 0 ||
          wf.zoomSection.indexOf('unit-detail:') === 0 ||
          wf.zoomSection.indexOf('level-detail:') === 0 ||
          wf.zoomSection.indexOf('room-detail:') === 0 ||
          wf.zoomSection.indexOf('sec:concept:') === 0 ||
          wf.zoomSection.indexOf('sec:unit:') === 0 ||
          wf.zoomSection.indexOf('sec:level:') === 0) {
        return '<div class="wf-list"></div>';
      }
      /* Production section zooms (sec:prod:*) handled by React ProductionSection component */
      if (wf.zoomSection.indexOf('sec:prod:') === 0) {
        return '<div class="wf-list"></div>';
      }
      /* Design section zooms (sec:design:*) handled by React DesignSection — every design
         section is React-owned and children-only-zoomable (CT-020). */
      if (wf.zoomSection.indexOf('sec:design:') === 0) {
        return '<div class="wf-list"></div>';
      }
      /* Phase sub-sections (sec:phase:{phId}:{milestones|threads|thcat:Type}) handled by React ScheduleSection */
      if (wf.zoomSection.indexOf('sec:phase:') === 0) {
        return '<div class="wf-list"></div>';
      }
      /* Thread category zooms (sec:threads:cat:Type) + individual thread zoom
         (thread-detail:<id>) handled by React ThreadsSection */
      if (wf.zoomSection.indexOf('sec:threads:') === 0 ||
          wf.zoomSection.indexOf('thread-detail:') === 0) {
        return '<div class="wf-list"></div>';
      }
      /* Meeting minutes detail + meeting category + meeting sub-sections handled by React MeetingsSection */
      /* Reference Documents group zoom handled by React ReferenceDocumentsSection */
      if (wf.zoomSection.indexOf('refdoc-grp:') === 0) {
        return '<div class="wf-list"></div>';
      }
      if (wf.zoomSection.indexOf('meeting-detail:') === 0 ||
          wf.zoomSection.indexOf('meeting-cat:') === 0 ||
          wf.zoomSection.indexOf('sec:meeting:') === 0) {
        return '<div class="wf-list"></div>';
      }
      /* Team group + subconsultant-org zooms (sec:team:group:*, sec:team:org:*) handled by React TeamSection */
      if (wf.zoomSection.indexOf('sec:team:') === 0) {
        return '<div class="wf-list"></div>';
      }
      /* Consultant candidate/RFP/proposal detail zooms handled by React TeamSection */
      if (wf.zoomSection.indexOf('candidate-detail:') === 0 ||
          wf.zoomSection.indexOf('rfp-detail:') === 0 ||
          wf.zoomSection.indexOf('proposal-detail:') === 0) {
        return '<div class="wf-list"></div>';
      }
      /* Milestone sections (sec:rec*) handled by React MsSectionZoom in MilestoneSections */
      if (wf.zoomSection.indexOf('sec:rec') === 0) {
        return '<div class="wf-list"></div>';
      }
      /* Permit + Construction entity zooms handled by React Permits/Construction sections */
      if (wf.zoomSection.indexOf('permit-detail:') === 0 ||
          wf.zoomSection.indexOf('review-detail:') === 0 ||
          wf.zoomSection.indexOf('campaign-detail:') === 0 ||
          wf.zoomSection.indexOf('bid-detail:') === 0 ||
          wf.zoomSection.indexOf('ve-effort:') === 0 ||
          wf.zoomSection.indexOf('ve-item:') === 0 ||
          wf.zoomSection.indexOf('contract-detail:') === 0 ||
          wf.zoomSection.indexOf('rfi-detail:') === 0 ||
          wf.zoomSection.indexOf('submittal-detail:') === 0 ||
          wf.zoomSection.indexOf('asi-detail:') === 0 ||
          wf.zoomSection.indexOf('spec-section-detail:') === 0 ||
          wf.zoomSection.indexOf('proposal-request-detail:') === 0 ||
          wf.zoomSection.indexOf('bim-system-detail:') === 0 ||
          wf.zoomSection.indexOf('bim-element-detail:') === 0 ||
          wf.zoomSection.indexOf('bim-phase-detail:') === 0 ||
          wf.zoomSection.indexOf('sec:construction:') === 0 ||
          wf.zoomSection.indexOf('sec:contract:') === 0) {
        return '<div class="wf-list"></div>';
      }
      /* Parcel detail zoom handled by React ParcelDetailSection (CT-020) */
      if (wf.zoomSection.indexOf('parcel-detail:') === 0) {
        return '<div class="wf-list"></div>';
      }
      return buildSectionZoom(wf.zoomSection);
    }
    if (wf.zoomMsId) return buildMsZoom(wf.zoomMsId);
    if (wf.zoomPhaseId) return buildPhaseZoom(wf.zoomPhaseId);
    if (wf.zoomConceptId) return buildConceptZoom(wf.zoomConceptId);
    if (wf.zoomRoot) return buildRootZoom(wf.zoomRoot);
    return buildTopLevel();
  }

  function buildSectionZoom(secKey) {
    /* Note: concept-detail / unit-detail / level-detail are handled by
       DesignSection React component — those cases never reach here. */
    var parts = secKey.split(':');
    var owner = parts[1] || ''; /* 'prod', 'design', or a milestone ID */
    var type = parts[2] || '';
    var h = '<div class="wf-list">';

    if (type === 'checklist') {
      var items = clCache[owner] || [];
      var cats = getCategoryNodes(owner, items);
      cats.forEach(function(cat) { h += buildCatNode(cat, owner); });
    } else if (type === 'criteria') {
      var cr = criteriaCache || [];
      for (var cri = 0; cri < cr.length; cri++) {
        var crf2 = cr[cri].fields;
        var crName2 = crf2['Name'] || cr[cri].id;
        var crToInclude2 = crf2['To Include'] || '';
        h += '<div class="wf-node wf-node--item"><div class="wf-row">';
        h += '<span class="wf-triangle-spacer wf-triangle-spacer--hidden"></span>';
        h += '<span class="wf-bullet wf-bullet--item"></span>';
        h += '<div class="wf-text-wrap"><span class="wf-node-text">' + crName2 + '</span>';
        if (crToInclude2) h += '<span class="wf-status-pill" style="color:#1D9E75;border-color:#1D9E7533;">Criteria set</span>';
        h += '</div></div></div>';
      }
    } else if (type === 'parcels') {
      var pr = parcelsCache || [];
      for (var pri = 0; pri < pr.length; pri++) {
        var prf = pr[pri].fields;
        var prName = prf['APN'] || prf['Name'] || prf['Parcel Name'] || pr[pri].id;
        var prZone = prf['Zone'] || '';
        h += '<div class="wf-node wf-node--item"><div class="wf-row">';
        h += '<span class="wf-triangle-spacer wf-triangle-spacer--hidden"></span>';
        h += '<span class="wf-bullet wf-bullet--item"></span>';
        h += '<div class="wf-text-wrap"><span class="wf-node-text">' + prName + '</span>';
        if (prZone) h += '<span class="wf-status-pill">' + prZone + '</span>';
        h += '</div></div></div>';
      }
    } else if (type === 'specs') {
      var sp = specsCache || [];
      for (var spi = 0; spi < sp.length; spi++) {
        var spf = sp[spi].fields;
        var spName = spf['Name'] || sp[spi].id;
        var spStatus = spf['Status'] || '';
        var spQuality = spf['Quality Grade'] || '';
        var spVersion = spf['Version Number'] ? 'v' + spf['Version Number'] : '';
        var spIssueDate = spf['Issue Date'] ? fmt(spf['Issue Date']) : '';
        var spStatusColor = (spStatus.indexOf('Posted') !== -1) ? '#1D9E75' : '#7a9aaa';
        h += '<div class="wf-node wf-node--item"><div class="wf-row">';
        h += '<span class="wf-triangle-spacer wf-triangle-spacer--hidden"></span>';
        h += '<span class="wf-bullet wf-bullet--item"></span>';
        h += '<div class="wf-text-wrap"><span class="wf-node-text">' + spName + '</span>';
        h += '<span class="wf-meta">';
        if (spStatus) h += '<span class="wf-status-pill" style="color:' + spStatusColor + ';border-color:' + spStatusColor + '33;">' + spStatus + '</span>';
        if (spQuality) h += '<span class="wf-badge wf-badge--gray">' + spQuality + '</span>';
        if (spVersion) h += '<span class="wf-date-btn" style="pointer-events:none;">' + spVersion + '</span>';
        if (spIssueDate) h += '<span class="wf-date-btn" style="pointer-events:none;">' + spIssueDate + '</span>';
        h += '</span></div></div></div>';
      }
    } else if (type === 'bim') {
      var bi = bimCache || [];
      for (var j=0; j<bi.length; j++) h += buildBimRow(bi[j]);
    } else if (type === 'sheets') {
      /* Sheets section zoom is owned entirely by React (DrawingSheetList via
         ProductionSection or MilestoneSections). Return empty — React renders
         the discipline-grouped list into the same #aj-sc-sections portal. */
    } else if (type === 'concepts') {
      var cc = conceptCache || [];
      for (var m=0; m<cc.length; m++) {
        h += buildConceptRow(cc[m], owner);
      }
    }
    h += '</div>';
    return h;
  }

  function buildRootZoom(name) {
    /* All root zoom views are handled by React components in #aj-sc-sections */
    return '<div class="wf-list"></div>';
  }

  function buildConceptZoom(conceptId) {
    var rec = null;
    if (conceptCache) {
      for (var i = 0; i < conceptCache.length; i++) {
        if (conceptCache[i].id === conceptId) { rec = conceptCache[i]; break; }
      }
    }
    if (!rec) return '<div class="wf-loading">Loading concept...</div>';
    var f = rec.fields;
    var parti = f['Parti Pris Title'] || '';
    var name = parti || 'Unnamed Concept';
    var h = '<div class="wf-list">';
    h += '<div class="wf-item-detail">';
    h += '<div style="font-size:16px;font-weight:700;color:#07272D;margin-bottom:6px;">' + name + '</div>';
    if (parti && parti !== name) h += '<div style="font-size:13px;color:#3a5260;margin-bottom:12px;">' + parti + '</div>';
    if (f['Selected Concept']) h += '<span class="wf-badge wf-badge--green">Active Concept</span>';
    h += '</div>';
    h += '</div>';
    return h;
  }

  /*    TOP LEVEL - three root sections (React components render the sections now)    */
  function buildTopLevel() {
    return '<div class="wf-list"></div>';
  }

  /*    CATEGORY NODE    */
  function getCategoryNodes(msId, items) {
    var visible = items.filter(function(item) {
      var cat = getFirst(item.fields['Category (from Deliverable Checklist Item Template)']) || 'Other';
      return HIDDEN_CL_CATEGORIES.indexOf(cat) === -1;
    });
    var bycat = {}, order = [];
    processItems(visible).forEach(function(item) {
      var cat = getFirst(item.fields['Category (from Deliverable Checklist Item Template)']) || 'Other';
      if (!bycat[cat]) { bycat[cat] = []; order.push(cat); }
      bycat[cat].push(item);
    });
    order.sort(function(a,b) {
      var ai=CAT_ORDER.indexOf(a), bi=CAT_ORDER.indexOf(b);
      if(ai===-1)ai=9; if(bi===-1)bi=9; return ai-bi;
    });
    return order.map(function(cat) { return { cat:cat, items:bycat[cat] }; });
  }

  function buildCatNode(catObj, msId) {
    var cat = catObj.cat, items = catObj.items;
    var key = catKey(msId, cat);
    var isCollapsed = isNodeCollapsed(key, false);
    var done = items.filter(function(i) { return i.isComplete; }).length;
    var notReq = items.filter(function(i) { return i.fields['Milestone Not Required']===true; }).length;
    var total = items.length;
    var pct = total > 0 ? Math.round(done/total*100) : 0;
    var gc = pct===100 ? '#1D9E75' : pct>0 ? '#1a6fa8' : 'rgba(7,39,45,0.10)';

    var h = '<div class="wf-node wf-node--cat' + (isCollapsed ? ' wf-node--collapsed' : '') + '" data-key="' + key + '">';
    h += '<div class="wf-row">';
    h += '<button class="wf-triangle wf-triangle--cat" data-fn="toggleCollapse" data-key="' + key + '">';
    h += '<span class="wf-tri-icon ' + (isCollapsed ? 'wf-tri-icon--right' : 'wf-tri-icon--down') + '"></span>';
    h += '</button>';
    /* Bullet - drill into category */
    h += '<button class="wf-bullet wf-bullet--cat" data-fn="zoomTo" data-level="cat" data-msid="' + msId + '" data-cat="' + cat + '" data-pid="' + (wf.zoomPhaseId||'') + '" title="Focus on this gate"></button>';
    h += '<div class="wf-text-wrap">';
    h += '<span class="wf-node-text wf-node-text--cat">' + catLabel(cat) + '</span>';
    h += '<span class="wf-meta">';
    h += '<span class="wf-cat-bar-wrap"><span class="wf-cat-bar-fill" style="width:' + pct + '%;background:' + gc + ';"></span></span>';
    h += '<span class="wf-cat-count">' + done + '/' + total + '</span>';
    if (notReq > 0) h += '<span class="wf-badge wf-badge--amber">' + notReq + ' skipped</span>';
    h += '</span>';
    h += '</div>'; /* end text-wrap */
    /* Menu */
    h += '<button class="wf-menu-btn" data-fn="openMenu" data-key="' + key + '" data-type="cat" data-msid="' + msId + '" data-cat="' + cat + '">&#8943;</button>';
    h += '</div>';

    /* Context menu */
    if (wf.menuNodeId === key) h += buildMenu('cat', { msId:msId, cat:cat, items:items });

    /* Children */
    if (!isCollapsed) {
      h += '<div class="wf-children">';
      /* Select mode bar */
      var inSelect = wf.selectMode === msId + ':' + cat || wf.selectMode === msId + ':ALL';
      /* Float bar is managed separately via updateSelectFloatBar() */
      if (inSelect && wf.panels['bulk:' + msId]) h += wf.panels['bulk:' + msId];
      items.forEach(function(item) { h += buildItemNode(item, msId, inSelect); });
      h += '</div>';
    }
    h += '</div>';
    return h;
  }

  /*    ITEM NODE    */
  function buildItemNode(item, msId, inSelect) {
    var f = item.fields;
    var itemId = item.record.id;
    var name = getFirst(f['Name for List (from Deliverable Checklist Item Template)']) || 'Item';
    var isActuallyDone = f['Complete']===true;
    var isDone = item.isComplete;
    var isSkip = f['Milestone Not Required']===true;
    var key = itemKey(itemId);

    var _itemCat = getFirst(f['Category (from Deliverable Checklist Item Template)']) || '';
    var hiddenClass = (isDone||isSkip) && shouldHideDoneForCat(msId, wf.zoomPhaseId||'', _itemCat) ? ' wf-hidden-done' : '';
    var h = '<div class="wf-node wf-node--item' + (isDone||isSkip?' wf-node--done':'') + hiddenClass + '" data-key="' + key + '">';
    h += '<div class="wf-row">';

    h += '<span class="wf-triangle-spacer wf-triangle-spacer--hidden"></span>';
    h += '<button class="wf-menu-btn wf-menu-btn--triangle" data-fn="openMenu" data-key="' + key + '" data-type="item" data-itemid="' + itemId + '" data-msid="' + msId + '" data-isdone="' + (isActuallyDone?'true':'false') + '" data-isskip="' + (isSkip?'true':'false') + '">&#8943;</button>';

    /* Bullet */
    if (inSelect && !isDone && !isSkip) {
      var isSel = wf.selected.indexOf(itemId) !== -1;
      h += '<input type="checkbox" class="wf-item-cb" ' + (isSel?'checked':'') + ' data-fn="selectItem" data-itemid="' + itemId + '" data-msid="' + msId + '">';
    } else {
      h += '<button class="wf-bullet wf-bullet--item' + (isDone?' wf-bullet--done':'') + '" data-fn="zoomTo" data-level="item" data-itemid="' + itemId + '" data-msid="' + msId + '" data-pid="' + (wf.zoomPhaseId||'') + '" data-cat="' + (wf.zoomCat||'') + '" title="Focus on this item"></button>';
    }

    /* Name */
    h += '<div class="wf-text-wrap">';
    h += '<span class="wf-node-text wf-node-text--item' + (isDone||isSkip?' wf-node-text--done':'') + '">' + name + '</span>';

    /* Badges */
    h += '<span class="wf-meta">';
    if (isSkip) {
      var nrReason = f['Milestone Not Required Reason'] || '';
      var nrMemberIds = f['Milestone Not Required Member'] || [];
      var nrMember = nrMemberIds.length ? getMemberName(nrMemberIds[0]) : '';
      var nrTooltip = (nrReason ? nrReason : '') + (nrMember ? (nrReason ? ' — ' : '') + 'by ' + nrMember : '') || 'Not required';
      h += '<span class="wf-badge wf-badge--amber" title="' + nrTooltip.replace(/"/g, '&quot;') + '" style="cursor:help;">';
      h += 'Not required';
      if (nrMember) h += ' · ' + nrMember;
      h += '</span>';
    }
    if (isDone) {
      /* Show who completed it - prefer Completed Message formula, fall back to Completed by lookup */
      var compMsg = f['Completed Message'] || '';
      var compIds = f['Completed by'] || [];
      var compName = compIds.length ? getMemberName(compIds[0]) : '';
      var compDate = (f['Completed Date'] || '').slice(0,10);
      var compLabel = compMsg ? compMsg : (compName ? compName + (compDate ? ' · ' + compDate : '') : 'Done');
      /* Strip Airtable formula formatting artifacts */
      compLabel = compLabel.replace(/__/g, '').trim() || 'Done';
      h += '<span class="wf-badge wf-badge--green" title="' + compLabel + '">';
      h += compName ? compName : 'Done';
      if (compDate) h += ' · ' + compDate;
      h += '</span>';
    }
    h += '</span>';
    h += '</div>'; /* end text-wrap */

    h += '</div>';

    /* Inline panel */
    if (wf.panels[key]) h += wf.panels[key];

    h += '</div>';
    return h;
  }

  /*    CONTEXT MENUS    */
  /* Expand or collapse all descendants of a node key */
  /* Expand a single milestone and trigger all data loads */
  function expandMs(msId, fullExpand) {
    wf.collapsed[msKey(msId)] = false;
    /* Expand all sections */
    ['checklist','bim','sheets'].forEach(function(t){ wf.collapsed[sectionKey(msId,t)] = false; });
    if (fullExpand) {
      /* Expand categories if already cached, otherwise mark pending */
      if (clCache[msId] && clCache[msId].length > 0) {
        var cats = {};
        clCache[msId].forEach(function(it){ var c=getFirst(it.fields['Category (from Deliverable Checklist Item Template)'])||'?'; cats[c]=true; });
        Object.keys(cats).forEach(function(c){ wf.collapsed[catKey(msId,c)] = false; });
      } else {
        pendingFullExpand[msId] = true;
      }
    }
    if (!clCache[msId]) loadChecklist(msId);
    if (bimCache === null) loadBim();
    if (sheetCache[msId] === undefined) loadSheets(msId);
  }

  function expandCollapseDescendants(key, expand) {
    if (key.indexOf('proj:') === 0) {
      var projId2 = key.replace('proj:', '');
      wf.collapsed[key] = !expand;
      /* Expand/collapse the three root sections in the project list */
      ['sched', 'design', 'production'].forEach(function(sec) {
        wf.collapsed[sec + ':' + projId2] = !expand;
      });
      /* Expand/collapse all phases + their Milestones/Threads sub-sections */
      (phasesCache[projId2] || []).forEach(function(ph) {
        wf.collapsed[phKey(ph.id)] = !expand;
        wf.collapsed['sec:phase:' + ph.id + ':milestones'] = !expand;
        wf.collapsed['sec:phase:' + ph.id + ':threads'] = !expand;
      });
      /* Expand/collapse all milestones — set keys only, let MilestoneSections load data */
      (milestonesCache[projId2] || []).forEach(function(ms) {
        if (expand) {
          wf.collapsed[msKey(ms.id)] = false;
          ['checklist','bim','sheets'].forEach(function(t){ wf.collapsed[sectionKey(ms.id,t)] = false; });
        } else {
          wf.collapsed[msKey(ms.id)] = true;
          ['checklist','bim','sheets'].forEach(function(t){ wf.collapsed[sectionKey(ms.id,t)] = true; });
        }
      });
    }
    else if (key.indexOf('root:') === 0) {
      var rootName = key.replace('root:', '');
      /* Expand/collapse the root node itself */
      wf.collapsed[key] = !expand;
      if (rootName === 'projects') {
        /* Home level (no root node anymore — ZoomTitle menu passes this key):
           expand/collapse every project row + its nav sections */
        (ctx.projectsCache || []).forEach(function(p) {
          wf.collapsed['proj:' + p.id] = !expand;
          ['sched', 'design', 'production'].forEach(function(sec) {
            wf.collapsed[sec + ':' + p.id] = !expand;
          });
        });
      } else if (rootName === 'reference') {
        /* Reference branch — expand/collapse every child root section */
        REFERENCE_ROOTS.forEach(function(r) {
          wf.collapsed['root:' + r] = !expand;
        });
      } else if (rootName === 'documents') {
        /* Reference Documents — toggle every kit-item group (docs/REFERENCE_DOCUMENTS.md) */
        var _rdc = ctx.refDocCache && ctx.refDocCache[wf.zoomProjectId];
        ((_rdc && _rdc.documents) || []).forEach(function(d) {
          wf.collapsed['refdoc-grp:' + (d.fact_id || 'none')] = !expand;
        });
      } else if (rootName === 'threads') {
        /* Threads root — toggle category nodes + every thread row */
        (ctx.threadCache || []).forEach(function(t) {
          var _ttype = (t.fields && t.fields['Type']) || 'Misc. Items';
          wf.collapsed['thcat:all:' + _ttype] = !expand;
          wf.collapsed['thread:' + t.id] = !expand;
        });
      } else if (rootName === 'schedule') {
        state.phases.forEach(function(ph) {
          wf.collapsed[phKey(ph.id)] = expand ? false : true;
          wf.collapsed['sec:phase:' + ph.id + ':milestones'] = !expand;
          wf.collapsed['sec:phase:' + ph.id + ':threads'] = !expand;
          if (expand) {
            getMilestonesForPhase(ph.id).forEach(function(ms) {
              wf.collapsed[msKey(ms.id)] = false;
              ['checklist','bim','sheets'].forEach(function(t){ wf.collapsed[sectionKey(ms.id,t)] = false; });
            });
          } else {
            state.milestones.forEach(function(ms) { wf.collapsed[msKey(ms.id)] = true; });
          }
        });
      } else {
        var rootSecs = rootName==='design' ? ['criteria','parcels','concepts','specs','inspiration'] : ['sheets','bim'];
        rootSecs.forEach(function(t){ wf.collapsed[sectionKey(rootName,t)] = !expand; });
      }
    }
    else if (key.indexOf('ph:') === 0) {
      /* Expand the phase itself, its Milestones/Threads sub-sections, then all its milestones */
      wf.collapsed[key] = expand ? false : true;
      var phId = key.replace('ph:','');
      wf.collapsed['sec:phase:' + phId + ':milestones'] = !expand;
      wf.collapsed['sec:phase:' + phId + ':threads'] = !expand;
      var ph = getPhaseById(phId);
      if (!ph) return;
      getMilestonesForPhase(phId).forEach(function(ms) {
        if (expand) {
          wf.collapsed[msKey(ms.id)] = false;
          ['checklist','bim','sheets'].forEach(function(t){ wf.collapsed[sectionKey(ms.id,t)] = false; });
        } else {
          wf.collapsed[msKey(ms.id)] = true;
          ['checklist','bim','sheets'].forEach(function(t){ wf.collapsed[sectionKey(ms.id,t)] = true; });
        }
      });
    }
    else if (key.indexOf('ms:') === 0) {
      var msId = key.replace('ms:','');
      /* Expand the ms itself */
      if (expand) {
        expandMs(msId, true);
      } else {
        wf.collapsed[msKey(msId)] = true;
        ['checklist','bim','sheets'].forEach(function(t){ wf.collapsed[sectionKey(msId,t)] = true; });
        var items2b = clCache[msId] || [];
        var cats2b = {};
        items2b.forEach(function(it){ var c=getFirst(it.fields['Category (from Deliverable Checklist Item Template)'])||'?'; cats2b[c]=true; });
        Object.keys(cats2b).forEach(function(c){ wf.collapsed[catKey(msId,c)] = true; });
      }
    }
    else if (key.indexOf('sec:threads:cat:') === 0) {
      /* Reference thread-category zoom — toggle that category's thread rows */
      wf.collapsed[key] = !expand;
      var _tcatType = key.slice('sec:threads:cat:'.length);
      wf.collapsed['thcat:all:' + _tcatType] = !expand;
      (ctx.threadCache || []).forEach(function(t) {
        if (((t.fields && t.fields['Type']) || 'Misc. Items') === _tcatType) {
          wf.collapsed['thread:' + t.id] = !expand;
        }
      });
    }
    else if (key.indexOf('sec:phase:') === 0) {
      /* Phase Milestones/Threads sub-section (or a thcat category zoom key) */
      wf.collapsed[key] = !expand;
      var pspId = key.split(':')[2] || '';
      var pspType = key.slice(key.lastIndexOf(':') + 1);
      if (key.indexOf(':thcat:') !== -1) {
        /* sec:phase:<phId>:thcat:<Type> — toggle that category's thread rows */
        wf.collapsed['thcat:' + pspId + ':' + pspType] = !expand;
        (ctx.threadCache || []).forEach(function(t) {
          if (((t.fields && t.fields['Type']) || 'Misc. Items') === pspType) {
            wf.collapsed['thread:' + t.id] = !expand;
          }
        });
      } else if (pspType === 'milestones') {
        getMilestonesForPhase(pspId).forEach(function(ms) {
          wf.collapsed[msKey(ms.id)] = !expand;
          if (!expand) ['checklist','bim','sheets'].forEach(function(t){ wf.collapsed[sectionKey(ms.id,t)] = true; });
        });
      } else if (pspType === 'threads') {
        (ctx.threadCache || []).forEach(function(t) {
          var _ptype = (t.fields && t.fields['Type']) || 'Misc. Items';
          wf.collapsed['thcat:' + pspId + ':' + _ptype] = !expand;
          wf.collapsed['thread:' + t.id] = !expand;
        });
      }
    }
    else if (key.indexOf('sec:concept:') === 0 || key.indexOf('sec:unit:') === 0 || key.indexOf('sec:level:') === 0) {
      /* Units/Levels/Rooms category-wrapper zoom keys — the row components underneath
         (ConceptUnitsSection/ConceptLevelsSection/ConceptRoomsSection) fetch their own children
         and read wf.collapsed fresh each render, so toggling the category key itself is enough;
         deep recursion isn't needed for correctness (unlike checklist categories below, which
         are derived synchronously from an already-loaded cache). */
      wf.collapsed[key] = !expand;
    }
    else if (key.indexOf('sec:') === 0) {
      var parts = key.split(':'); var secMsId = parts[1]; var secType = parts[2];
      wf.collapsed[key] = !expand;
      if (secType === 'checklist') {
        var items3 = clCache[secMsId] || [];
        var cats3 = {};
        items3.forEach(function(it){ var c=getFirst(it.fields['Category (from Deliverable Checklist Item Template)'])||'?'; cats3[c]=true; });
        Object.keys(cats3).forEach(function(c){ wf.collapsed[catKey(secMsId,c)] = !expand; });
      }
    }
    else if (key.indexOf('cat:') === 0) {
      /* Category has no children currently, just toggle itself */
      wf.collapsed[key] = !expand;
    }
    else if (key.indexOf('sched:') === 0) {
      /* Home-level Schedule nav row — expand/collapse the row and all its phases */
      var schedProjId = key.replace('sched:', '');
      wf.collapsed[key] = !expand;
      (phasesCache[schedProjId] || []).forEach(function(ph) {
        wf.collapsed[phKey(ph.id)] = !expand;
        wf.collapsed['sec:phase:' + ph.id + ':milestones'] = !expand;
        wf.collapsed['sec:phase:' + ph.id + ':threads'] = !expand;
        if (!expand) {
          (milestonesCache[schedProjId] || []).forEach(function(ms) {
            wf.collapsed[msKey(ms.id)] = true;
          });
        }
      });
    }
    else if (key.indexOf('design:') === 0) {
      /* Home-level Design nav row — just toggle the row itself */
      wf.collapsed[key] = !expand;
    }
    else if (key.indexOf('production:') === 0) {
      /* Home-level Production nav row — toggle the row and its sub-section containers */
      var prodProjId = key.replace('production:', '');
      wf.collapsed[key] = !expand;
      wf.collapsed['list-sheets:' + prodProjId] = !expand;
      wf.collapsed['list-bim:' + prodProjId] = !expand;
      /* Also toggle any previously-loaded discipline groups for this project */
      Object.keys(wf.collapsed).forEach(function(ck) {
        if (ck.indexOf('list-disc:') === 0 && ck.slice(ck.lastIndexOf(':') + 1) === prodProjId) {
          wf.collapsed[ck] = !expand;
        }
      });
    }
    wf.suppressAnim = true;
    render();
    wf.suppressAnim = false;
    /* The key lists above only reach the node types someone wrote them for; finish with the DOM
       sweep so every descendant of any type opens/closes all the way down (outline/subtreeSweep.js). */
    sweepSubtree(key, expand, { strict: true });
  }

  function buildMenu(type, ctx) {
    var h = '<div class="wf-menu">';
    if (type === 'phase') {
      if (ctx.meetingsCount) {
        h += '<div class="wf-menu-info"><span class="wf-menu-info-label">Meetings</span><span class="wf-menu-info-value">' + ctx.meetingsCount + '</span></div>';
      }
      var ICO_CLOSEOUT = '<svg width="13" height="13" viewBox="0 0 16 16" fill="none"><rect x="2" y="7" width="12" height="8" rx="1.5" stroke="currentColor" stroke-width="1.3"/><path d="M5 7V5a3 3 0 0 1 6 0v2" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><circle cx="8" cy="11" r="1.2" fill="currentColor"/></svg>';
      var ICO_REOPEN = '<svg width="13" height="13" viewBox="0 0 16 16" fill="none"><rect x="2" y="7" width="12" height="8" rx="1.5" stroke="currentColor" stroke-width="1.3"/><path d="M5 7V5a3 3 0 0 1 6 0" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><path d="M11 3l2-1v2" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      if (ctx.closedOut) {
        h += menuItem('reopenPhase', 'data-pid="'+ctx.phId+'"', 'Reopen phase', ICO_REOPEN);
        h += menuDivider();
        h += menuItem('expandAll', 'data-key="ph:'+ctx.phId+'"', 'Expand all', ICO_EXPAND);
        h += menuItem('collapseAll', 'data-key="ph:'+ctx.phId+'"', 'Collapse all', ICO_COLLAPSE);
      } else {
        h += menuItem('togglePhaseTime', 'data-pid="'+ctx.phId+'" data-val="'+ctx.allowTime+'"', ctx.allowTime ? 'Disable time entries' : 'Enable time entries', ICO_TIME);
        var coreUrl = ctx.openInCore || '';
        if (coreUrl) h += '<a class="wf-menu-item" href="' + coreUrl + '" target="_blank" rel="noopener">' + '<span class="wf-menu-icon">' + ICO_LINK + '</span>Open in BQE Core</a>';
        h += menuDivider();
        if (ctx.isAS) h += menuItem('editPhaseTitle', 'data-pid="'+ctx.phId+'"', 'Edit title', ICO_EDIT);
        h += menuItem('editPhase', 'data-pid="'+ctx.phId+'"', 'Edit phase', ICO_EDIT);
        h += menuDivider();
        h += menuItem('openNewMs', 'data-pid="'+ctx.phId+'"', 'Add milestone', ICO_ADD);
        h += menuDivider();
        h += menuItem('expandAll', 'data-key="ph:'+ctx.phId+'"', 'Expand all', ICO_EXPAND);
        h += menuItem('collapseAll', 'data-key="ph:'+ctx.phId+'"', 'Collapse all', ICO_COLLAPSE);
        h += menuDivider();
        h += menuItem('closeOutPhase', 'data-pid="'+ctx.phId+'"', 'Close out phase', ICO_CLOSEOUT);
        h += menuDivider();
        h += '<button class="wf-menu-item" style="color:#c0392b;" data-fn="deletePhase" data-pid="'+ctx.phId+'">'
          + '<span class="wf-menu-icon"><svg width="13" height="13" viewBox="0 0 16 16" fill="none"><polyline points="2,4 14,4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><path d="M5 4V2h6v2M6 7v5M10 7v5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><rect x="3" y="4" width="10" height="10" rx="1.5" stroke="currentColor" stroke-width="1.3"/></svg></span>'
          + 'Delete phase</button>';
      }
    } else if (type === 'unit') {
      h += menuItem('editUnitType', 'data-unitid="'+ctx.unitId+'"', 'Edit type', ICO_EDIT);
      h += menuDivider();
      h += menuItem('expandAll',   'data-key="unit:'+ctx.unitId+'"', 'Expand all',   ICO_EXPAND);
      h += menuItem('collapseAll', 'data-key="unit:'+ctx.unitId+'"', 'Collapse all', ICO_COLLAPSE);
    } else if (type === 'level') {
      h += menuItem('addRoom', 'data-levelid="'+ctx.levelId+'" data-conceptid="'+(ctx.conceptId||'')+'"', 'Add room', ICO_ADD);
      h += menuDivider();
      h += menuItem('expandAll',  'data-key="level:'+ctx.levelId+'"', 'Expand all',   ICO_EXPAND);
      h += menuItem('collapseAll','data-key="level:'+ctx.levelId+'"', 'Collapse all', ICO_COLLAPSE);
    } else if (type === 'room') {
      h += menuItem('moveRoomToLevel', 'data-roomid="'+ctx.roomId+'" data-levelid="'+ctx.levelId+'"', 'Move to level', ICO_EDIT);
      h += menuDivider();
      h += '<button class="wf-menu-item" style="color:#c0392b;" data-fn="deleteRoom" data-roomid="'+ctx.roomId+'" data-levelid="'+ctx.levelId+'">'
        + '<span class="wf-menu-icon"><svg width="13" height="13" viewBox="0 0 16 16" fill="none"><polyline points="2,4 14,4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><path d="M5 4V2h6v2M6 7v5M10 7v5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><rect x="3" y="4" width="10" height="10" rx="1.5" stroke="currentColor" stroke-width="1.3"/></svg></span>'
        + 'Delete room</button>';
    } else if (type === 'concept') {
      var ICO_STAR = '<svg width="13" height="13" viewBox="0 0 16 16" fill="none"><polygon points="8,1 10,6 15,6 11,9.5 12.5,15 8,12 3.5,15 5,9.5 1,6 6,6" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/></svg>';
      var ICO_PLAN = '<svg width="13" height="13" viewBox="0 0 16 16" fill="none"><rect x="1.5" y="1.5" width="13" height="13" rx="1" stroke="currentColor" stroke-width="1.3"/><path d="M7 1.5v6H1.5M7 7.5h7M10.5 7.5v7" stroke="currentColor" stroke-width="1.3"/></svg>';
      h += menuItem('openProgramModel', 'data-conceptid="'+ctx.conceptId+'"', 'Open Building Information Model', ICO_PLAN);
      var ICO_CUBE = '<svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M8 1.5 14 5v6l-6 3.5L2 11V5l6-3.5Z" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/><path d="M2 5l6 3.5L14 5M8 8.5V14.5" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/></svg>';
      h += menuItem('openModeler', 'data-conceptid="'+ctx.conceptId+'"', 'Open AlderCAD', ICO_CUBE);
      h += menuDivider();
      if (!ctx.selected) {
        h += menuItem('setSelectedConcept', 'data-conceptid="'+ctx.conceptId+'" data-owner="'+ctx.owner+'"', 'Set as active', ICO_STAR);
      }
      h += menuItem('editConceptName', 'data-conceptid="'+ctx.conceptId+'" data-owner="'+ctx.owner+'"', 'Edit name', ICO_EDIT);
      h += menuItem('copyConcept', 'data-conceptid="'+ctx.conceptId+'"', 'Copy concept', ICO_COPY);
      h += menuDivider();
      if (ctx.selected) {
        h += '<div style="display:flex;align-items:center;gap:8px;padding:8px 14px;font-family:\'DM Sans\',sans-serif;font-size:13px;color:#c0d0d4;cursor:default;">'
          + '<span class="wf-menu-icon" style="opacity:0.4"><svg width="13" height="13" viewBox="0 0 16 16" fill="none"><polyline points="2,4 14,4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><path d="M5 4V2h6v2M6 7v5M10 7v5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><rect x="3" y="4" width="10" height="10" rx="1.5" stroke="currentColor" stroke-width="1.3"/></svg></span>'
          + 'Delete concept'
          + '<span style="margin-left:auto;font-size:11px;color:#c0d0d4;">Active</span>'
          + '</div>';
      } else {
        h += '<button class="wf-menu-item" style="color:#c0392b;" data-fn="deleteConceptItem" data-conceptid="'+ctx.conceptId+'" data-owner="'+ctx.owner+'">'
          + '<span class="wf-menu-icon"><svg width="13" height="13" viewBox="0 0 16 16" fill="none"><polyline points="2,4 14,4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><path d="M5 4V2h6v2M6 7v5M10 7v5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><rect x="3" y="4" width="10" height="10" rx="1.5" stroke="currentColor" stroke-width="1.3"/></svg></span>'
          + 'Delete concept</button>';
      }
    } else if (type === 'ms' && ctx.readOnly) {
      // Closed-out (read-only) phase: the schedule is locked, so offer view / navigation actions
      // only — no posting, moving, date/team edits, or deletion. Reopen the phase to change it.
      if (ctx.slackThread) h += '<a class="wf-menu-item" href="'+ctx.slackThread+'" target="_blank" rel="noopener"><span class="wf-menu-icon">'+ICO_SLACK+'</span>Open in Slack</a>';
      h += menuItem('copyShareLink', 'data-msid="'+ctx.msId+'"', 'Copy document share link', ICO_COPY);
      if (PRESENTATION_TYPE_IDS.indexOf(ctx.tplId) !== -1) h += menuItem('openPresentation', 'data-msid="'+ctx.msId+'"', 'Open presentation', ICO_TEMPLATE);
      if (DRB_PRESENTATION_MS_TYPE_IDS.indexOf(ctx.tplId) !== -1) h += menuItem('makeDrbPresentation', 'data-msid="'+ctx.msId+'"', 'Make DRB presentation…', ICO_TEMPLATE);
      if (Object.prototype.hasOwnProperty.call(SPEC_RELEASE_MS_TYPE_IDS, ctx.tplId)) h += menuItem('openSpecEditor', 'data-msid="'+ctx.msId+'"', 'Open AlderSPEC', ICO_TEMPLATE);
      if (ctx.tplUrl) h += menuItem('openTemplateEditor', 'data-tplid="'+ctx.tplUrl+'" data-tplname="'+escAttr(ctx.msDelType)+'"', 'Edit template', ICO_TEMPLATE);
      h += menuDivider();
      h += menuItem('expandAll', 'data-key="ms:'+ctx.msId+'"', 'Expand all', ICO_EXPAND);
      h += menuItem('collapseAll', 'data-key="ms:'+ctx.msId+'"', 'Collapse all', ICO_COLLAPSE);
    } else if (type === 'ms') {
      var _msPosted = !!(ctx.msPosted);
      if (_msPosted) {
        h += menuItem('unpostMilestone', 'data-msid="'+ctx.msId+'"', 'Unpost', ICO_CHECK);
        h += menuDivider();
      } else if (ctx.blocked) {
        // Waiting on a predecessor: no posting, and a plain line saying why (outline/blocked.js).
        h += '<div class="wf-menu-item wf-menu-item--note" style="cursor:default;color:var(--aj-text-muted);white-space:normal;max-width:260px;">Waiting on '+String(ctx.waitingOn||'').replace(/[&<>"]/g,function(c){return {'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;'}[c];})+'.</div>';
        // The compressed chain (docs/COMPRESSED_CHAIN.md): when the earlier steps really happened in
        // one meeting, post them together; or a firm leader lets this start before they post.
        h += menuItem('postWithEarlier', 'data-msid="'+ctx.msId+'"', 'Post the steps before it…', ICO_CHECK);
        if (hasModule('workload') || hasModule('admin')) h += menuItem('startAhead', 'data-msid="'+ctx.msId+'"', 'Start ahead…', ICO_DATE);
        h += menuDivider();
      } else {
        h += menuItem('postMilestone', 'data-msid="'+ctx.msId+'"', 'Post milestone', ICO_CHECK);
        h += menuDivider();
      }
      h += menuItem('moveToPhase', 'data-msid="'+ctx.msId+'" data-pid="'+ctx.phId+'"', 'Move to phase', ICO_EDIT);
      h += menuDivider();
      /* Dates come from the Build Ready forecast (2026-10-27): no hand-set due date or pin here. */
      h += menuItem('editLft', 'data-msid="'+ctx.msId+'"', 'Open the schedule', ICO_DATE);
      h += menuDivider();
      /* No "Edit team" here: crews are assigned only on the Flight Board. */
      if (ctx.slackThread) h += '<a class="wf-menu-item" href="'+ctx.slackThread+'" target="_blank" rel="noopener"><span class="wf-menu-icon">'+ICO_SLACK+'</span>Open in Slack</a>';
      h += menuItem('copyShareLink', 'data-msid="'+ctx.msId+'"', 'Copy document share link', ICO_COPY);
      if (ctx.tplUrl) h += menuItem('openTemplateEditor', 'data-tplid="'+ctx.tplUrl+'" data-tplname="'+escAttr(ctx.msDelType)+'"', 'Edit template', ICO_TEMPLATE);
      h += menuDivider();
      if (ctx.tplId === BID_CAMPAIGN_MS_TYPE_ID && !ctx.blocked) {
        h += menuItem('addBidCampaignToMs', 'data-msid="'+ctx.msId+'"', 'Add bid campaign', ICO_ADD);
        h += menuDivider();
      }
      if (ctx.tplId === VALUE_ENGINEERING_MS_TYPE_ID && !ctx.blocked) {
        h += menuItem('addVeEffortToMs', 'data-msid="'+ctx.msId+'"', 'Add VE effort', ICO_ADD);
        h += menuDivider();
      }
      if (PRESENTATION_TYPE_IDS.indexOf(ctx.tplId) !== -1) {
        h += menuItem('openPresentation', 'data-msid="'+ctx.msId+'"', 'Open presentation', ICO_TEMPLATE);
        h += menuDivider();
      }
      // the DRB presentation (docs/DRB_PRESENTATION.md) — on the DAPC milestones this is its only
      // door, since those hide Documents
      if (DRB_PRESENTATION_MS_TYPE_IDS.indexOf(ctx.tplId) !== -1) {
        h += menuItem('makeDrbPresentation', 'data-msid="'+ctx.msId+'"', 'Make DRB presentation…', ICO_TEMPLATE);
        h += menuDivider();
      }
      if (Object.prototype.hasOwnProperty.call(SPEC_RELEASE_MS_TYPE_IDS, ctx.tplId) && !ctx.blocked) {
        h += menuItem('openSpecEditor', 'data-msid="'+ctx.msId+'"', 'Open AlderSPEC', ICO_TEMPLATE);
        h += menuDivider();
      }
      if (ctx.tplId === PRE_DESIGN_RESEARCH_TYPE_ID) {
        h += menuItem('previewPreDesignReport', 'data-msid="'+ctx.msId+'"', 'Preview pre-design report', ICO_TEMPLATE);
        h += menuDivider();
      }
      h += menuItem('openEditMs', 'data-msid="'+ctx.msId+'"', 'Edit milestone', ICO_EDIT);
      h += menuDivider();
      if (!ctx.blocked) {
        h += menuItem('addToJourney', 'data-msid="'+ctx.msId+'"', 'Add to Client Hub', ICO_ADD);
        h += menuDivider();
      }
      h += menuItem('expandAll', 'data-key="ms:'+ctx.msId+'"', 'Expand all', ICO_EXPAND);
      h += menuItem('collapseAll', 'data-key="ms:'+ctx.msId+'"', 'Collapse all', ICO_COLLAPSE);
      h += menuDivider();
      h += '<button class="wf-menu-item" style="color:#c0392b;" data-fn="deleteMilestone" data-msid="' + ctx.msId + '"><span class="wf-menu-icon"><svg width="13" height="13" viewBox="0 0 16 16" fill="none"><polyline points="2,4 14,4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><path d="M5 4V2h6v2M6 7v5M10 7v5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/><rect x="3" y="4" width="10" height="10" rx="1.5" stroke="currentColor" stroke-width="1.3"/></svg></span>Delete milestone</button>';
    } else if (type === 'cat') {
      h += menuItem('enterSelectMode', 'data-msid="'+ctx.msId+'" data-cat="'+ctx.cat+'"', 'Select items', ICO_SELECT);
      h += menuDivider();
      h += menuItem('resetCategory', 'data-msid="'+ctx.msId+'" data-cat="'+encodeURIComponent(ctx.cat)+'"', 'Reset category', ICO_TEMPLATE);
      h += menuDivider();
      h += menuItem('expandAll', 'data-key="cat:'+ctx.msId+':'+ctx.cat+'"', 'Expand all', ICO_EXPAND);
      h += menuItem('collapseAll', 'data-key="cat:'+ctx.msId+':'+ctx.cat+'"', 'Collapse all', ICO_COLLAPSE);
    } else if (type === 'item') {
      var itemDone = ctx.isDone;
      var itemSkip = ctx.isSkip;
      if (itemSkip) {
        /* Not-required item: only action is to undo */
        h += menuItem('markRequired', 'data-id="'+ctx.itemId+'" data-msid="'+ctx.msId+'"', 'Mark required', ICO_SKIP);
      } else if (itemDone) {
        /* Completed item */
        h += menuItem('completeItem', 'data-id="'+ctx.itemId+'" data-msid="'+ctx.msId+'"', 'Mark incomplete', ICO_CHECK);
      } else {
        /* Active item */
        h += menuItem('completeItem', 'data-id="'+ctx.itemId+'" data-msid="'+ctx.msId+'"', 'Complete', ICO_CHECK);
        h += menuDivider();
        h += menuItem('inlineNotRequired', 'data-itemid="'+ctx.itemId+'" data-msid="'+ctx.msId+'"', 'Not required', ICO_SKIP);
      }
    } else if (type === 'project') {
      h += menuItem('openActiveConceptModeler', 'data-projid="'+ctx.projId+'"', 'Open Active Concept in AlderCAD', '<svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M8 1.5 14 5v6l-6 3.5L2 11V5l6-3.5Z" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/><path d="M2 5l6 3.5L14 5M8 8.5V14.5" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"/></svg>');
      h += menuDivider();
      h += menuItem('projectInfo', 'data-projid="'+ctx.projId+'"', 'Project info', ICO_INFO);
      h += menuItem('manageProjectHold', 'data-projid="'+ctx.projId+'"', 'Project hold…', ICO_INFO);
      h += menuItem('manageProjectCloseout', 'data-projid="'+ctx.projId+'"', 'Close out project…', ICO_INFO);
      h += menuDivider();
      h += menuItem('expandAll',   'data-key="proj:'+ctx.projId+'"', 'Expand all',   ICO_EXPAND);
      h += menuItem('collapseAll', 'data-key="proj:'+ctx.projId+'"', 'Collapse all', ICO_COLLAPSE);
    } else if (type === 'root') {
      if (ctx.rootName === 'schedule') {
        h += menuItem('addPhase', '', 'Add phase', ICO_ADD);
        h += menuItem('openGantt', '', 'Open Gantt scheduler', ICO_DATE);
        h += menuDivider();
      }
      if (ctx.rootName === 'permits') {
        h += menuItem('addPermit', '', 'Add permit', ICO_ADD);
        h += menuItem('permitReportCard', '', 'Plan check report card…', ICO_INFO);
        h += menuDivider();
      }
      if (ctx.rootName === 'documents') {
        h += menuItem('uploadRefDocs', '', 'Upload documents', ICO_ADD);
        h += menuDivider();
      }
      if (ctx.rootName === 'projects') {
        if (!ctx.showClosedProjects) {
          var _onHoldN = (ctx.projectHoldIds && ctx.projectHoldIds.size) || 0;
          var _onHoldLbl = ctx.showOnHoldProjects ? 'Hide on-hold projects' : 'Show on-hold projects' + (_onHoldN ? ' (' + _onHoldN + ' hidden)' : '');
          h += menuItem('toggleOnHoldProjects', '', _onHoldLbl, ICO_INFO);
        }
        h += menuItem('toggleClosedProjects', '', ctx.showClosedProjects ? 'Show active projects' : 'Show closed projects…', ICO_INFO);
        h += menuDivider();
      }
      // Bid campaigns are created from the 'Bid Campaign' milestone ⋯ menu, not the root.
      // The Construction root menu provides expand/collapse only.
      h += menuItem('expandAll', 'data-key="root:'+ctx.rootName+'"', 'Expand all', ICO_EXPAND);
      h += menuItem('collapseAll', 'data-key="root:'+ctx.rootName+'"', 'Collapse all', ICO_COLLAPSE);
    } else if (type === 'project-nav') {
      h += menuItem('expandAll',   'data-key="'+ctx.nodeKey+'"', 'Expand all',   ICO_EXPAND);
      h += menuItem('collapseAll', 'data-key="'+ctx.nodeKey+'"', 'Collapse all', ICO_COLLAPSE);
    } else if (type === 'section') {
      var stype = ctx.sectionType || '';
      if (stype === 'checklist') {
        h += menuItem('enterSelectModeAll', 'data-msid="'+ctx.msId+'"', 'Select items', ICO_SELECT);
        h += menuDivider();
        h += menuItem('resetChecklist', 'data-msid="'+ctx.msId+'"', 'Reset checklist', ICO_TEMPLATE);
        h += menuDivider();
      }
      if (stype === 'sheets') {
        h += menuItem('openAddSheets', 'data-msid="'+ctx.msId+'" data-sectionkey="'+ctx.sectionKey+'"', 'Add drawing sheets', ICO_ADD);
        if (!ctx.msId) {
          /* Production context only — milestone sheets don't support batch export */
          h += menuItem('enterSelectSheets', '', 'Select sheets for export', ICO_SELECT);
        }
        h += menuDivider();
      }
      if (stype === 'threads' && ctx.sectionKey && ctx.sectionKey.indexOf('sec:phase:') === 0) {
        h += menuItem('addPhaseThread', 'data-pid="' + (ctx.sectionKey.split(':')[2] || '') + '"', 'Add thread', ICO_ADD);
        h += menuDivider();
      }
      if (stype === 'bim' && !ctx.msId) {
        /* Reference-level context only (Design > BIM Systems) — systems aren't milestone-scoped, so setup lives here, not inside a milestone's own BIM Systems view. */
        h += menuItem('setupBim', '', 'Set up BIM systems', ICO_ADD);
        h += menuDivider();
      }
      if (stype === 'parcels') {
        h += menuItem('addParcel', '', 'Add parcel', ICO_ADD);
        h += menuDivider();
      }
      h += menuItem('expandAll', 'data-key="'+ctx.sectionKey+'"', 'Expand all', ICO_EXPAND);
      h += menuItem('collapseAll', 'data-key="'+ctx.sectionKey+'"', 'Collapse all', ICO_COLLAPSE);
    }
    h += '</div>';
    return h;
  }
  function menuItem(fn, extraAttrs, label, iconSvg) {
    var ico = iconSvg ? '<span class="wf-menu-icon">' + iconSvg + '</span>' : '<span class="wf-menu-icon" style="width:14px;"></span>';
    return '<button class="wf-menu-item" data-fn="' + fn + '" ' + extraAttrs + '>' + ico + label + '</button>';
  }
  function menuDivider() { return '<div class="wf-menu-divider"></div>'; }
  function escAttr(s) { return (s||'').replace(/"/g,'&quot;'); }
  /* Icon SVGs for menu items */
  function wfAddBtn(type, id) {
    return ''; /* Add milestone removed - use phase action menu instead */
  }

  /*    SIDEBAR    */
  function buildSidebar() {
    return ''; /* Coordination now shown inline in outline */
  }

  /*    WIRE EVENTS    */
  function wireOutlineBody() {
    /* Wire only outline nodes - NOT the search input (already wired once at init) */
    var timeToggles = document.querySelectorAll('.wf-inline-toggle-inp');
    for (var ti=0; ti<timeToggles.length; ti++) {
      (function(inp) {
        inp.addEventListener('change', function() {
          togglePhaseTime(inp.getAttribute('data-pid'), inp.getAttribute('data-val') === 'true');
        });
      })(timeToggles[ti]);
    }
    var inputs = document.querySelectorAll('.wf-lft-input');
    for (var i=0; i<inputs.length; i++) {
      (function(inp) {
        var msId = inp.getAttribute('data-msid');
        setTimeout(function() { inp.focus(); }, 30);
        inp.addEventListener('change', function() { commitLft(msId, inp.value); });
        inp.addEventListener('blur', function() { if (wf.editingLft===msId) { wf.editingLft=null; render(); } });
        inp.addEventListener('keydown', function(e) { if (e.key==='Escape') { wf.editingLft=null; render(); } });
      })(inputs[i]);
    }
    /* Checkboxes wired via document delegation - see wireCheckboxDelegation() */
  }

  function wireOutline() {
    /* Phase time toggles — scoped to #aj-sc-root so React-owned inputs in #aj-sc-sections
       don't accumulate extra listeners on every render() call */
    var _root = rootEl || document;
    var timeToggles = _root.querySelectorAll('.wf-inline-toggle-inp');
    for (var ti=0; ti<timeToggles.length; ti++) {
      (function(inp) {
        inp.addEventListener('change', function() {
          togglePhaseTime(inp.getAttribute('data-pid'), inp.getAttribute('data-val') === 'true');
        });
      })(timeToggles[ti]);
    }
    /* LFT inputs */
    var inputs = _root.querySelectorAll('.wf-lft-input');
    for (var i=0; i<inputs.length; i++) {
      (function(inp) {
        var msId = inp.getAttribute('data-msid');
        setTimeout(function() { inp.focus(); }, 30);
        inp.addEventListener('change', function() { commitLft(msId, inp.value); });
        inp.addEventListener('blur', function() { if (wf.editingLft===msId) { wf.editingLft=null; render(); } });
        inp.addEventListener('keydown', function(e) { if (e.key==='Escape') { wf.editingLft=null; render(); } });
      })(inputs[i]);
    }
    /* Checkboxes handled via document delegation in wireCheckboxDelegation() */
    /* Menu close handled in main delegated listener */
  }

  function loadExpandedData() {
    /* Queue handles throttling - just fire all loads */
    if (bimCache === null) loadBim();
    state.milestones.forEach(function(ms) {
      if (!clCache[ms.id]) loadChecklist(ms.id);
      if (sheetCache[ms.id] === undefined) loadSheets(ms.id);
    });
  }

  /*    INLINE ACTIONS    */
  /* Surgically update a root section children without full render */
  function refreshRootChildren(name) {
    var key = rootSectionKey(name);
    var rootEl2 = document.querySelector('.wf-node[data-key="' + key + '"] > .wf-children');
    if (!rootEl2) return; /* not expanded, nothing to update */
    /* Don't manipulate nodes inside #aj-sc-sections — React owns those; notify instead */
    var _rcEl = document.getElementById('aj-sc-sections');
    if (_rcEl && _rcEl.contains(rootEl2)) {
      window.dispatchEvent(new CustomEvent('outline-render'));
      return;
    }
    var h = name === 'design' ? buildDesignChildren() :
             name === 'production' ? buildProductionChildren() :
             name === 'schedule' ? buildScheduleChildren() : '';
    rootEl2.innerHTML = h;
    wireOutline();
  }

  /* Surgically update a single section node without full render */
  function refreshSection(msId, type, buildFn) {
    var secKey = sectionKey(msId, type);
    var secNode = document.querySelector('.wf-node[data-key="' + secKey + '"]');
    /* Don't manipulate nodes inside #aj-sc-sections — React owns those */
    var reactEl = document.getElementById('aj-sc-sections');
    var isReactOwned = secNode && reactEl && reactEl.contains(secNode);
    if (!secNode || isReactOwned) { if (!wf.searchQuery) render(); return; }
    /* Rebuild the full section node html and swap it */
    var tmp = document.createElement('div');
    tmp.innerHTML = buildFn();
    if (tmp.firstChild) secNode.parentNode.replaceChild(tmp.firstChild, secNode);
    wireOutline();
  }

  function refreshCats(msId) {
    /* If expand-all was pending for this ms, expand all categories now */
    if (pendingFullExpand[msId]) {
      delete pendingFullExpand[msId];
      var allItems = clCache[msId] || [];
      var allCats = {};
      allItems.forEach(function(it){ var c=getFirst(it.fields['Category (from Deliverable Checklist Item Template)'])||'?'; allCats[c]=true; });
      Object.keys(allCats).forEach(function(c){ wf.collapsed[catKey(msId,c)] = false; });
    }
    var items = clCache[msId] || [];
    var cats = getCategoryNodes(msId, items);
    var h = '';
    cats.forEach(function(cat) { h += buildCatNode(cat, msId); });
    if (wf.zoomMsId === msId) {
      if (wf.zoomSection === sectionKey(msId, 'checklist')) {
        /* Section zoom — cats render directly inside .wf-list */
        var listEl = document.querySelector('.wf-list');
        if (listEl) { listEl.innerHTML = h; wireOutline(); return; }
      }
      /* Full milestone zoom — React handles sections; trigger outline-render via render() */
      render(); return;
    }
    /* Normal view - update checklist section children */
    var secKey = sectionKey(msId, 'checklist');
    var secEl = document.querySelector('.wf-node[data-key="' + secKey + '"] > .wf-children');
    /* Don't manipulate nodes inside #aj-sc-sections — React owns those */
    var _rcEl = document.getElementById('aj-sc-sections');
    if (secEl && _rcEl && _rcEl.contains(secEl)) { render(); return; }
    if (secEl) { secEl.innerHTML = h; wireOutline(); return; }
    /* Section not expanded yet - no update needed */
    /* If searching, re-run search to pick up newly loaded items */
    if (wf.searchQuery) { renderOutlineOnly(); wireSearchInput(); }
  }

  function commitLft(msId, newDate) {
    if (!newDate) { wf.editingLft=null; render(); return; }
    var ms = getMsById(msId);
    if (!ms) return;
    var old = ms.fields['Latest Finish (LFT)'];
    ms.fields['Latest Finish (LFT)'] = newDate;
    wf.editingLft = null;
    wf.dirty[msId] = true;
    render();
    showPill('Date updated', false);
    apiPatch(TBL_DEL, msId, {'Latest Finish (LFT)':newDate}, function(ok) {
      if (!ok) { ms.fields['Latest Finish (LFT)']=old; wf.dirty[msId]=false; render(); showPill('Save failed',true); }
    });
  }

  function togglePhaseTime(phaseId, currentVal) {
    var ph = getPhaseById(phaseId);
    if (!ph) return;
    var newVal = !currentVal;
    ph.fields['Allow Time Entries'] = newVal;
    wf.menuNodeId = null;
    /* Update just the toggle and signal in place - no full render */
    updatePhaseTimeInline(phaseId, newVal);
    apiPatch(TBL_PH, phaseId, {'Allow Time Entries':newVal}, function(ok) {
      if (!ok) {
        ph.fields['Allow Time Entries'] = currentVal;
        updatePhaseTimeInline(phaseId, currentVal);
        showPill('Save failed', true);
      } else {
        showPill(newVal ? 'Time entries enabled' : 'Time entries disabled', false);
      }
    });
  }

  function updatePhaseTimeInline(phaseId, newVal) {
    /* Find the toggle checkbox and track for this phase and update without re-render */
    var phRowKey = phKey(phaseId);
    var phRow = document.querySelector('.wf-row[data-key="' + phRowKey + '"]');
    if (!phRow) { render(); return; }
    /* Update checkbox state */
    var inp = phRow.querySelector('.wf-inline-toggle-inp');
    if (inp) {
      inp.checked = newVal;
      inp.setAttribute('data-val', newVal);
    }
    /* Update menu button data-allowtime so next menu open reflects new state */
    var menuBtn = phRow.querySelector('.wf-menu-btn[data-type="phase"]');
    if (menuBtn) menuBtn.setAttribute('data-allowtime', newVal ? 'true' : 'false');
    /* Update signal text */
    var signal = phRow.querySelector('.wf-phase-signal');
    var ph = getPhaseById(phaseId);
    var milestones = getMilestonesForPhase(phaseId);
    var activeMsCount = milestones.filter(function(m){ return !m.fields['Posted Date']; }).length;
    var timeOpenNoWork = newVal && activeMsCount === 0;
    if (signal) {
      if (!timeOpenNoWork) signal.parentNode.removeChild(signal);
    } else if (timeOpenNoWork) {
      var meta = phRow.querySelector('.wf-meta');
      if (meta) {
        var s = document.createElement('span');
        s.className = 'wf-phase-signal wf-phase-signal--amber';
        s.textContent = 'Time open, no active work';
        meta.appendChild(s);
      }
    }
  }

  function openDeletePhaseModal(phId) {
    var ph = getPhaseById(phId);
    if (!ph) return;
    var phName = ph.fields['Phase Name'] || ph.fields['Name'] || 'Phase';
    var msCount = getMilestonesForPhase(phId).length;
    var m = createModal('wf-delete-ph-modal', 'Delete phase', 380);

    if (msCount > 0) {
      /* Blocked - show explanation */
      var block = document.createElement('div');
      block.style.cssText = 'background:#fff5f5;border:0.5px solid rgba(192,57,43,0.2);border-radius:6px;padding:14px 16px;';
      block.innerHTML = '<div style="font-family:\"DM Sans\",sans-serif;font-size:13px;color:#c0392b;font-weight:500;margin-bottom:6px;">Cannot delete this phase</div>'
        + '<div style="font-family:\"DM Sans\",sans-serif;font-size:13px;color:#07272D;line-height:1.6;">'
        + '<strong>' + phName + '</strong> has ' + msCount + ' milestone' + (msCount===1?'':'s') + ' that must be deleted first.</div>'
        + '<div style="font-family:\"DM Sans\",sans-serif;font-size:12px;color:#7a9aaa;margin-top:8px;line-height:1.5;">Open the milestone menu and choose Delete milestone for each one, then return here to delete the phase.</div>';
      m.body.appendChild(block);
      var ok = modalBtn('OK', true);
      ok.addEventListener('click', m.close);
      m.foot.appendChild(ok);
    } else {
      /* Empty phase - show confirm form */
      var warn = document.createElement('div');
      warn.style.cssText = 'background:#fff5f5;border:0.5px solid rgba(192,57,43,0.2);border-radius:6px;padding:12px 14px;margin-bottom:16px;';
      warn.innerHTML = '<div style="font-family:\"DM Sans\",sans-serif;font-size:13px;color:#c0392b;font-weight:500;margin-bottom:4px;">This cannot be undone</div>'
        + '<div style="font-family:\"DM Sans\",sans-serif;font-size:12px;color:#7a9aaa;line-height:1.5;">Deleting <strong style="color:#07272D;">' + phName + '</strong> will permanently remove the phase record.</div>';
      m.body.appendChild(warn);
      m.body.appendChild(modalLabel('Type DELETE to confirm'));
      var inp = document.createElement('input');
      inp.type = 'text';
      inp.placeholder = 'DELETE';
      inp.style.cssText = 'width:100%;box-sizing:border-box;padding:8px 10px;border:0.5px solid rgba(7,39,45,0.20);border-radius:4px;font-family:"DM Sans",sans-serif;font-size:14px;color:#07272D;outline:none;letter-spacing:0.05em;';
      m.body.appendChild(inp);
      var cancel = modalBtn('Cancel', false);
      var confirm = modalBtn('Delete', true);
      confirm.style.background = '#c0392b';
      confirm.disabled = true;
      confirm.style.opacity = '0.4';
      inp.addEventListener('input', function() {
        var ready = inp.value.trim() === 'DELETE';
        confirm.disabled = !ready;
        confirm.style.opacity = ready ? '1' : '0.4';
      });
      cancel.addEventListener('click', m.close);
      confirm.addEventListener('click', function() {
        if (inp.value.trim() !== 'DELETE') return;
        confirm.disabled = true;
        confirm.textContent = 'Deleting...';
        apiDelete(TBL_PH, phId, function(ok) {
          if (ok) {
            state.phases = state.phases.filter(function(p) { return p.id !== phId; });
            if (wf.zoomPhaseId === phId) {
              wf.zoomPhaseId = null; wf.zoomMsId = null; wf.zoomCat = null;
              wf.zoomItemId = null; wf.zoomSection = null; wf.zoomRoot = null;
            }
            m.close();
            render();
            showPill('Phase deleted', false);
          } else {
            confirm.disabled = false;
            confirm.textContent = 'Delete';
            showPill('Delete failed', true);
          }
        });
      });
      m.foot.appendChild(cancel);
      m.foot.appendChild(confirm);
      setTimeout(function() { inp.focus(); }, 60);
    }
  }

  /* Deleting a milestone heals the schedule (docs/OPERATING_MODEL.md, 2026-09-24): the React
     DeleteMilestoneModal shows what it waited on, the rest of its group to delete too, and a choice
     for each milestone left loose, before anything is deleted. The old box only dropped the links. */
  function openDeleteMsModal(msId) {
    var ms = getMsById(msId);
    if (!ms) return;
    var pid = (ms.fields && ms.fields['Project'] && ms.fields['Project'][0]) || wf.zoomProjectId || ctx.recordId || null;
    window.dispatchEvent(new CustomEvent('open-delete-ms-modal', { detail: { msId: msId, projectId: pid } }));
  }

  /* Restored 2026-09-25: removed by accident with the old delete window in be8f6f3e (the Reset
     checklist / Reset category menu items still called it and silently did nothing). */
  function resetChecklist(msId, cat) {
    /* cat is optional - if provided, only reset that category */
    if (refuseBlocked(msId)) return;   /* waiting on a predecessor: read-only (outline/blocked.js) */
    var ms = getMsById(msId);
    if (!ms) { showPill('Milestone not found', true); return; }
    var tplIds = ms.fields['Deliverable'] || [];
    if (!tplIds.length) { showPill('No template on this milestone', true); return; }
    var tplId = tplIds[0];
    showPill('Syncing...', false);

    /* Step 1: fetch templates */
    var tplFilter = 'FIND("' + tplId + '",ARRAYJOIN({Record ID (from Project Deliverable Types)},","))';
    if (cat) tplFilter = 'AND(' + tplFilter + ',{Category}="' + cat + '")';
    var tplQs = 'filterByFormula=' + encodeURIComponent(tplFilter) + '&sort[0][field]=Sort+Order&sort[0][direction]=asc&maxRecords=200';

    apiGet(TBL_CLT, tplQs, function(td) {
      var templates = td ? td.records : [];
      if (!templates.length && !cat) { showPill('No templates found for this milestone', true); return; }

      /* Step 2: fetch existing checklist items directly from API */
      var clFilter = 'FIND("' + msId + '",ARRAYJOIN({Record ID (from Deliverable)},","))';
      if (cat) clFilter = 'AND(' + clFilter + ',{Category (from Deliverable Checklist Item Template)}="' + cat + '")';
      var clQs = 'filterByFormula=' + encodeURIComponent(clFilter) + '&maxRecords=200';

      apiGet(TBL_CL, clQs, function(cd) {
        var existing = cd ? cd.records : [];

        /* Valid template IDs for THIS milestone's deliverable type. Built first so the keeper
           grouping below ignores foreign-template items — otherwise an orphan that links to a
           template from a DIFFERENT deliverable type becomes its own "keeper" and is never deleted
           (the root cause of "already up to date" leaving stray items on the checklist). */
        var tplIdSet = {};
        templates.forEach(function(t) { tplIdSet[t.id] = true; });

        /* Group existing items by template ID — keep best candidate per template. */
        /* Priority: done > skipped > incomplete (keep the most meaningful one). */
        /* Only group items whose template belongs to this deliverable type; foreign-template */
        /* items are NOT keepers (so they fall through to the orphan-deletion branch below). */
        var existingByTpl = {};
        existing.forEach(function(item) {
          var tid = (item.fields['Deliverable Checklist Item Template'] || [])[0] || '';
          if (!tid || !tplIdSet[tid]) return;
          if (!existingByTpl[tid]) {
            existingByTpl[tid] = item;
          } else {
            /* Keep the more meaningful one — done beats skipped beats incomplete */
            var cur = existingByTpl[tid];
            var curScore = cur.fields['Complete']===true ? 2 : cur.fields['Milestone Not Required']===true ? 1 : 0;
            var newScore = item.fields['Complete']===true ? 2 : item.fields['Milestone Not Required']===true ? 1 : 0;
            if (newScore > curScore) existingByTpl[tid] = item;
          }
        });

        /* Templates to create (no existing item for this template) */
        var toCreate = [];
        templates.forEach(function(tpl) {
          if (!existingByTpl[tpl.id]) {
            toCreate.push({'Deliverable': [msId], 'Deliverable Checklist Item Template': [tpl.id], 'Complete': false});
          }
        });

        /* Items to delete: orphans (foreign / removed template) OR duplicates (not the kept one).
           tplIdSet is built above. keepIds covers only valid-template items, so orphans are deletable. */
        var keepIds = {};
        Object.keys(existingByTpl).forEach(function(tid) { keepIds[existingByTpl[tid].id] = true; });
        var toDelete = [];
        existing.forEach(function(item) {
          var tid = (item.fields['Deliverable Checklist Item Template'] || [])[0] || '';
          var isDone = item.fields['Complete'] === true;
          var isSkip = item.fields['Milestone Not Required'] === true;
          if (keepIds[item.id]) return; /* This is the keeper for its template */
          if (!tid) {
            /* No template link at all — always delete; these are true orphans */
            toDelete.push(item.id);
          } else if (!tplIdSet[tid]) {
            /* Foreign template (removed from this deliverable type) — delete only if not done/skipped */
            if (!isDone && !isSkip) toDelete.push(item.id);
          } else {
            /* Duplicate — same template already has a keeper. Delete only if not done/skipped */
            if (!isDone && !isSkip) toDelete.push(item.id);
          }
        });

        var orphans = existing.filter(function(item) {
          var tid = (item.fields['Deliverable Checklist Item Template'] || [])[0] || '';
          return tid && !tplIdSet[tid];
        });
        var orphansDone = orphans.filter(function(i){ return i.fields['Complete']===true; }).length;
        var orphansSkip = orphans.filter(function(i){ return i.fields['Milestone Not Required']===true; }).length;
        var orphansNoTpl = existing.filter(function(i){ return !(i.fields['Deliverable Checklist Item Template']||[])[0]; }).length;
        console.log('reset: tplId=' + tplId + ' templates=' + templates.length + ' existing=' + existing.length + ' toCreate=' + toCreate.length + ' toDelete=' + toDelete.length);
        console.log('reset detail: orphans=' + orphans.length + ' orphansDone=' + orphansDone + ' orphansSkip=' + orphansSkip + ' noTplId=' + orphansNoTpl);

        if (!toCreate.length && !toDelete.length) {
          showPill('Checklist already up to date', false);
          return;
        }

        var ops = (toCreate.length > 0 ? 1 : 0) + (toDelete.length > 0 ? 1 : 0);
        var done5 = 0;
        function finish5() {
          done5++;
          if (done5 < ops) return;
          /* Bust cache and reload */
          clCache[msId] = undefined;
          loadChecklist(msId);
          showPill((cat ? 'Category' : 'Checklist') + ' synced (' + toCreate.length + ' added, ' + toDelete.length + ' removed)', false);
        }
        if (toCreate.length) apiPostBatch(TBL_CL, toCreate, function() { finish5(); });
        if (toDelete.length) apiDeleteBatch(TBL_CL, toDelete, function() { finish5(); });
      });
    });
  }


  function resetCategory(msId, cat) {
    if (refuseBlocked(msId)) return;
    resetChecklist(msId, cat);
  }


  /* ── Move milestone to a different phase ──────────────────────────────── */

  /* ── Milestone action: Open AlderSPEC (spec-release milestones) ─────────────
     AlderSPEC Phase 2 — opens the tool in its own tab (/spec), deep-linked to this
     milestone's release via ?msId=. The page self-resolves the Selected concept and the
     milestone's release mode, so none of the old concept/type plumbing is needed. */
  function openSpecEditorForMs(msId) {
    var pid = ctx.recordId || wf.zoomProjectId || '';
    window.open('/spec?projectId=' + encodeURIComponent(pid)
      + '&msId=' + encodeURIComponent(msId || '')
      + (ctx.projectName ? '&projectName=' + encodeURIComponent(ctx.projectName) : ''), '_blank');
  }

  /* ── Concept action: Set as Active ───────────────────────────────────────
     "Active" is UI terminology; the Airtable field remains `Selected Concept`.
     Writes are ordered target-first, old-cleared-second (Round 8): if clearing the
     previously-active concept fails after the target write succeeds, the project is
     left with TWO active concepts (recoverable — the user just retries) rather than
     ZERO (which would silently break Specifications/Schedules until noticed). */
  function openSetSelectedConceptModal(conceptId, owner) {
    var concept = null;
    for (var i = 0; i < (conceptCache || []).length; i++) {
      if (conceptCache[i].id === conceptId) { concept = conceptCache[i]; break; }
    }
    if (!concept) return;
    var cname = concept.fields['Parti Pris Title'] || 'Unnamed Concept';
    var currentActive = null;
    for (var k = 0; k < (conceptCache || []).length; k++) {
      if (conceptCache[k].id !== conceptId && conceptCache[k].fields['Selected Concept'] === true) { currentActive = conceptCache[k]; break; }
    }
    var activeName = currentActive ? (currentActive.fields['Parti Pris Title'] || 'Unnamed Concept') : null;

    var m = createModal('wf-set-selected-concept-modal', 'Set active concept', 380);

    var desc = document.createElement('div');
    desc.style.cssText = 'font-family:"DM Sans",sans-serif;font-size:13px;color:#3a5260;line-height:1.55;margin-bottom:12px;';
    desc.textContent = 'Only one concept can be active on a project at a time. The active concept is the one that drives Specifications, Schedules, and every other project surface that reads off "the concept."';
    m.body.appendChild(desc);

    var nameEl = document.createElement('div');
    nameEl.style.cssText = 'font-family:"DM Sans",sans-serif;font-size:13px;color:#07272D;'
      + 'padding:8px 10px;background:rgba(29,158,117,0.08);border-radius:4px;font-weight:500;margin-bottom:4px;';
    nameEl.textContent = cname + ' — will become active';
    m.body.appendChild(nameEl);

    if (activeName) {
      var swapEl = document.createElement('div');
      swapEl.style.cssText = 'font-family:"DM Sans",sans-serif;font-size:13px;color:#3a5260;'
        + 'padding:8px 10px;background:rgba(7,39,45,0.04);border-radius:4px;margin-bottom:4px;';
      swapEl.textContent = activeName + ' — will trade places, moving back to Candidates';
      m.body.appendChild(swapEl);
    }

    var cancel  = modalBtn('Cancel', false);
    var confirm = modalBtn('Set as active', true);
    var saving  = false;

    cancel.addEventListener('click', function() { m.close(); });

    confirm.addEventListener('click', function() {
      if (saving) return;
      saving = true;
      confirm.disabled = true;
      confirm.textContent = 'Saving...';

      function finish(hadClearFailure) {
        m.close();
        showPill(hadClearFailure ? 'Active concept set — the previous active concept could not be cleared, please retry' : 'Active concept set', !!hadClearFailure);
        render();
        /* Notify React (DesignSection) to re-resolve the active concept + spec gate */
        window.dispatchEvent(new Event('concepts-updated'));
        /* Drawing-sheet PDF Inserts follow the active concept (best-effort). */
        var projId = (concept.fields['Project'] || [])[0] || ctx.recordId || wf.zoomProjectId;
        relinkProjectPdfInserts(projId, conceptId).then(function(r) {
          if (r && r.failed) showPill(r.failed + ' PDF insert(s) could not be re-linked to the active concept', true);
        }).catch(function(err) { console.warn('[setActiveConcept] PDF insert relink failed', err); });
      }

      /* Target-first: only clear the old active concept(s) once the new one is
         confirmed active, so a mid-swap failure never leaves zero active concepts. */
      apiPatch(TBL_CONCEPT, conceptId, { 'Selected Concept': true }, function(ok) {
        if (!ok) {
          saving = false;
          confirm.disabled = false;
          confirm.textContent = 'Set as active';
          showPill('Save failed', true);
          return;
        }
        for (var j = 0; j < (conceptCache || []).length; j++) {
          if (conceptCache[j].id === conceptId) conceptCache[j].fields['Selected Concept'] = true;
        }

        var others = (conceptCache || []).filter(function(c) {
          return c.id !== conceptId && c.fields['Selected Concept'];
        });
        if (!others.length) { finish(false); return; }

        var patchCount = 0;
        var anyFailed = false;
        others.forEach(function(c) {
          apiPatch(TBL_CONCEPT, c.id, { 'Selected Concept': false }, function(clearOk) {
            patchCount++;
            if (clearOk) {
              for (var j2 = 0; j2 < (conceptCache || []).length; j2++) {
                if (conceptCache[j2].id === c.id) conceptCache[j2].fields['Selected Concept'] = false;
              }
            } else {
              anyFailed = true;
            }
            if (patchCount >= others.length) finish(anyFailed);
          });
        });
      });
    });

    m.foot.appendChild(cancel);
    m.foot.appendChild(confirm);
  }

  /* ── Concept action: Edit Name ────────────────────────────────────────── */
  function openEditConceptNameModal(conceptId, owner) {
    var concept = null;
    for (var i = 0; i < (conceptCache || []).length; i++) {
      if (conceptCache[i].id === conceptId) { concept = conceptCache[i]; break; }
    }
    if (!concept) return;
    var currentName = concept.fields['Parti Pris Title'] || '';

    var m = createModal('wf-edit-concept-name-modal', 'Edit concept name', 360);

    m.body.appendChild(modalLabel('Concept name'));
    var inp = document.createElement('input');
    inp.type = 'text';
    inp.value = currentName;
    inp.style.cssText = 'display:block;width:100%;box-sizing:border-box;padding:8px 10px;'
      + 'border:0.5px solid rgba(7,39,45,0.20);border-radius:4px;font-family:"DM Sans",sans-serif;'
      + 'font-size:13px;color:#07272D;outline:none;';
    m.body.appendChild(inp);

    /* Concept names are unique per project — live check against the loaded list, and
       renameConcept re-checks against Airtable before writing. */
    var errEl = document.createElement('div');
    errEl.style.cssText = 'font-family:"DM Sans",sans-serif;font-size:11px;color:#c0392b;line-height:1.45;margin-top:6px;min-height:14px;';
    m.body.appendChild(errEl);

    var cancel = modalBtn('Cancel', false);
    var save   = modalBtn('Save', true);
    var saving = false;

    function dupeFor(val) { return findConceptByName(conceptCache || [], val, conceptId); }
    function refreshValidity() {
      var val = inp.value.trim();
      var dupe = val ? dupeFor(val) : null;
      errEl.textContent = dupe ? 'Another concept is already named "' + (dupe.fields['Parti Pris Title'] || '') + '".' : '';
      save.disabled = saving || !val || !!dupe;
    }
    inp.addEventListener('input', refreshValidity);

    cancel.addEventListener('click', function() { m.close(); });

    save.addEventListener('click', function() {
      var val = inp.value.trim();
      if (!val || saving || dupeFor(val)) { refreshValidity(); return; }
      saving = true;
      save.disabled = true;
      save.textContent = 'Saving...';

      var projectId = owner || ctx.recordId || wf.zoomProjectId || '';
      renameConcept(projectId, conceptId, val).then(function() {
        if (concept) concept.fields['Parti Pris Title'] = val;
        m.close();
        showPill('Name updated', false);
        render();
        window.dispatchEvent(new Event('concepts-updated'));
      }).catch(function(err) {
        saving = false;
        save.textContent = 'Save';
        errEl.textContent = (err && err.message) || 'Save failed';
        refreshValidity();
      });
    });

    m.foot.appendChild(cancel);
    m.foot.appendChild(save);
    setTimeout(function() { inp.focus(); inp.select(); }, 60);
  }

  /* ── Concept action: Delete ───────────────────────────────────────────── */
  function openDeleteConceptModal(conceptId, owner) {
    var concept = null;
    for (var i = 0; i < (conceptCache || []).length; i++) {
      if (conceptCache[i].id === conceptId) { concept = conceptCache[i]; break; }
    }
    if (!concept) return;
    var cname = concept.fields['Parti Pris Title'] || 'Unnamed Concept';

    var m = createModal('wf-delete-concept-modal', 'Delete concept', 360);

    var desc = document.createElement('div');
    desc.style.cssText = 'font-family:"DM Sans",sans-serif;font-size:13px;color:#c0392b;line-height:1.55;margin-bottom:12px;';
    desc.textContent = 'This will permanently delete this concept and all associated data. This cannot be undone.';
    m.body.appendChild(desc);

    var nameEl = document.createElement('div');
    nameEl.style.cssText = 'font-family:"DM Sans",sans-serif;font-size:13px;color:#07272D;'
      + 'padding:8px 10px;background:rgba(7,39,45,0.04);border-radius:4px;font-weight:500;margin-bottom:16px;';
    nameEl.textContent = cname;
    m.body.appendChild(nameEl);

    m.body.appendChild(modalLabel('Type DELETE to confirm'));
    var inp = document.createElement('input');
    inp.type = 'text';
    inp.placeholder = 'DELETE';
    inp.style.cssText = 'display:block;width:100%;box-sizing:border-box;padding:8px 10px;'
      + 'border:0.5px solid rgba(7,39,45,0.20);border-radius:4px;font-family:"DM Mono",monospace;'
      + 'font-size:13px;color:#07272D;outline:none;letter-spacing:0.04em;';
    m.body.appendChild(inp);

    var cancel  = modalBtn('Cancel', false);
    var confirm = modalBtn('Delete', true);
    confirm.style.background  = '#c0392b';
    confirm.style.borderColor = '#c0392b';
    confirm.disabled = true;
    var saving = false;

    inp.addEventListener('input', function() {
      confirm.disabled = inp.value !== 'DELETE';
    });

    cancel.addEventListener('click', function() { m.close(); });

    confirm.addEventListener('click', function() {
      if (saving || inp.value !== 'DELETE') return;
      saving = true;
      confirm.disabled = true;
      confirm.textContent = 'Deleting...';

      apiDelete(TBL_CONCEPT, conceptId, function(ok) {
        if (!ok) {
          saving = false;
          confirm.disabled = false;
          confirm.textContent = 'Delete';
          showPill('Delete failed', true);
          return;
        }
        conceptCache = (conceptCache || []).filter(function(c) { return c.id !== conceptId; });
        m.close();
        showPill('Concept deleted', false);
        render();
        // Reference > Design keeps its own concept list; tell it to reload.
        window.dispatchEvent(new Event('concepts-updated'));
      });
    });

    m.foot.appendChild(cancel);
    m.foot.appendChild(confirm);
    setTimeout(function() { inp.focus(); }, 60);
  }

  /* ── Move room to a different level ──────────────────────────────────── */
  /* ── Add room ─────────────────────────────────────────────────────────── */
  function openAddRoomModal(levelId, conceptId) {
    /* Resolve level name and concept id from context */
    var level = null;
    Object.keys(levelCache).forEach(function(uid) {
      (levelCache[uid]||[]).forEach(function(l) { if (l.id === levelId) level = l; });
    });
    /* If no conceptId passed, try to resolve from levelCache → unitCache */
    if (!conceptId) {
      Object.keys(unitCache).forEach(function(cid) {
        (unitCache[cid]||[]).forEach(function(u) {
          (levelCache[u.id]||[]).forEach(function(l) { if (l.id === levelId) conceptId = cid; });
        });
      });
    }
    var levelName = level ? (level.fields['Name'] || 'Level') : '';
    var levelSsName = levelName; /* use level name for Level singleSelect */

    var m = createModal('wf-add-room-modal', 'Add room', 380);

    /* Room Number */
    m.body.appendChild(modalLabel('Room number'));
    var numInp = document.createElement('input');
    numInp.type = 'text';
    numInp.placeholder = 'e.g. 101';
    numInp.style.cssText = 'display:block;width:100%;box-sizing:border-box;padding:8px 10px;'
      + 'border:0.5px solid rgba(7,39,45,0.20);border-radius:4px;font-family:"DM Mono",monospace;'
      + 'font-size:13px;color:#07272D;outline:none;margin-bottom:12px;';
    m.body.appendChild(numInp);

    /* Room name */
    m.body.appendChild(modalLabel('Room name'));
    var nameInp = document.createElement('input');
    nameInp.type = 'text';
    nameInp.placeholder = 'e.g. Primary Bedroom';
    nameInp.style.cssText = 'display:block;width:100%;box-sizing:border-box;padding:8px 10px;'
      + 'border:0.5px solid rgba(7,39,45,0.20);border-radius:4px;font-family:"DM Sans",sans-serif;'
      + 'font-size:13px;color:#07272D;outline:none;margin-bottom:12px;';
    m.body.appendChild(nameInp);

    /* Scope Type */
    m.body.appendChild(modalLabel('Scope type'));
    var scopeSel = document.createElement('select');
    scopeSel.style.cssText = 'display:block;width:100%;box-sizing:border-box;padding:8px 10px;'
      + 'border:0.5px solid rgba(7,39,45,0.20);border-radius:4px;font-family:"DM Sans",sans-serif;'
      + 'font-size:13px;color:#07272D;outline:none;background:white;margin-bottom:12px;';
    var blankScope = document.createElement('option');
    blankScope.value = ''; blankScope.textContent = 'Select scope...';
    scopeSel.appendChild(blankScope);
    SCOPE_TYPE_OPTIONS.forEach(function(opt) {
      var o = document.createElement('option');
      o.value = opt; o.textContent = opt;
      scopeSel.appendChild(o);
    });
    m.body.appendChild(scopeSel);

    /* Level display (read-only if levelId provided) */
    if (levelName) {
      var levelEl = document.createElement('div');
      levelEl.style.cssText = 'font-family:"DM Sans",sans-serif;font-size:12px;color:#7a9aaa;padding:0 2px;margin-bottom:4px;';
      levelEl.textContent = 'Level: ' + levelName;
      m.body.appendChild(levelEl);
    }

    var cancel = modalBtn('Cancel', false);
    var save   = modalBtn('Add room', true);
    var saving = false;

    cancel.addEventListener('click', function() { m.close(); });

    save.addEventListener('click', function() {
      var num  = numInp.value.trim();
      var name = nameInp.value.trim();
      if (!num && !name || saving) {
        if (!num && !name) nameInp.style.borderColor = '#c0392b';
        return;
      }
      saving = true;
      save.disabled = true;
      save.textContent = 'Adding...';

      var fields = {};
      if (num)  fields['Room Number'] = num;
      if (name) fields['Custom Name'] = name;
      if (scopeSel.value) fields['Scope Type'] = scopeSel.value;
      if (conceptId) fields['Project Concept'] = [conceptId];
      if (levelId) {
        fields['Concept Level'] = [levelId];
        /* Also set Level singleSelect — use level name if it matches a valid option */
        var LEVEL_OPTIONS = ['Fifth','Fourth','Third','Second','First','Site'];
        for (var li = 0; li < LEVEL_OPTIONS.length; li++) {
          if (LEVEL_OPTIONS[li].toLowerCase() === levelSsName.toLowerCase()) {
            fields['Level'] = LEVEL_OPTIONS[li]; break;
          }
        }
      }

      apiPost(TBL_ROOMS, fields, function(ok, result) {
        if (!ok) {
          saving = false;
          save.disabled = false;
          save.textContent = 'Add room';
          showPill('Save failed', true);
          return;
        }
        /* Bust caches so new room appears */
        if (levelId) delete roomCache[levelId];
        if (conceptId) delete unassignedRoomsCache[conceptId];
        m.close();
        showPill('Room added', false);
        /* Reload */
        if (levelId) loadRooms(levelId);
        if (conceptId && !levelId) loadUnassignedRooms(conceptId);
      });
    });

    m.foot.appendChild(cancel);
    m.foot.appendChild(save);
    setTimeout(function() { numInp.focus(); }, 60);
  }

  /* ── Delete room ──────────────────────────────────────────────────────── */
  function openDeleteRoomModal(roomId, levelId) {
    var room = null;
    var conceptId = '';
    Object.keys(roomCache).forEach(function(lid) {
      (roomCache[lid]||[]).forEach(function(r) { if (r.id === roomId) room = r; });
    });
    Object.keys(unassignedRoomsCache).forEach(function(cid) {
      (unassignedRoomsCache[cid]||[]).forEach(function(r) { if (r.id === roomId) { room = r; conceptId = cid; } });
    });
    if (!room) return;
    var rdisplay = room.fields['Room'] || room.fields['Room Number and Name'] || 'Room';

    var m = createModal('wf-delete-room-modal', 'Delete room', 340);

    var desc = document.createElement('div');
    desc.style.cssText = 'font-family:"DM Sans",sans-serif;font-size:13px;color:#3a5260;line-height:1.55;margin-bottom:12px;';
    desc.textContent = 'This will permanently delete this room.';
    m.body.appendChild(desc);

    var nameEl = document.createElement('div');
    nameEl.style.cssText = 'font-family:"DM Sans",sans-serif;font-size:13px;color:#07272D;'
      + 'padding:8px 10px;background:rgba(7,39,45,0.04);border-radius:4px;font-weight:500;margin-bottom:16px;';
    nameEl.textContent = rdisplay;
    m.body.appendChild(nameEl);

    var cancel  = modalBtn('Cancel', false);
    var confirm = modalBtn('Delete', true);
    confirm.style.background  = '#c0392b';
    confirm.style.borderColor = '#c0392b';
    var saving = false;

    cancel.addEventListener('click', function() { m.close(); });

    confirm.addEventListener('click', function() {
      if (saving) return;
      saving = true;
      confirm.disabled = true;
      confirm.textContent = 'Deleting...';

      apiDelete(TBL_ROOMS, roomId, function(ok) {
        if (!ok) {
          saving = false;
          confirm.disabled = false;
          confirm.textContent = 'Delete';
          showPill('Delete failed', true);
          return;
        }
        /* Bust caches */
        if (levelId && roomCache[levelId]) {
          roomCache[levelId] = roomCache[levelId].filter(function(r) { return r.id !== roomId; });
        }
        if (conceptId && unassignedRoomsCache[conceptId]) {
          unassignedRoomsCache[conceptId] = unassignedRoomsCache[conceptId].filter(function(r) { return r.id !== roomId; });
        }
        m.close();
        showPill('Room deleted', false);
        render();
      });
    });

    m.foot.appendChild(cancel);
    m.foot.appendChild(confirm);
  }

  function openMoveRoomToLevelModal(roomId, currentLevelId) {
    /* Find room in any cache */
    var room = null;
    var conceptId = '';
    Object.keys(roomCache).forEach(function(lid) {
      (roomCache[lid]||[]).forEach(function(r) { if (r.id === roomId) room = r; });
    });
    Object.keys(unassignedRoomsCache).forEach(function(cid) {
      (unassignedRoomsCache[cid]||[]).forEach(function(r) { if (r.id === roomId) { room = r; conceptId = cid; } });
    });
    /* Resolve conceptId from level if not found above */
    if (!conceptId) {
      Object.keys(unitCache).forEach(function(cid) {
        (unitCache[cid]||[]).forEach(function(u) {
          (levelCache[u.id]||[]).forEach(function(l) {
            if (l.id === currentLevelId) conceptId = cid;
          });
        });
      });
    }
    if (!room) return;
    var rname = room.fields['Room'] || room.fields['Room Number and Name'] || 'Room';

    /* Build unit list from unitCache for this concept */
    var units = conceptId ? (unitCache[conceptId] || []) : [];
    if (!units.length) {
      /* Flatten all units if concept not resolved */
      Object.keys(unitCache).forEach(function(cid) {
        (unitCache[cid]||[]).forEach(function(u) { units.push(u); });
      });
    }
    if (!units.length) { showPill('No units available', true); return; }

    var m = createModal('wf-move-room-level-modal', 'Assign room to level', 380);

    /* Room name callout */
    var nameEl = document.createElement('div');
    nameEl.style.cssText = 'font-family:"DM Sans",sans-serif;font-size:13px;color:#07272D;'
      + 'padding:8px 10px;background:rgba(7,39,45,0.04);border-radius:4px;font-weight:500;margin-bottom:16px;';
    nameEl.textContent = rname;
    m.body.appendChild(nameEl);

    /* ── Unit selector ── */
    m.body.appendChild(modalLabel('Unit'));
    var unitSel = document.createElement('select');
    unitSel.style.cssText = 'display:block;width:100%;box-sizing:border-box;padding:8px 10px;'
      + 'border:0.5px solid rgba(7,39,45,0.20);border-radius:4px;font-family:"DM Sans",sans-serif;'
      + 'font-size:13px;color:#07272D;outline:none;background:white;margin-bottom:16px;';
    var blankUnit = document.createElement('option');
    blankUnit.value = ''; blankUnit.textContent = 'Select unit...';
    unitSel.appendChild(blankUnit);
    units.forEach(function(u) {
      var o = document.createElement('option');
      o.value = u.id;
      o.textContent = u.fields['Unit Type'] || u.fields['Name'] || u.id;
      unitSel.appendChild(o);
    });
    m.body.appendChild(unitSel);

    /* ── Level selector (populated when unit chosen) ── */
    m.body.appendChild(modalLabel('Level'));
    var levelSel = document.createElement('select');
    levelSel.style.cssText = 'display:block;width:100%;box-sizing:border-box;padding:8px 10px;'
      + 'border:0.5px solid rgba(7,39,45,0.20);border-radius:4px;font-family:"DM Sans",sans-serif;'
      + 'font-size:13px;color:#07272D;outline:none;background:white;margin-bottom:4px;';
    levelSel.disabled = true;
    var blankLevel = document.createElement('option');
    blankLevel.value = ''; blankLevel.textContent = 'Select unit first...';
    levelSel.appendChild(blankLevel);
    m.body.appendChild(levelSel);

    var save   = modalBtn('Move', true);
    var cancel = modalBtn('Cancel', false);
    save.disabled = true;
    var saving = false;

    function populateLevels(unitId) {
      levelSel.innerHTML = '';
      var blank2 = document.createElement('option');
      blank2.value = ''; blank2.textContent = 'Select level...';
      levelSel.appendChild(blank2);
      var levels = levelCache[unitId];
      if (!levels || !levels.length) {
        /* Fetch if not loaded */
        if (!levels) {
          levelSel.disabled = true;
          blank2.textContent = 'Loading...';
          loadLevels(unitId, function() {
            populateLevels(unitId);
          });
          return;
        }
        blank2.textContent = 'No levels';
        levelSel.disabled = true;
        return;
      }
      levels.forEach(function(lev) {
        if (lev.id === currentLevelId) return; /* skip current */
        var o = document.createElement('option');
        o.value = lev.id;
        o.textContent = lev.fields['Name'] || lev.id;
        levelSel.appendChild(o);
      });
      levelSel.disabled = false;
      levelSel.onchange = function() {
        save.disabled = !levelSel.value;
      };
    }

    unitSel.addEventListener('change', function() {
      save.disabled = true;
      if (unitSel.value) {
        populateLevels(unitSel.value);
      } else {
        levelSel.innerHTML = '';
        var b = document.createElement('option');
        b.value = ''; b.textContent = 'Select unit first...';
        levelSel.appendChild(b);
        levelSel.disabled = true;
      }
    });

    cancel.addEventListener('click', function() { m.close(); });

    save.addEventListener('click', function() {
      var selUnitId  = unitSel.value;
      var selLevelId = levelSel.value;
      if (!selLevelId || saving) return;
      saving = true;
      save.disabled = true;
      save.textContent = 'Moving...';

      var selUnit  = null;
      units.forEach(function(u) { if (u.id === selUnitId) selUnit = u; });
      var selLevel = null;
      (levelCache[selUnitId]||[]).forEach(function(l) { if (l.id === selLevelId) selLevel = l; });
      var selLevelName = selLevel ? (selLevel.fields['Name'] || '') : '';
      var LEVEL_OPTIONS = ['Fifth','Fourth','Third','Second','First','Site'];
      var levelSsVal = '';
      for (var li = 0; li < LEVEL_OPTIONS.length; li++) {
        if (LEVEL_OPTIONS[li].toLowerCase() === selLevelName.toLowerCase()) { levelSsVal = LEVEL_OPTIONS[li]; break; }
      }

      var patch = { 'Concept Level': [selLevelId] };
      if (levelSsVal) patch['Level'] = levelSsVal;

      apiPatch(TBL_ROOMS, roomId, patch, function(ok) {
        if (!ok) {
          saving = false;
          save.disabled = false;
          save.textContent = 'Move';
          showPill('Move failed', true);
          return;
        }
        /* Remove from old level cache or unassigned cache */
        if (currentLevelId && roomCache[currentLevelId]) {
          roomCache[currentLevelId] = roomCache[currentLevelId].filter(function(r) { return r.id !== roomId; });
        }
        if (conceptId && unassignedRoomsCache[conceptId]) {
          unassignedRoomsCache[conceptId] = unassignedRoomsCache[conceptId].filter(function(r) { return r.id !== roomId; });
        }
        /* Add to new level cache */
        if (room) {
          room.fields['Concept Level'] = [selLevelId];
          if (levelSsVal) room.fields['Level'] = levelSsVal;
          if (!roomCache[selLevelId]) roomCache[selLevelId] = [];
          roomCache[selLevelId].push(room);
          roomCache[selLevelId].sort(function(a, b) {
            return (a.fields['Room Number']||'') < (b.fields['Room Number']||'') ? -1 : 1;
          });
        }
        m.close();
        showPill('Room assigned to ' + selLevelName, false);
        render();
      });
    });

    m.foot.appendChild(cancel);
    m.foot.appendChild(save);
  }

  /* ── Edit unit type ───────────────────────────────────────────────────── */
  function openEditUnitTypeModal(unitId) {
    var unit = null;
    Object.keys(unitCache).forEach(function(cid) {
      (unitCache[cid] || []).forEach(function(u) { if (u.id === unitId) unit = u; });
    });
    if (!unit) return;
    var current = unit.fields['Unit Type'] || '';
    var m = createModal('wf-edit-unit-type-modal', 'Edit unit type', 340);

    m.body.appendChild(modalLabel('Unit type'));
    var sel = document.createElement('select');
    sel.style.cssText = 'display:block;width:100%;box-sizing:border-box;padding:8px 10px;'
      + 'border:0.5px solid rgba(7,39,45,0.20);border-radius:4px;font-family:"DM Sans",sans-serif;'
      + 'font-size:13px;color:#07272D;outline:none;background:white;margin-bottom:4px;';
    var blankOpt = document.createElement('option');
    blankOpt.value = '';
    blankOpt.textContent = 'Select type...';
    sel.appendChild(blankOpt);
    UNIT_TYPE_OPTIONS.forEach(function(opt) {
      var o = document.createElement('option');
      o.value = opt;
      o.textContent = opt;
      if (opt === current) o.selected = true;
      sel.appendChild(o);
    });
    m.body.appendChild(sel);

    var cancel = modalBtn('Cancel', false);
    var save   = modalBtn('Save', true);
    var saving = false;

    cancel.addEventListener('click', function() { m.close(); });

    save.addEventListener('click', function() {
      var val = sel.value;
      if (!val || saving) return;
      saving = true;
      save.disabled = true;
      save.textContent = 'Saving...';

      apiPatch(TBL_CONCEPT_UNIT, unitId, { 'Unit Type': val }, function(ok) {
        if (!ok) {
          saving = false;
          save.disabled = false;
          save.textContent = 'Save';
          showPill('Save failed', true);
          return;
        }
        if (unit) unit.fields['Unit Type'] = val;
        m.close();
        showPill('Unit type updated', false);
        render();
      });
    });

    m.foot.appendChild(cancel);
    m.foot.appendChild(save);
  }

  /* Dead code — handled by MoveToPhaseModal React component */
  function openMoveToPhaseModal(msId, currentPhId) {
    var ms = getMsById(msId);
    if (!ms) return;
    var msName = ms.fields['Full Deliverable Name'] || ms.fields['Name'] || 'Milestone';
    var otherPhases = state.phases.filter(function(ph) { return ph.id !== currentPhId; });
    if (!otherPhases.length) { return; }
    var m = createModal('wf-move-phase-modal', 'Move milestone', 360);

    /* Milestone name display */
    var nameEl = document.createElement('div');
    nameEl.style.cssText = 'font-family:"DM Sans",sans-serif;font-size:13px;color:#07272D;padding:8px 10px;background:rgba(7,39,45,0.04);border-radius:4px;margin-bottom:16px;font-weight:500;';
    nameEl.textContent = msName;
    m.body.appendChild(nameEl);

    m.body.appendChild(modalLabel('Move to phase'));

    var selectedPhId = '';
    var radios = [];

    otherPhases.forEach(function(ph) {
      var phName = ph.fields['Phase Name'] || ph.id;
      var row = document.createElement('label');
      row.style.cssText = 'display:flex;align-items:center;gap:8px;padding:7px 8px;cursor:pointer;border-radius:4px;font-family:"DM Sans",sans-serif;font-size:13px;color:#07272D;transition:background 0.08s;border:0.5px solid transparent;margin-bottom:4px;';
      var radio = document.createElement('input');
      radio.type = 'radio';
      radio.name = 'wf-move-phase-radio';
      radio.style.cssText = 'flex-shrink:0;accent-color:#07272D;cursor:pointer;';
      radio.setAttribute('data-phid', ph.id);
      radio.addEventListener('change', function() {
        selectedPhId = ph.id;
        /* Highlight selected row */
        radios.forEach(function(r) { r.parentNode.style.background = ''; r.parentNode.style.borderColor = 'transparent'; });
        row.style.background = 'rgba(7,39,45,0.04)';
        row.style.borderColor = 'rgba(7,39,45,0.12)';
        save.disabled = false;
      });
      radios.push(radio);
      var span = document.createElement('span');
      span.textContent = phName;
      row.appendChild(radio);
      row.appendChild(span);
      m.body.appendChild(row);
    });

    var cancel = modalBtn('Cancel', false);
    var save   = modalBtn('Move', true);
    save.disabled = true;
    var saving = false;

    cancel.addEventListener('click', function() { m.close(); });

    save.addEventListener('click', function() {
      if (saving || !selectedPhId) return;
      saving = true;
      save.disabled = true;
      save.textContent = 'Moving...';

      var newPh = getPhaseById(selectedPhId);
      var newPhName = newPh ? (newPh.fields['Phase Name'] || '') : '';

      apiPatch(TBL_DEL, msId, { 'Phase': [selectedPhId], 'Project Phase': newPhName }, function(ok) {
        if (!ok) {
          saving = false;
          save.disabled = false;
          save.textContent = 'Move';
          showPill('Move failed', true);
          return;
        }
        /* Update in-memory so render re-buckets correctly */
        if (ms) ms.fields['Project Phase'] = newPhName;
        m.close();
        showPill('Moved to ' + newPhName, false);
        render();
      });
    });

    m.foot.appendChild(cancel);
    m.foot.appendChild(save);
  }

  /* ── Post milestone ──────────────────────────────────────────────────────
   * Three variants based on deliverable type:
   *   STANDARD  — Posted Date + LFT sync
   *   PERMIT    — + Agency Approvals (single)
   *   REVISION  — + Delta Revisions - Plan Check (single) + Delta Revisions - Permit (single)
   * Extra fields are required and pre-populated from existing linked values.
   * ──────────────────────────────────────────────────────────────────────── */
  function openPostMilestoneModal(msId) {
    var ms = getMsById(msId);
    if (!ms) return;
    var msName    = ms.fields['Full Deliverable Name'] || ms.fields['Name'] || 'Milestone';
    var delTypeId = (ms.fields['Deliverable'] || [])[0] || '';
    var isPermit  = POST_PERMIT_TYPE_IDS.indexOf(delTypeId) !== -1;
    var isRevision= POST_REVISION_TYPE_IDS.indexOf(delTypeId) !== -1;
    var today     = todayStr();
    var mo        = ['January','February','March','April','May','June','July',
                     'August','September','October','November','December'];
    var dp        = today.split('-');
    var todayFmt  = mo[parseInt(dp[1])-1] + ' ' + parseInt(dp[2]) + ', ' + dp[0];

    /* Read existing linked values from ms fields (arrays of record IDs) */
    var existingAgency    = (ms.fields['Agency Approvals']           || [])[0] || '';
    var existingPlanCheck = (ms.fields['Delta Revisions - Plan Check']|| [])[0] || '';
    var existingPermitRev = (ms.fields['Delta Revisions - Permit']    || [])[0] || '';

    var m = createModal('wf-post-ms-modal', 'Post milestone', 400);

    /* ── Milestone name callout ── */
    var nameEl = document.createElement('div');
    nameEl.style.cssText = 'font-family:"DM Sans",sans-serif;font-size:13px;color:#07272D;'
      + 'padding:8px 10px;background:rgba(7,39,45,0.04);border-radius:4px;font-weight:500;margin-bottom:4px;';
    nameEl.textContent = msName;
    m.body.appendChild(nameEl);

    /* ── Posted date input (defaults to today, editable for backdating) ── */
    var dateRow = document.createElement('div');
    dateRow.style.cssText = 'margin-bottom:20px;';
    m.body.appendChild(modalLabel('Posted date'));
    var dateInp = document.createElement('input');
    dateInp.type = 'date';
    dateInp.value = today;
    dateInp.style.cssText = 'display:block;width:100%;box-sizing:border-box;padding:7px 10px;'
      + 'border:0.5px solid rgba(7,39,45,0.20);border-radius:4px;font-family:"DM Mono",monospace;'
      + 'font-size:13px;color:#07272D;outline:none;background:white;';
    dateRow.appendChild(dateInp);
    m.body.appendChild(dateRow);

    /* ── Helper: build a linked-record single-select picker ──────────────
     * Shows a dropdown of records from `records` array [{id,name}].
     * Pre-selects `preselectedId` if provided.
     * Returns { wrap, getSelectedId, markInvalid }
     * ─────────────────────────────────────────────────────────────────── */
    function buildLinkedPicker(records, preselectedId) {
      var selectedId   = preselectedId || '';
      var selectedName = '';
      for (var i=0; i<records.length; i++) {
        if (records[i].id === preselectedId) { selectedName = records[i].name; break; }
      }

      var wrap = document.createElement('div');
      wrap.style.cssText = 'position:relative;margin-bottom:16px;';

      var display = document.createElement('div');
      display.style.cssText = 'display:flex;align-items:center;justify-content:space-between;'
        + 'padding:8px 10px;border:0.5px solid rgba(7,39,45,0.20);border-radius:4px;cursor:pointer;'
        + 'font-family:"DM Sans",sans-serif;font-size:13px;background:white;user-select:none;';
      var dispLabel = document.createElement('span');
      dispLabel.style.cssText = 'flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;';
      dispLabel.style.color = selectedName ? '#07272D' : '#7a9aaa';
      dispLabel.textContent = selectedName || 'Select...';
      var dispArrow = document.createElement('span');
      dispArrow.style.cssText = 'font-size:10px;color:#c0d0d4;flex-shrink:0;margin-left:6px;';
      dispArrow.textContent = '\u25be';
      display.appendChild(dispLabel);
      display.appendChild(dispArrow);

      var dd = document.createElement('div');
      dd.style.cssText = 'border:0.5px solid rgba(7,39,45,0.20);border-top:none;border-radius:0 0 4px 4px;'
        + 'background:white;max-height:160px;overflow-y:auto;display:none;';

      var srch = document.createElement('input');
      srch.type = 'text';
      srch.placeholder = 'Search...';
      srch.style.cssText = 'display:block;width:100%;box-sizing:border-box;padding:7px 10px;border:none;'
        + 'border-bottom:0.5px solid rgba(7,39,45,0.08);font-family:"DM Sans",sans-serif;font-size:13px;'
        + 'color:#07272D;outline:none;';
      var optWrap = document.createElement('div');
      dd.appendChild(srch);
      dd.appendChild(optWrap);
      wrap.appendChild(display);
      wrap.appendChild(dd);

      function renderOpts(q) {
        optWrap.innerHTML = '';
        var list = q ? records.filter(function(r) {
          return r.name.toLowerCase().indexOf(q.toLowerCase()) !== -1;
        }) : records;
        if (!list.length) {
          optWrap.innerHTML = '<div style="padding:8px 10px;font-family:\'DM Sans\',sans-serif;'
            + 'font-size:13px;color:#7a9aaa;font-style:italic;">'
            + (records.length ? 'No matches' : 'None found') + '</div>';
          return;
        }
        list.forEach(function(rec) {
          var opt = document.createElement('div');
          opt.style.cssText = 'padding:7px 10px;font-family:"DM Sans",sans-serif;font-size:13px;'
            + 'color:#07272D;cursor:pointer;transition:background 0.08s;';
          if (rec.id === selectedId) opt.style.background = 'rgba(7,39,45,0.05)';
          opt.textContent = rec.name;
          opt.addEventListener('mouseover', function() { opt.style.background = 'rgba(7,39,45,0.05)'; });
          opt.addEventListener('mouseout',  function() {
            opt.style.background = rec.id === selectedId ? 'rgba(7,39,45,0.05)' : '';
          });
          opt.addEventListener('click', function(e) {
            e.stopPropagation();
            selectedId   = rec.id;
            selectedName = rec.name;
            dispLabel.textContent = rec.name;
            dispLabel.style.color = '#07272D';
            display.style.borderColor = 'rgba(7,39,45,0.20)';
            display.style.borderRadius = '4px';
            dd.style.display = 'none';
            renderOpts(srch.value); /* refresh highlight */
          });
          optWrap.appendChild(opt);
        });
      }

      display.addEventListener('click', function(e) {
        e.stopPropagation();
        var open = dd.style.display === 'block';
        dd.style.display = open ? 'none' : 'block';
        display.style.borderRadius = open ? '4px' : '4px 4px 0 0';
        if (!open) { srch.value = ''; renderOpts(''); srch.focus(); }
      });
      srch.addEventListener('click', function(e) { e.stopPropagation(); });
      srch.addEventListener('input', function() { renderOpts(srch.value); });

      /* Close on outside click */
      var outsideHandler = function(e) {
        if (!wrap.contains(e.target)) {
          dd.style.display = 'none';
          display.style.borderRadius = '4px';
        }
      };
      document.addEventListener('click', outsideHandler);
      wrap._removeOutside = function() { document.removeEventListener('click', outsideHandler); };

      renderOpts('');

      return {
        wrap: wrap,
        getSelectedId: function() { return selectedId; },
        markInvalid: function() {
          display.style.borderColor = '#c0392b';
          setTimeout(function() { display.style.borderColor = 'rgba(7,39,45,0.20)'; }, 2000);
        }
      };
    }

    /* ── LFT sync toggle ── */
    var lftRow = document.createElement('label');
    lftRow.style.cssText = 'display:flex;align-items:flex-start;gap:8px;cursor:pointer;'
      + 'padding:10px;background:rgba(7,39,45,0.03);border-radius:4px;'
      + 'border:0.5px solid rgba(7,39,45,0.10);margin-bottom:4px;';
    var lftCb = document.createElement('input');
    lftCb.type = 'checkbox';
    lftCb.style.cssText = 'margin-top:2px;flex-shrink:0;accent-color:#07272D;cursor:pointer;';
    var lftText = document.createElement('div');
    lftText.style.cssText = 'font-family:"DM Sans",sans-serif;font-size:13px;color:#07272D;line-height:1.45;';
    lftText.innerHTML = 'Update Latest Finish to match posted date'
      + '<div style="font-size:11px;color:#7a9aaa;margin-top:2px;">Sets LFT to the selected posted date</div>';
    lftRow.appendChild(lftCb);
    lftRow.appendChild(lftText);

    /* ── Variant-specific pickers ── */
    var agencyPicker = null, planCheckPicker = null, permitRevPicker = null;
    var pickersReady = (!isPermit && !isRevision); /* standard variant needs no fetch */

    var loadingEl = document.createElement('div');
    loadingEl.style.cssText = 'font-family:"DM Sans",sans-serif;font-size:13px;color:#7a9aaa;'
      + 'padding:8px 2px;margin-bottom:8px;';

    function finishBuildingModal() {
      /* Remove loading indicator if present */
      if (loadingEl.parentNode) m.body.removeChild(loadingEl);

      if (isPermit && agencyPicker) {
        m.body.appendChild(modalLabel('Agency approval'));
        m.body.appendChild(agencyPicker.wrap);
      }
      if (isRevision) {
        if (planCheckPicker) {
          m.body.appendChild(modalLabel('Plan check revision'));
          m.body.appendChild(planCheckPicker.wrap);
        }
        if (permitRevPicker) {
          m.body.appendChild(modalLabel('Permit revision'));
          m.body.appendChild(permitRevPicker.wrap);
        }
      }

      m.body.appendChild(lftRow);
      m.foot.appendChild(cancel);
      m.foot.appendChild(confirm);
      pickersReady = true;
    }

    var cancel  = modalBtn('Cancel', false);
    var confirm = modalBtn('Post milestone', true);
    var saving  = false;

    cancel.addEventListener('click', function() {
      /* Clean up all outside-click handlers */
      if (agencyPicker    && agencyPicker.wrap._removeOutside)    agencyPicker.wrap._removeOutside();
      if (planCheckPicker && planCheckPicker.wrap._removeOutside)  planCheckPicker.wrap._removeOutside();
      if (permitRevPicker && permitRevPicker.wrap._removeOutside)  permitRevPicker.wrap._removeOutside();
      m.close();
    });

    confirm.addEventListener('click', function() {
      if (saving || !pickersReady) return;

      /* Validate required linked pickers */
      var valid = true;
      if (isPermit && agencyPicker && !agencyPicker.getSelectedId()) {
        agencyPicker.markInvalid(); valid = false;
      }
      if (isRevision) {
        if (planCheckPicker && !planCheckPicker.getSelectedId()) {
          planCheckPicker.markInvalid(); valid = false;
        }
        if (permitRevPicker && !permitRevPicker.getSelectedId()) {
          permitRevPicker.markInvalid(); valid = false;
        }
      }
      if (!valid) return;

      saving = true;
      confirm.disabled = true;
      confirm.textContent = 'Posting...';

      var postedDate = dateInp.value || today;
      var patch = {
        'Posted Date': postedDate,
        'Sync Latest Finish to Post Date': lftCb.checked
      };
      if (isPermit  && agencyPicker)     patch['Agency Approvals']            = [agencyPicker.getSelectedId()];
      if (isRevision && planCheckPicker) patch['Delta Revisions - Plan Check'] = [planCheckPicker.getSelectedId()];
      if (isRevision && permitRevPicker) patch['Delta Revisions - Permit']     = [permitRevPicker.getSelectedId()];

      apiPatch(TBL_DEL, msId, patch, function(ok) {
        if (!ok) {
          saving = false;
          confirm.disabled = false;
          confirm.textContent = 'Post milestone';
          showPill('Post failed', true);
          return;
        }
        refreshMilestoneMirror([msId]);   // cutover slice one — the board reads posted_date off Supabase
        /* Update in memory */
        ms.fields['Posted Date'] = postedDate;
        if (isPermit  && agencyPicker)    ms.fields['Agency Approvals']            = [agencyPicker.getSelectedId()];
        if (isRevision && planCheckPicker) ms.fields['Delta Revisions - Plan Check'] = [planCheckPicker.getSelectedId()];
        if (isRevision && permitRevPicker) ms.fields['Delta Revisions - Permit']     = [permitRevPicker.getSelectedId()];
        /* Clean up listeners */
        if (agencyPicker    && agencyPicker.wrap._removeOutside)    agencyPicker.wrap._removeOutside();
        if (planCheckPicker && planCheckPicker.wrap._removeOutside)  planCheckPicker.wrap._removeOutside();
        if (permitRevPicker && permitRevPicker.wrap._removeOutside)  permitRevPicker.wrap._removeOutside();
        m.close();
        showPill('Milestone posted', false);
        render();
      });
    });

    /* ── Fetch variant data then build modal body ── */
    if (isPermit) {
      m.body.appendChild(loadingEl);
      loadingEl.textContent = 'Loading agency approvals...';
      /* Filter to project via Permits → Project */
      var agencyQs = 'filterByFormula='
        + encodeURIComponent('FIND("' + (wf.zoomProjectId || ctx.recordId) + '",ARRAYJOIN({Project Record ID (from Permits)},","))')
        + '&maxRecords=50';
      apiGet(TBL_AGENCY_APPROVALS, agencyQs, function(d) {
        var recs = (d ? d.records : []).map(function(r) {
          return { id: r.id, name: r.fields['Name'] || r.id };
        });
        agencyPicker = buildLinkedPicker(recs, existingAgency);
        finishBuildingModal();
      });

    } else if (isRevision) {
      m.body.appendChild(loadingEl);
      loadingEl.textContent = 'Loading revisions...';
      /* Filter Delta Revisions to project via Permit → Project Record ID */
      var deltaQs = 'filterByFormula='
        + encodeURIComponent('FIND("' + (wf.zoomProjectId || ctx.recordId) + '",ARRAYJOIN({Project Record ID (from Permit)},","))')
        + '&maxRecords=100';
      apiGet(TBL_DELTA_REVISIONS, deltaQs, function(d) {
        var all = d ? d.records : [];
        var pcRecs = all.filter(function(r) { return r.fields['Type'] === 'Plan Check Revision'; })
          .map(function(r) { return { id: r.id, name: r.fields['Name'] || r.id }; });
        var prRecs = all.filter(function(r) { return r.fields['Type'] === 'Permit Revision'; })
          .map(function(r) { return { id: r.id, name: r.fields['Name'] || r.id }; });
        planCheckPicker = buildLinkedPicker(pcRecs, existingPlanCheck);
        permitRevPicker = buildLinkedPicker(prRecs, existingPermitRev);
        finishBuildingModal();
      });

    } else {
      /* Standard — no fetch needed */
      finishBuildingModal();
    }
  }

  /* ── Unpost milestone ─────────────────────────────────────────────────── */
  function openUnpostMilestoneModal(msId) {
    var ms = getMsById(msId);
    if (!ms) return;
    var msName = ms.fields['Full Deliverable Name'] || ms.fields['Name'] || 'Milestone';
    var postedDate = ms.fields['Posted Date'] || '';
    var postedDisplay = postedDate ? (function() {
      var dp = postedDate.split('-');
      var mo = ['January','February','March','April','May','June','July','August','September','October','November','December'];
      return mo[parseInt(dp[1])-1] + ' ' + parseInt(dp[2]) + ', ' + dp[0];
    }()) : '';

    var m = createModal('wf-unpost-ms-modal', 'Unpost milestone', 360);

    var desc = document.createElement('div');
    desc.style.cssText = 'font-family:"DM Sans",sans-serif;font-size:13px;color:#3a5260;line-height:1.55;margin-bottom:16px;';
    desc.textContent = 'This will clear the posted date and return the milestone to an open state.';
    m.body.appendChild(desc);

    var nameEl = document.createElement('div');
    nameEl.style.cssText = 'font-family:"DM Sans",sans-serif;font-size:13px;color:#07272D;padding:8px 10px;background:rgba(7,39,45,0.04);border-radius:4px;font-weight:500;margin-bottom:6px;';
    nameEl.textContent = msName;
    m.body.appendChild(nameEl);

    if (postedDisplay) {
      var dateEl = document.createElement('div');
      dateEl.style.cssText = 'font-family:"DM Mono",monospace;font-size:12px;color:#7a9aaa;padding:0 2px;margin-bottom:4px;';
      dateEl.textContent = 'Posted: ' + postedDisplay;
      m.body.appendChild(dateEl);
    }

    // A posted milestone may have an associated client-journey entry. Unposting un-delivers the
    // milestone, so its journey entry must be removed. Warn if one exists, and delete on confirm.
    var journeyProjId = (ms.fields['Project'] && ms.fields['Project'][0]) || wf.zoomProjectId || ctx.recordId || '';
    if (journeyProjId) {
      getJourneyEntriesForMilestone(journeyProjId, msId).then(function(entries) {
        if (!entries || !entries.length) return;
        var warn = document.createElement('div');
        warn.style.cssText = 'font-family:"DM Sans",sans-serif;font-size:12.5px;color:#b8540a;background:#fef5eb;border-radius:6px;padding:9px 11px;margin-top:10px;line-height:1.5;';
        warn.textContent = 'This milestone is on the Client Hub. Unposting will remove its Client Hub '
          + (entries.length > 1 ? (entries.length + ' entries') : 'entry') + ' as well.';
        m.body.appendChild(warn);
      }).catch(function() {});
    }

    var cancel  = modalBtn('Cancel', false);
    var confirm = modalBtn('Unpost', true);
    confirm.style.background = '#c0392b';
    confirm.style.borderColor = '#c0392b';
    var saving = false;

    cancel.addEventListener('click', function() { m.close(); });

    confirm.addEventListener('click', function() {
      if (saving) return;
      saving = true;
      confirm.disabled = true;
      confirm.textContent = 'Unposting...';

      apiPatch(TBL_DEL, msId, { 'Posted Date': null }, function(ok) {
        if (!ok) {
          saving = false;
          confirm.disabled = false;
          confirm.textContent = 'Unpost';
          showPill('Unpost failed', true);
          return;
        }
        refreshMilestoneMirror([msId]);   // cutover slice one — the board reads posted_date off Supabase
        if (ms) ms.fields['Posted Date'] = '';
        // Titleblock issuance: clear the posted flag so a re-post re-stamps with the (possibly new)
        // date. The released PDFs revert to their raw/QC source until re-posted. Best-effort.
        resetMilestoneIssuance(msId).catch(function() {});
        // Reference/Production Drawing Sheets branch caches each sheet's revisions in
        // ctx.sheetRevCache, keyed by sheetId (lazy-load-once, no other invalidation path) — unposting
        // reverts the same issuance data posting stamps in, so it needs the identical refresh
        // PostMilestoneModal.jsx dispatches on post. Best-effort; never blocks the unpost itself.
        // msId is included so MilestoneSections.jsx's own listener can refresh the SEPARATE
        // ctx.sheetCache[msId] the milestone-context Drawing Sheets section reads — that section's
        // QC "Replace (Admin)" button gates on posted state, and was going stale the same way,
        // requiring a hard refresh to see the button re-enable after unpost (Matt, 2026-07-23).
        (function() {
          var formula = 'FIND("' + msId + '",ARRAYJOIN({Record ID (from Deliverables)},","))';
          apiGet(TBL_REV, 'filterByFormula=' + encodeURIComponent(formula) + '&maxRecords=100', function(d) {
            var records = d ? d.records : [];
            var seen = {}, sheetIds = [];
            for (var i = 0; i < records.length; i++) {
              var sid = getFirst(records[i].fields['Record ID (from Drawing Sheet)']);
              if (sid && !seen[sid]) { seen[sid] = true; sheetIds.push(sid); }
            }
            if (!sheetIds.length) return;
            for (var j = 0; j < sheetIds.length; j++) { if (ctx.sheetRevCache) ctx.sheetRevCache[sheetIds[j]] = undefined; }
            window.dispatchEvent(new CustomEvent('sheet-revisions-stale', { detail: { sheetIds: sheetIds, msId: msId } }));
          });
        })();
        // Remove the milestone's client-journey entry/entries (best-effort) — it's no longer delivered.
        if (journeyProjId) {
          deleteJourneyEntriesForMilestone(journeyProjId, msId)
            .then(function() { window.dispatchEvent(new CustomEvent('journey-updated', { detail: {} })); })
            .catch(function() {});
        }
        m.close();
        showPill('Milestone unposted', false);
        render();
      });
    });

    m.foot.appendChild(cancel);
    m.foot.appendChild(confirm);
  }

  function inlineNotRequired(itemId, msId) {
    if (refuseBlocked(msId)) { wf.menuNodeId = null; return; }
    wf.menuNodeId = null;
    var m = createModal('wf-notrequired-modal', 'Mark not required', 360);

    m.body.appendChild(modalLabel('Reason'));

    /* Pills */
    var grid = document.createElement('div');
    grid.style.cssText = 'display:flex;flex-wrap:wrap;gap:6px;margin-bottom:12px;';
    var selReason = '';
    NOT_REQUIRED_REASONS.forEach(function(reason) {
      var btn = document.createElement('button');
      btn.style.cssText = 'padding:5px 10px;border:0.5px solid rgba(7,39,45,0.20);border-radius:20px;background:none;font-family:"DM Sans",sans-serif;font-size:12px;color:#3a5260;cursor:pointer;transition:all 0.1s;';
      btn.textContent = reason;
      btn.addEventListener('click', function() {
        selReason = reason;
        customInp.value = '';
        grid.querySelectorAll('button').forEach(function(b){ b.style.background='none'; b.style.color='#3a5260'; b.style.borderColor='rgba(7,39,45,0.20)'; });
        btn.style.background='#07272D'; btn.style.color='white'; btn.style.borderColor='#07272D';
      });
      grid.appendChild(btn);
    });
    m.body.appendChild(grid);

    var customInp = document.createElement('input');
    customInp.type = 'text';
    customInp.placeholder = 'Or describe a reason...';
    customInp.style.cssText = 'width:100%;box-sizing:border-box;padding:8px 10px;border:0.5px solid rgba(7,39,45,0.20);border-radius:4px;font-family:"DM Sans",sans-serif;font-size:14px;color:#07272D;outline:none;';
    customInp.addEventListener('input', function() {
      if (customInp.value) {
        selReason = customInp.value;
        grid.querySelectorAll('button').forEach(function(b){ b.style.background='none'; b.style.color='#3a5260'; b.style.borderColor='rgba(7,39,45,0.20)'; });
      }
    });
    m.body.appendChild(customInp);

    var cancel = modalBtn('Cancel', false);
    var confirm = modalBtn('Confirm', true);
    cancel.addEventListener('click', m.close);
    confirm.addEventListener('click', function() {
      var reason = customInp.value.trim() || selReason;
      if (!reason) { customInp.style.borderColor='#c0392b'; return; }
      var items=clCache[msId]||[], record=null;
      for(var i=0;i<items.length;i++){if(items[i].id===itemId){record=items[i];break;}}
      var memberId = getCurrentMemberId();
      if (record) {
        record.fields['Milestone Not Required']=true;
        if (reason) record.fields['Milestone Not Required Reason']=reason;
        if (memberId) record.fields['Milestone Not Required Member']=[memberId];
        wf.dirty[msId]=true;
      }
      m.close();
      refreshCats(msId);
      var patchFields = {'Milestone Not Required':true,'Milestone Not Required Reason':reason};
      if (memberId) patchFields['Milestone Not Required Member']=[memberId];
      apiPatch(TBL_CL, itemId, patchFields, function(ok){
        if(!ok) { showPill('Save failed',true); return; }
        resyncMilestoneRollup(msId);
      });
    });
    m.foot.appendChild(cancel);
    m.foot.appendChild(confirm);
  }

  function bulkNotRequired(msId, cat) {
    if (refuseBlocked(msId)) return;
    var ids = wf.selected.slice();
    if (!ids.length) return;
    var m = createModal('wf-bulk-nr-modal', 'Not required — ' + ids.length + ' item' + (ids.length>1?'s':''), 360);
    m.body.appendChild(modalLabel('Reason'));

    var grid = document.createElement('div');
    grid.style.cssText = 'display:flex;flex-wrap:wrap;gap:6px;margin-bottom:12px;';
    var selReason = '';
    NOT_REQUIRED_REASONS.forEach(function(reason) {
      var btn = document.createElement('button');
      btn.style.cssText = 'padding:5px 10px;border:0.5px solid rgba(7,39,45,0.20);border-radius:20px;background:none;font-family:"DM Sans",sans-serif;font-size:12px;color:#3a5260;cursor:pointer;transition:all 0.1s;';
      btn.textContent = reason;
      btn.addEventListener('click', function() {
        selReason = reason;
        customInp.value = '';
        grid.querySelectorAll('button').forEach(function(b){ b.style.background='none'; b.style.color='#3a5260'; b.style.borderColor='rgba(7,39,45,0.20)'; });
        btn.style.background='#07272D'; btn.style.color='white'; btn.style.borderColor='#07272D';
      });
      grid.appendChild(btn);
    });
    m.body.appendChild(grid);

    var customInp = document.createElement('input');
    customInp.type = 'text';
    customInp.placeholder = 'Or describe a reason...';
    customInp.style.cssText = 'width:100%;box-sizing:border-box;padding:8px 10px;border:0.5px solid rgba(7,39,45,0.20);border-radius:4px;font-family:"DM Sans",sans-serif;font-size:14px;color:#07272D;outline:none;';
    customInp.addEventListener('input', function() {
      if (customInp.value) {
        selReason = customInp.value;
        grid.querySelectorAll('button').forEach(function(b){ b.style.background='none'; b.style.color='#3a5260'; b.style.borderColor='rgba(7,39,45,0.20)'; });
      }
    });
    m.body.appendChild(customInp);

    var cancel = modalBtn('Cancel', false);
    var confirm = modalBtn('Confirm all', true);
    cancel.addEventListener('click', m.close);
    confirm.addEventListener('click', function() {
      var reason = customInp.value.trim() || selReason;
      if (!reason) { customInp.style.borderColor='#c0392b'; return; }
      var memberId = getCurrentMemberId();
      var items = clCache[msId]||[], done = 0;
      ids.forEach(function(itemId) {
        for(var i=0;i<items.length;i++){
          if(items[i].id===itemId){
            items[i].fields['Milestone Not Required']=true;
            if (reason) items[i].fields['Milestone Not Required Reason']=reason;
            var _mbr = getCurrentMemberId();
            if (_mbr) items[i].fields['Milestone Not Required Member']=[_mbr];
            break;
          }
        }
        var pf = {'Milestone Not Required':true,'Milestone Not Required Reason':reason};
        if (memberId) pf['Milestone Not Required Member']=[memberId];
        apiPatch(TBL_CL, itemId, pf, function(){ done++; if(done===ids.length) showPill('Updated',false); });
      });
      wf.selected=[]; wf.selectMode=null; wf.dirty[msId]=true;
      m.close();
      refreshCats(msId);
    });
    m.foot.appendChild(cancel);
    m.foot.appendChild(confirm);
  }

  function updateSelectFloatBar() {
    var existing = document.getElementById('wf-select-float');
    /* Remove if no select mode */
    if (!wf.selectMode) {
      if (existing) existing.parentNode.removeChild(existing);
      return;
    }
    var parts = wf.selectMode.split(':');
    var msId = parts[0], cat = parts[1];
    /* Analyse selected items - ALL means all categories */
    var allItems = cat === 'ALL'
      ? processItems(clCache[msId] || [])
      : processItems((clCache[msId] || []).filter(function(r) { return (getFirst(r.fields['Category (from Deliverable Checklist Item Template)'])||'') === cat; }));
    var selItems = allItems.filter(function(it){ return it && it.record && wf.selected.indexOf(it.record.id) !== -1; });
    var anyDone = selItems.some(function(it){ return it.fields['Complete']===true; });
    var anyNotDone = selItems.length===0 || selItems.some(function(it){ return it.fields['Complete']!==true && it.fields['Milestone Not Required']!==true; });
    var anyNotRequired = selItems.some(function(it){ return it.fields['Milestone Not Required']===true; });
    /* Build or update the float bar */
    var bar = existing || document.createElement('div');
    bar.id = 'wf-select-float';
    bar.className = 'wf-select-float';
    bar.innerHTML = ''
      + '<span class="wf-select-float-count">' + (wf.selected.length ? wf.selected.length + ' selected' : 'Select items') + '</span>';
    function addBtn(label, fn, cls) {
      var b = document.createElement('button');
      var isDim = cls && cls.indexOf('wf-select-float-btn--dim') !== -1;
      b.className = 'wf-select-float-btn' + (cls ? ' ' + cls : '');
      b.textContent = label;
      if (!isDim) {
        b.setAttribute('data-fn', fn);
        b.setAttribute('data-msid', msId);
        b.setAttribute('data-cat', cat);
      } else {
        b.setAttribute('disabled', 'true');
      }
      bar.appendChild(b);
    }
    function addSep() {
      var s = document.createElement('span');
      s.className = 'wf-select-float-sep';
      bar.appendChild(s);
    }
    var hasSelection = wf.selected.length > 0;
    if (!hasSelection) {
      addBtn('Complete', 'bulkComplete', 'wf-select-float-btn--green wf-select-float-btn--dim');
      addBtn('Not required', 'bulkNotRequired', 'wf-select-float-btn--dim');
    } else {
      if (anyNotDone) {
        addBtn('Complete', 'bulkComplete', 'wf-select-float-btn--green');
        addBtn('Not required', 'bulkNotRequired');
      }
      if (anyDone || anyNotRequired) {
        if (anyNotDone) addSep();
        if (anyDone) addBtn('Mark incomplete', 'bulkUncomplete');
        if (anyNotRequired) addBtn('Mark required', 'bulkMarkRequired');
      }
    }
    addSep();
    addBtn('Cancel', 'cancelSelect', 'wf-select-float-btn--cancel');
    if (!existing) document.body.appendChild(bar);
  }

  function bulkComplete(msId, cat) {
    if (refuseBlocked(msId)) return;
    var ids = wf.selected.slice();
    if (!ids.length) return;
    var memberId = getCurrentMemberId();
    var items = clCache[msId]||[], done = 0;
    ids.forEach(function(itemId) {
      for(var i=0;i<items.length;i++){
        if(items[i].id===itemId){
          items[i].fields['Complete']=true;
          if (memberId) items[i].fields['Completed by']=[memberId];
          /* See ajScCompleteItem — Completed Date is a lastModifiedTime field, set it
             optimistically so the done badge shows a date immediately, not just after refresh. */
          items[i].fields['Completed Date']=new Date().toISOString();
          break;
        }
      }
      var pf = {'Complete':true};
      if (memberId) pf['Completed by']=[memberId];
      apiPatch(TBL_CL, itemId, pf, function(){ done++; if(done===ids.length) showPill('Completed',false); });
    });
    wf.selected=[]; wf.selectMode=null; wf.dirty[msId]=true;
    updateSelectFloatBar();
    refreshCats(msId);
  }

  function bulkUncomplete(msId, cat) {
    if (refuseBlocked(msId)) return;
    var ids = wf.selected.slice();
    if (!ids.length) return;
    var items = clCache[msId]||[], done = 0;
    ids.forEach(function(itemId) {
      for(var i=0;i<items.length;i++){
        if(items[i].id===itemId){
          items[i].fields['Complete']=false;
          items[i].fields['Completed by']=[];
          break;
        }
      }
      apiPatch(TBL_CL, itemId, {'Complete':false,'Completed by':[]}, function(){ done++; if(done===ids.length) showPill('Marked incomplete',false); });
    });
    wf.selected=[]; wf.selectMode=null; wf.dirty[msId]=true;
    updateSelectFloatBar();
    refreshCats(msId);
  }

  function bulkMarkRequired(msId, cat) {
    if (refuseBlocked(msId)) return;
    var ids = wf.selected.slice();
    if (!ids.length) return;
    var items = clCache[msId]||[], done = 0;
    ids.forEach(function(itemId) {
      for(var i=0;i<items.length;i++){
        if(items[i].id===itemId){
          items[i].fields['Milestone Not Required']=false;
          items[i].fields['Milestone Not Required Reason']='';
          items[i].fields['Milestone Not Required Member']=[];
          break;
        }
      }
      apiPatch(TBL_CL, itemId, {'Milestone Not Required':false,'Milestone Not Required Reason':'','Milestone Not Required Member':[]}, function(){ done++; if(done===ids.length) showPill('Marked required',false); });
    });
    wf.selected=[]; wf.selectMode=null; wf.dirty[msId]=true;
    updateSelectFloatBar();
    refreshCats(msId);
  }

  function openPlanningModal(msId) {
    wf.zoomMsId = msId;
    /* Expand all cats and enter select mode */
    var ph = getMsPhase(getMsById(msId)||{});
    if (ph) wf.zoomPhaseId = ph.id;
    wf.selectMode = null; wf.selected = [];
    if (!clCache[msId]) loadChecklist(msId);
    render();
  }

  /*    WINDOW ACTIONS    */

  /* NAV HISTORY */
  function zoomSnap() {
    return {
      zoomProjectId: wf.zoomProjectId,
      zoomPhaseId:   wf.zoomPhaseId,
      zoomMsId:      wf.zoomMsId,
      zoomCat:       wf.zoomCat,
      zoomItemId:    wf.zoomItemId,
      zoomRoot:      wf.zoomRoot,
      zoomSection:   wf.zoomSection,
      zoomConceptId: wf.zoomConceptId,
      hideDoneKeys:  wf.hideDoneKeys
    };
  }

  function applySnap(snap) {
    var prevProjectId = wf.zoomProjectId;
    wf.zoomProjectId = snap.zoomProjectId || null;
    wf.zoomPhaseId   = snap.zoomPhaseId   || null;
    wf.zoomMsId      = snap.zoomMsId      || null;
    wf.zoomCat       = snap.zoomCat       || null;
    wf.zoomItemId    = snap.zoomItemId    || null;
    wf.zoomRoot      = snap.zoomRoot      || null;
    wf.zoomSection   = snap.zoomSection   || null;
    wf.zoomConceptId = snap.zoomConceptId || null;
    wf.hideDoneKeys  = snap.hideDoneKeys  || {};
    /* If navigating to a different project, load its data if needed */
    if (wf.zoomProjectId !== prevProjectId) {
      if (wf.zoomProjectId) {
        ctx.recordId = wf.zoomProjectId;
        if (phasesCache[wf.zoomProjectId] !== undefined
            && phasesCache[wf.zoomProjectId] !== null
            && milestonesCache[wf.zoomProjectId] !== null) {
          syncProjectState(wf.zoomProjectId);
        } else {
          state.phases = []; state.milestones = [];
          loadProjectData(wf.zoomProjectId);
        }
      } else {
        state.phases = []; state.milestones = [];
        ctx.recordId = null;
      }
    }
  }

  function pushHistory() {
    /* Push current state to browser history before applying new zoom */
    var snap = zoomSnap();
    var stateObj = { outline: snap };
    /* Use replaceState for the very first entry, pushState for subsequent */
    if (!history.state || !history.state.outline) {
      history.replaceState(stateObj, '');
    } else {
      history.pushState(stateObj, '');
    }
  }

  /* ── Deep links ──────────────────────────────────────────────────────────────
     Encode the current zoom into the address bar so any view is a shareable URL
     (paste into Slack → recipient lands on the same node). The zoom target rides in a
     base64 `z` param; `recordId` is kept for the project so the existing routing still works. */
  function encodeZoom() {
    var src = { p: wf.zoomProjectId, r: wf.zoomRoot, ph: wf.zoomPhaseId, ms: wf.zoomMsId,
                sec: wf.zoomSection, cat: wf.zoomCat, it: wf.zoomItemId, cn: wf.zoomConceptId };
    var obj = {};
    Object.keys(src).forEach(function(k) { if (src[k]) obj[k] = src[k]; });
    if (!Object.keys(obj).length) return '';
    try { return btoa(JSON.stringify(obj)); } catch (e) { return ''; }
  }

  function applyEncodedZoom(z) {
    try {
      var obj = JSON.parse(atob(z));
      wf.zoomProjectId = obj.p || null;
      wf.zoomRoot      = obj.r || null;
      wf.zoomPhaseId   = obj.ph || null;
      wf.zoomMsId      = obj.ms || null;
      wf.zoomSection   = obj.sec || null;
      wf.zoomCat       = obj.cat || null;
      wf.zoomItemId    = obj.it || null;
      wf.zoomConceptId = obj.cn || null;
      if (wf.zoomProjectId) ctx.recordId = wf.zoomProjectId;
      return true;
    } catch (e) { return false; }
  }

  function buildShareUrl() {
    var z = encodeZoom();
    var params = [];
    if (wf.zoomProjectId) params.push('recordId=' + encodeURIComponent(wf.zoomProjectId));
    if (z) params.push('z=' + encodeURIComponent(z));
    return window.location.pathname + (params.length ? '?' + params.join('&') : '');
  }

  var _lastSyncedUrl = null;
  function syncUrl() {
    /* An embedded split pane must not rewrite its own address bar — that would strip ?split=1 and
       churn history inside the iframe. Its view is fixed by the ?z= it loaded with. */
    if (isEmbedded()) return;
    /* Keep the address bar in step with the current zoom (guarded so we don't spam replaceState). */
    try {
      var url = buildShareUrl();
      if (url === _lastSyncedUrl) return;
      _lastSyncedUrl = url;
      history.replaceState({ outline: zoomSnap() }, '', url);
    } catch (e) {}
  }

  /* Absolute deep link that zooms to a SPECIFIC entity (used by the Slack discussion openers so the
     link posted in the first message lands the recipient on that node). Mirrors encodeZoom's `z`
     scheme. Entities without a dedicated node zoom (coordination items) fall back to the project view
     with the correct route param. */
  ctx.entityShareUrl = function(entityType, entityId) {
    var p = wf.zoomProjectId || ctx.recordId || '';
    var obj = { p: p };
    if (entityType === 'milestone') {
      obj.r = 'schedule'; obj.ms = entityId;
      var _m = getMsById(entityId);
      var _pn = (_m && _m.fields && _m.fields['Project Phase']) || '';
      if (_pn) for (var i = 0; i < (state.phases || []).length; i++) {
        var _pf = state.phases[i].fields || {};
        if ((_pf['Phase Name'] || _pf['Name']) === _pn) { obj.ph = state.phases[i].id; break; }
      }
    } else if (entityType === 'sheet') { obj.r = 'production'; obj.sec = 'sec:prod:sheets'; }
    // Same fix as entityZoomObj above — a BIM System's Slack panel always has a real system id
    // for entityId, so deep-link there instead of dropping it and landing on the whole section.
    else if (entityType === 'bim')     { obj.r = 'design'; obj.sec = 'bim-system-detail:' + entityId; }
    else if (entityType === 'agency_review') { obj.r = 'permits'; obj.sec = 'review-detail:' + entityId; }
    else if (entityType === 'thread')  { obj.r = 'threads'; obj.sec = 'thread-detail:' + entityId; }
    /* coord + any unknown type: project view only (no dedicated node zoom) */
    var params = [];
    if (p) params.push('recordId=' + encodeURIComponent(p));
    if (obj.ms || obj.sec) {
      try { var z = btoa(JSON.stringify(obj)); if (z) params.push('z=' + encodeURIComponent(z)); } catch (e) {}
    }
    return window.location.origin + '/project' + (params.length ? '?' + params.join('&') : '');
  };

  /* CT-025 — deep-navigate to a specific entity (My Flights strip, /go). Builds the same ?z= deep link
     entityShareUrl uses (so it lands exactly on the node), parameterized by the entity's OWN project,
     and navigates there. Cross-project jumps must load the target project, so we use a full deep-link
     navigation for uniform reliability across every entity type. */
  function entityZoomObj(entityType, entityId, projectId) {
    var p = projectId || wf.zoomProjectId || ctx.recordId || '';
    var o = { p: p };
    if (entityType === 'milestone')          { o.r = 'schedule';   o.ms = entityId; }
    else if (entityType === 'thread')        { o.r = 'threads';    o.sec = 'thread-detail:' + entityId; }
    else if (entityType === 'agency_review') { o.r = 'permits';    o.sec = 'review-detail:' + entityId; }
    else if (entityType === 'sheet')         { o.r = 'production'; o.sec = 'sec:prod:sheets'; }
    // 'bim' used to drop entityId and land on the whole BIM Systems section — every RASCI row
    // for a BIM System always carries a real system id, so deep-link to that specific system,
    // matching every other entity branch's pattern above.
    else if (entityType === 'bim')           { o.r = 'design'; o.sec = 'bim-system-detail:' + entityId; }
    else if (entityType === 'bim-element')   { o.r = 'design'; o.sec = 'bim-element-detail:' + entityId; }
    // entityId is pre-combined as '<elementId>:<phase>' by the caller (getMyBimAssignments'
    // criterion hits) — matches the exact 'bim-phase-detail:<elementId>:<phase>' zoom key shape.
    else if (entityType === 'bim-phase')     { o.r = 'design'; o.sec = 'bim-phase-detail:' + entityId; }
    else if (entityType === 'checklist')     { o.r = 'schedule';   o.ms = entityId; o.sec = 'sec:' + entityId + ':checklist'; }
    return o;
  }
  // newTab: open in a fresh browser tab (window.open) instead of navigating this one away —
  // for a caller sitting on a full-bleed workspace (e.g. the Flight Tracker) where leaving is
  // the wrong default: the user wants to glance at the milestone without losing the board.
  ctx.zoomToEntity = function(entityType, entityId, projectId, newTab) {
    if (!entityType || !entityId) return;
    var o = entityZoomObj(entityType, entityId, projectId);
    var params = [];
    if (o.p) params.push('recordId=' + encodeURIComponent(o.p));
    var z = {};
    ['p', 'r', 'ph', 'ms', 'sec', 'cat', 'it', 'cn'].forEach(function(k) { if (o[k]) z[k] = o[k]; });
    if (o.r || o.ms || o.sec) { try { var b = btoa(JSON.stringify(z)); if (b) params.push('z=' + encodeURIComponent(b)); } catch (e) {} }
    var url = window.location.origin + '/project' + (params.length ? '?' + params.join('&') : '');
    if (newTab) { window.open(url, '_blank', 'noopener'); return; }
    window.location.assign(url);
  };

  /* Full absolute URL to the current view — for the Copy-link affordance. */
  ctx.getShareUrl = function() { return window.location.origin + buildShareUrl(); };
  ctx.copyShareLink = function() {
    var url = ctx.getShareUrl();
    try { return navigator.clipboard.writeText(url).then(function(){ return url; }); }
    catch (e) { return Promise.resolve(url); }
  };

  /* Browser back/forward button handler */
  window.addEventListener('popstate', function(e) {
    if (e.state && e.state.outline) {
      applySnap(e.state.outline);
      render();
    }
  });

  window.navBack = function() {
    history.back();
  };

  window.navForward = function() {
    history.forward();
  };

  /* Level-based back — always steps one logical level UP the hierarchy
     regardless of browser history. Used by ←, Ctrl+[, and the ← nav button. */
  window.navLevelUp = function() {
    pushHistory();
    var _sec = wf.zoomSection || '';

    if (wf.zoomItemId) {
      /* item zoom → category zoom */
      wf.zoomItemId = null;
    } else if (wf.zoomCat) {
      /* category zoom → Checklist section zoom */
      wf.zoomCat = null;
      wf.zoomSection = wf.zoomMsId ? sectionKey(wf.zoomMsId, 'checklist') : null;
    } else if (_sec.indexOf('room-detail:') === 0) {
      /* room-detail → up to that level's Rooms category */
      var _rdUpParts = _sec.split(':');
      wf.zoomConceptId = null;
      wf.zoomSection = 'sec:level:' + (_rdUpParts[2] || '') + ':rooms';
    } else if (_sec.indexOf('sec:level:') === 0) {
      /* Rooms category → up to the level itself (resolve its unitId via the cache) */
      var _lrUpLevelId = _sec.split(':')[2] || '';
      var _lrUpUnitId = '';
      Object.keys(unitCache).forEach(function(cid) {
        (unitCache[cid]||[]).forEach(function(u) {
          (levelCache[u.id]||[]).forEach(function(l) { if (l.id === _lrUpLevelId) _lrUpUnitId = u.id; });
        });
      });
      wf.zoomConceptId = null;
      wf.zoomSection = 'level-detail:' + _lrUpLevelId + ':' + _lrUpUnitId;
    } else if (_sec.indexOf('level-detail:') === 0) {
      /* level-detail → up to that unit's Levels category */
      var _ldUpParts = _sec.split(':');
      wf.zoomConceptId = null;
      wf.zoomSection = 'sec:unit:' + (_ldUpParts[2] || '') + ':levels';
    } else if (_sec.indexOf('sec:unit:') === 0) {
      /* Levels category → up to the unit itself (resolve its conceptId via the cache) */
      var _ulUpUnitId = _sec.split(':')[2] || '';
      var _ulUpConceptId = '';
      Object.keys(unitCache).forEach(function(cid) {
        (unitCache[cid]||[]).forEach(function(u) { if (u.id === _ulUpUnitId) _ulUpConceptId = cid; });
      });
      wf.zoomConceptId = null;
      wf.zoomSection = 'unit-detail:' + _ulUpUnitId + ':' + _ulUpConceptId;
    } else if (_sec.indexOf('unit-detail:') === 0) {
      /* unit-detail → up to that concept's Units category */
      var _udUpParts = _sec.split(':');
      wf.zoomConceptId = null;
      wf.zoomSection = 'sec:concept:' + (_udUpParts[2] || '') + ':units';
    } else if (_sec.indexOf('sec:concept:') === 0) {
      /* Units category → up to the concept itself */
      var _scUpConceptId = _sec.split(':')[2] || '';
      wf.zoomConceptId = null;
      wf.zoomSection = 'concept-detail:' + _scUpConceptId;
    } else if (_sec.indexOf('concept-detail:') === 0) {
      /* concept-detail → up to Design > Concepts (reached via Reference) or the milestone's
         Design/Concepts section (reached from inside a milestone's Design node). Does NOT set
         wf.zoomConceptId — that's the legacy flat concept zoom (buildConceptZoom), superseded by
         this zoomSection-based hierarchy; leaving it unset avoids falling into that dead view. */
      wf.zoomConceptId = null;
      if (wf.zoomMsId) {
        wf.zoomSection = designMsSectionCrumb(getMsById(wf.zoomMsId)).key;
      } else {
        wf.zoomSection = 'sec:design:concepts';
      }
    } else if (_sec.indexOf('parcel-detail:') === 0) {
      var _pcdUpParts = _sec.split(':');
      if (_pcdUpParts.length > 2) {
        /* nested parcel sub-zoom (e.g. :agencies) → back to the parcel detail itself */
        wf.zoomSection = 'parcel-detail:' + _pcdUpParts[1];
      } else {
        /* parcel detail zoom → Parcels section under Design */
        wf.zoomSection = 'sec:design:parcels';
      }
    } else if (wf.zoomMsId && _sec.indexOf('sec:' + wf.zoomMsId + ':disc:') === 0) {
      /* milestone discipline zoom → milestone sheets section */
      wf.zoomSection = sectionKey(wf.zoomMsId, 'sheets');
    } else if (_sec.indexOf('sec:prod:disc:') === 0) {
      /* production discipline zoom → production sheets section */
      wf.zoomSection = 'sec:prod:sheets';
    } else if (_sec.indexOf('bim-phase-detail:') === 0) {
      /* BIM phase zoom → its parent element's zoom */
      var _bpUpParts = _sec.split(':');
      wf.zoomSection = 'bim-element-detail:' + (_bpUpParts[1] || '');
      wf.zoomRoot = 'design';
    } else if (_sec.indexOf('bim-element-detail:') === 0) {
      /* BIM element zoom → its parent system's zoom (fall back to the BIM Systems section) */
      var _beUpId = _sec.slice('bim-element-detail:'.length);
      var _beUpSys = (ctx.bimElementSystem || {})[_beUpId] ||
        (((ctx.bimZoomCache || {})[_beUpId] || {}).system || {}).id || '';
      wf.zoomSection = _beUpSys ? 'bim-system-detail:' + _beUpSys : 'sec:design:bim';
      wf.zoomRoot = 'design';
    } else if (_sec.indexOf('bim-system-detail:') === 0) {
      /* BIM system zoom → BIM Systems section under Design */
      wf.zoomSection = 'sec:design:bim';
      wf.zoomRoot = 'design';
    } else if (_sec && wf.zoomMsId && _sec.indexOf('sec:' + wf.zoomMsId + ':') === 0) {
      /* any milestone section zoom → milestone zoom */
      wf.zoomSection = null;
    } else if (_sec.indexOf('sec:prod:') === 0) {
      /* production section zoom → production root zoom */
      wf.zoomSection = null;
    } else if (_sec.indexOf('sec:design:') === 0) {
      /* design section zoom → design root zoom */
      wf.zoomSection = null;
    } else if (_sec.indexOf('sec:meeting:') === 0) {
      /* meeting sub-section (Details/Threads) → the meeting detail view */
      wf.zoomSection = 'meeting-detail:' + (_sec.split(':')[2] || '');
    } else if (_sec.indexOf('sec:threads:cat:') === 0) {
      /* thread category zoom → Threads root zoom */
      wf.zoomSection = null;
    } else if (_sec.indexOf('sec:team:printorder:') === 0) {
      /* print order → its shop */
      var _poUp = null;
      (ctx.printOrderCache || []).forEach(function(o) { if (o.id === _sec.slice('sec:team:printorder:'.length)) _poUp = o; });
      wf.zoomSection = _poUp ? 'sec:team:shop:' + _poUp.org_airtable_id : 'sec:team:group:local-resources';
    } else if (_sec.indexOf('sec:team:shop:') === 0) {
      /* print shop → Local Resources group zoom */
      wf.zoomSection = 'sec:team:group:local-resources';
    } else if (_sec.indexOf('sec:team:org:') === 0) {
      /* subconsultant org zoom → Subconsultants group zoom */
      wf.zoomSection = 'sec:team:group:subconsultants';
    } else if (_sec.indexOf('sec:team:group:') === 0) {
      /* team group zoom → Team root zoom */
      wf.zoomSection = null;
    } else if (_sec.indexOf('proposal-detail:') === 0) {
      /* proposal-detail → up to its RFP round (if any), else straight to the candidate */
      var _pdUpId = _sec.slice('proposal-detail:'.length);
      var _pdUpRfpCache = ctx.consultantRfpCache || { rfps: [], proposals: [] };
      var _pdUpProposal = null;
      (_pdUpRfpCache.proposals || []).forEach(function(p) { if (p.id === _pdUpId) _pdUpProposal = p; });
      if (_pdUpProposal && _pdUpProposal.rfp_id) {
        wf.zoomSection = 'rfp-detail:' + _pdUpProposal.rfp_id;
      } else {
        wf.zoomSection = 'candidate-detail:' + (_pdUpProposal ? _pdUpProposal.project_consultant_airtable_id : '');
      }
    } else if (_sec.indexOf('rfp-detail:') === 0) {
      /* rfp-detail → up to its candidate */
      var _rdUpId = _sec.slice('rfp-detail:'.length);
      var _rdUpRfpCache = ctx.consultantRfpCache || { rfps: [], proposals: [] };
      var _rdUpRfp = null;
      (_rdUpRfpCache.rfps || []).forEach(function(r) { if (r.id === _rdUpId) _rdUpRfp = r; });
      wf.zoomSection = 'candidate-detail:' + (_rdUpRfp ? _rdUpRfp.project_consultant_airtable_id : '');
    } else if (_sec.indexOf('candidate-detail:') === 0) {
      /* candidate-detail → up to the Subconsultants group zoom (Candidates has no zoom key of its own) */
      wf.zoomSection = 'sec:team:group:subconsultants';
    } else if (_sec.indexOf('sec:phase:') === 0 && _sec.indexOf(':thcat:') !== -1) {
      /* phase thread-category zoom → phase Threads section */
      wf.zoomSection = 'sec:phase:' + (_sec.split(':')[2] || '') + ':threads';
    } else if (_sec.indexOf('sec:phase:') === 0) {
      /* phase Milestones/Threads section zoom → phase zoom */
      wf.zoomSection = null;
    } else if (wf.zoomMsId) {
      /* milestone zoom → phase Milestones section (or phase zoom if no phase context) */
      wf.zoomMsId = null; wf.zoomCat = null; wf.zoomItemId = null;
      wf.zoomSection = wf.zoomPhaseId ? 'sec:phase:' + wf.zoomPhaseId + ':milestones' : null;
    } else if (wf.zoomPhaseId) {
      /* phase zoom → schedule root zoom */
      wf.zoomPhaseId = null;
    } else if (wf.zoomConceptId) {
      /* concept zoom → design root zoom */
      wf.zoomConceptId = null; wf.zoomSection = null;
    } else if (wf.zoomRoot) {
      if (_sec) {
        /* spec-section detail → up to the Specifications section (not all the way to Production) */
        if (wf.zoomSection.indexOf('spec-section-detail:') === 0) { wf.zoomSection = 'sec:prod:specs'; }
        /* entity zoom under a root (permit-detail, campaign-detail, …) → that root zoom */
        else { wf.zoomSection = null; }
      } else if (wf.zoomRoot !== 'reference' && REFERENCE_ROOTS.indexOf(wf.zoomRoot) !== -1) {
        /* reference child root → Reference zoom */
        wf.zoomRoot = 'reference';
      } else {
        /* root section → project home */
        wf.zoomRoot = null; wf.zoomSection = null; wf.zoomPhaseId = null;
        wf.zoomMsId = null; wf.zoomCat = null; wf.zoomItemId = null;
      }
    } else if (wf.zoomProjectId) {
      /* project home → project list */
      wf.zoomProjectId = null; wf.zoomRoot = null;
      state.phases = []; state.milestones = [];
      ctx.recordId = null;
    }

    saveViewState();
    render();
  };

  function applyHistoryState(s) {
    wf.zoomPhaseId = s.zoomPhaseId || null;
    wf.zoomMsId = s.zoomMsId || null;
    wf.zoomCat = s.zoomCat || null;
    wf.zoomItemId = s.zoomItemId || null;
    wf.suppressAnim = false;
    render();
  }

  /* ── Keyboard navigation state ─────────────────────────────────────────── */
  var _menuKbIdx = -1;   // highlighted index inside the currently-open action menu
  var _lastMenuEl = null; // tracks which menu element is open; resets _menuKbIdx on change
  var _kbSavedKey = null;  // saved selection (node data-key) restored by render() after a state change (Enter+menu, ← back)
  var _navBackKey = null;  // selection key at the time of a → zoom; restored when ← fires navBack()

  /* Stable identity of a navigable row = its node's data-key (rows live inside a .wf-node).
     Anchoring selection to this — not an array index — is what keeps arrow nav from snapping back
     when React re-renders / lazy-loads rows underneath the list. Returns null for keyless rows. */
  function rowKey(row) {
    if (!row || !row.closest) return null;
    var node = row.closest('.wf-node');
    return node ? node.getAttribute('data-key') : null;
  }
  /* The currently-selected row: by stable key if we have one, else by last index (keyless fallback). */
  function currentKbRow() {
    var items = getKbItems();
    if (wf.kbKey) { for (var i = 0; i < items.length; i++) { if (rowKey(items[i]) === wf.kbKey) return items[i]; } }
    if (wf.kbIdx >= 0 && wf.kbIdx < items.length) return items[wf.kbIdx];
    return null;
  }
  /* The data-key of the node we're currently zoomed INTO, expressed as it appears as a ROW one level
     up — so after ← (navLevelUp) the selection lands on the row we stepped up from, and an immediate
     → zooms straight back in. Derived from zoom state so it works regardless of how we entered (arrow
     OR click). Section-zoom keys already equal their row's data-key, so they pass through. Returns
     null when there's no meaningful parent row. */
  function zoomedFromKey() {
    if (wf.zoomItemId) return 'item:' + wf.zoomItemId;
    if (wf.zoomCat && wf.zoomMsId) return 'cat:' + wf.zoomMsId + ':' + wf.zoomCat;
    var sec = wf.zoomSection || '';
    if (sec) {
      if (sec.indexOf('meeting-detail:') === 0) return 'meeting:' + sec.slice('meeting-detail:'.length);
      if (sec.indexOf('meeting-cat:') === 0)    return 'mtgtype:' + sec.slice('meeting-cat:'.length);
      if (sec.indexOf('thread-detail:') === 0)   return 'thread:' + sec.slice('thread-detail:'.length);
      if (sec.indexOf('sec:threads:cat:') === 0) return 'thcat:all:' + sec.slice('sec:threads:cat:'.length);
      var _thm = sec.match(/^sec:phase:([^:]+):thcat:(.+)$/);
      if (_thm) return 'thcat:' + _thm[1] + ':' + _thm[2];
      return sec;   // section nodes use their section key as the row data-key
    }
    if (wf.zoomMsId) return 'ms:' + wf.zoomMsId;
    if (wf.zoomPhaseId) return 'ph:' + wf.zoomPhaseId;
    if (wf.zoomConceptId) return 'concept:' + wf.zoomConceptId;
    if (wf.zoomRoot) return 'root:' + wf.zoomRoot;
    return null;
  }
  /* Re-apply the highlight to the keyed row after a rebuild (React re-render / lazy load fire
     outline-render). Class-only, no scroll — so it never fights the user's scroll. Deferred + one
     retry to clear React's commit timing. Leaves wf.kbKey intact if the row is gone (collapsed away),
     so the next arrow re-seeds cleanly. */
  function _applyKbByKey() {
    if (!wf.kbKey) return false;
    var items = getKbItems();
    for (var i = 0; i < items.length; i++) {
      if (rowKey(items[i]) === wf.kbKey) { clearAllKbHighlight(); items[i].classList.add('wf-row--kb-selected'); wf.kbIdx = i; return true; }
    }
    return false;
  }
  function reapplyKb() {
    if (!wf.kbKey) return;
    setTimeout(function() { if (!_applyKbByKey()) setTimeout(_applyKbByKey, 60); }, 0);
  }
  window.addEventListener('outline-render', reapplyKb);

  /* ── Keyboard navigation helpers ───────────────────────────────────────── */
  /* Returns ALL visible .wf-row elements in both outline panels.
     Every row is navigable — section headers, leaf items, placeholders, etc. */
  function getKbItems() {
    return Array.from(document.querySelectorAll(
      '#aj-sc-root .wf-row, #aj-sc-sections .wf-row'
    )).filter(function(el) { return el.offsetParent !== null; });
  }
  /* Nesting depth of a row = number of .wf-children ancestors between it and an outline container.
     Top-level rows are depth 0; their children 1; grandchildren 2; etc. Used by Shift+↑/↓ to jump
     between same-level peers, matching the tree's own .wf-children nesting. */
  function rowDepth(row) {
    var d = 0, el = row ? row.parentElement : null;
    while (el && el.id !== 'aj-sc-root' && el.id !== 'aj-sc-sections' && el !== document.body) {
      if (el.classList && el.classList.contains('wf-children')) d++;
      el = el.parentElement;
    }
    return d;
  }
  /* Items are now .wf-row elements — operate on them directly. */
  function clearKbHighlight(items) {
    items.forEach(function(el) { el.classList.remove('wf-row--kb-selected'); });
  }
  /* Also clear any lingering class from React sections without a full item scan. */
  function clearAllKbHighlight() {
    Array.from(document.querySelectorAll('.wf-row--kb-selected')).forEach(function(el) {
      el.classList.remove('wf-row--kb-selected');
    });
  }
  /* noScroll = true when called from mouseover — hover shouldn't scroll the page.
     Keyboard navigation always scrolls (noScroll omitted or false).
     Uses manual scrollBy instead of scrollIntoView so we get one-row-at-a-time
     increments, no smooth-animation stutter, and correct accounting for the
     sticky chrome bar which scrollIntoView doesn't know about. */
  function setKbHighlight(items, idx, noScroll) {
    clearKbHighlight(items);
    var row = items[idx];
    if (!row) return;
    row.classList.add('wf-row--kb-selected');
    wf.kbIdx = idx;
    wf.kbKey = rowKey(row);   // anchor selection to a stable identity, not just the position
    if (!noScroll) {
      var chromeEl = document.getElementById('aj-sc-chrome');
      var chromeH  = chromeEl ? chromeEl.offsetHeight : 0;
      var pad  = 6; /* px breathing room at top/bottom edges */
      var rect = row.getBoundingClientRect();
      var vH   = window.innerHeight;
      if (rect.top < chromeH + pad) {
        /* Row is above visible area (or hidden behind sticky chrome bar) — scroll up */
        window.scrollBy(0, rect.top - chromeH - pad);
      } else if (rect.bottom > vH - pad) {
        /* Row is below visible area — scroll down */
        window.scrollBy(0, rect.bottom - vH + pad);
      }
    }
  }

  /* Track GENUINE pointer movement. A programmatic scroll (from keyboard nav's scrollBy) slides content
     under a stationary cursor and makes the browser fire `mouseover` on the newly-underneath row — with
     NO accompanying `mousemove`. Honoring that hijacks the keyboard selection back to wherever the cursor
     happens to rest (usually a top/open row), which is the "snaps to top on scroll" bug. So we only treat
     a mouseover as a real hover if the pointer actually moved just before it. */
  var _lastRealMoveAt = 0;
  document.addEventListener('mousemove', function() { _lastRealMoveAt = Date.now(); });

  /* ── Unified hover / keyboard highlight via mouseover ───────────────────── */
  document.addEventListener('mouseover', function(e) {
    /* Don't update outline highlight while an action menu is open */
    if (document.getElementById('wf-active-menu') || document.querySelector('body > .wf-menu')) return;

    /* Ignore scroll-induced mouseover (no real movement just happened) — keyboard selection wins. */
    if (Date.now() - _lastRealMoveAt > 120) return;

    /* Only handle events inside the two outline panels */
    var _tgt = e.target;
    var _inOutline = _tgt.closest && (
      _tgt.closest('#aj-sc-root') || _tgt.closest('#aj-sc-sections')
    );
    if (!_inOutline) {
      /* Mouse left the outline — do NOT clear highlight; keyboard nav stays active */
      return;
    }
    /* Find the closest .wf-row — every row is now navigable */
    var _hovRow = _tgt.closest ? _tgt.closest('.wf-row') : null;
    if (!_hovRow) return; /* Inside outline but not over a row — keep current highlight */
    var _hovItems = getKbItems();
    var _hovIdx = _hovItems.indexOf(_hovRow);
    if (_hovIdx >= 0 && _hovIdx !== wf.kbIdx) {
      wf.kbIdx = _hovIdx;
      setKbHighlight(_hovItems, _hovIdx, true /* noScroll — mouse hover never scrolls */);
    }
  });

  /* Global keyboard shortcuts */
  document.addEventListener('keydown', function(e) {
    /* A workspace (Billing, CRM, Bookkeeping, Templates, ...) is its own page: the outline hidden
       behind it must not answer navigation keys, or x could tick a checklist item nobody can see. */
    var _wsParam = new URLSearchParams(window.location.search).get('ws');
    var _wsOpen = !!_wsParam && _wsParam !== 'projects';
    if (!_wsOpen && (e.ctrlKey || e.metaKey) && e.key === '[') {
      e.preventDefault();
      _kbSavedKey = _navBackKey || null;
      _navBackKey = null;
      window.navLevelUp();
    }
    if (!_wsOpen && (e.ctrlKey || e.metaKey) && e.key === ']') { e.preventDefault(); _navBackKey = null; _kbSavedKey = null; window.navForward(); }
    /* '/' key opens the slash command bar when no text input is focused */
    if (e.key === '/' && !e.ctrlKey && !e.metaKey && !e.altKey) {
      var tag = document.activeElement ? document.activeElement.tagName : '';
      if (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT'
          && !document.querySelector('.wf-closeout-overlay, .aj-proj-info-overlay, .aj-pm-overlay')) {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('open-command-bar'));
      }
    }

    /* ── Action menu keyboard navigation ───────────────────────────────── */
    var _activeMenuEl = document.getElementById('wf-active-menu') || document.querySelector('body > .wf-menu');
    /* Reset index when the open menu element changes (new menu opened) */
    if (_activeMenuEl !== _lastMenuEl) { _menuKbIdx = -1; }
    _lastMenuEl = _activeMenuEl;

    if (_activeMenuEl && (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === 'Escape')) {
      e.preventDefault();
      var _mAllItems = Array.from(_activeMenuEl.querySelectorAll('.wf-menu-item'));
      /* Exclude disabled items (inline opacity:0.4 marks them as non-interactive) */
      var _mItems = _mAllItems.filter(function(it) { return it.style.opacity !== '0.4'; });

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (!_mItems.length) return;
        if (_menuKbIdx < 0) {
          _menuKbIdx = e.key === 'ArrowDown' ? 0 : _mItems.length - 1;
        } else {
          _menuKbIdx = e.key === 'ArrowDown'
            ? Math.min(_menuKbIdx + 1, _mItems.length - 1)
            : Math.max(_menuKbIdx - 1, 0);
        }
        _mItems.forEach(function(it, i) { it.classList.toggle('wf-menu-item--kb', i === _menuKbIdx); });

      } else if (e.key === 'Enter' && _menuKbIdx >= 0 && _menuKbIdx < _mItems.length) {
        var _selItem = _mItems[_menuKbIdx];
        _mAllItems.forEach(function(it) { it.classList.remove('wf-menu-item--kb'); });
        _menuKbIdx = -1;
        _selItem.click();

      } else if (e.key === 'Escape') {
        _mAllItems.forEach(function(it) { it.classList.remove('wf-menu-item--kb'); });
        _menuKbIdx = -1;
        /* Close outline engine menu */
        var _oel = document.getElementById('wf-active-menu');
        if (_oel && _oel.parentNode) { _oel.parentNode.removeChild(_oel); wf.menuNodeId = null; }
        /* React .wf-menu has its own Escape listener and closes itself */
      }
      return;
    }

    /* ── Outline node navigation ────────────────────────────────────────── */
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'a' || e.key === 'Escape' || e.key === ' ' || e.key === 'x') {
      var _kbTag = document.activeElement ? document.activeElement.tagName : '';
      if (_kbTag === 'INPUT' || _kbTag === 'TEXTAREA' || _kbTag === 'SELECT') return;
      if (wf.commandOpen) return;
      if (_wsOpen) return;
      if (document.querySelector('.wf-closeout-overlay, .aj-proj-info-overlay, .aj-pm-overlay')) return;
      /* Prevent browser scroll / default for ALL nav keys — must happen before any early return */
      e.preventDefault();

      /* ↑ / ↓ — move selection by STABLE KEY, not a raw index. Re-find the currently-selected row in
         the freshly-queried list (so rows that lazy-loaded above us don't make +1 land on an earlier
         row), then step one. Falls back to the last index for keyless rows. */
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        var _kbItems = getKbItems();
        if (!_kbItems.length) return;
        var _down = e.key === 'ArrowDown';
        var _cur = -1;
        if (wf.kbKey) { for (var _ci = 0; _ci < _kbItems.length; _ci++) { if (rowKey(_kbItems[_ci]) === wf.kbKey) { _cur = _ci; break; } } }
        if (_cur < 0 && wf.kbIdx >= 0 && wf.kbIdx < _kbItems.length) _cur = wf.kbIdx;  // keyless fallback
        /* Shift held: jump to the next/previous row at the SAME nesting depth, skipping any deeper
           (child) rows in between. Falls back to plain step when there's no current selection. */
        if (e.shiftKey && _cur >= 0) {
          var _depth = rowDepth(_kbItems[_cur]);
          var _tgt = -1;
          for (var _di = _cur + (_down ? 1 : -1); _di >= 0 && _di < _kbItems.length; _di += (_down ? 1 : -1)) {
            if (rowDepth(_kbItems[_di]) === _depth) { _tgt = _di; break; }
          }
          if (_tgt >= 0) setKbHighlight(_kbItems, _tgt);
          return;
        }
        var _next = _cur < 0 ? (_down ? 0 : _kbItems.length - 1)
                             : Math.max(0, Math.min(_kbItems.length - 1, _cur + (_down ? 1 : -1)));
        setKbHighlight(_kbItems, _next);   // sets wf.kbIdx + wf.kbKey
      }

      /* 'a' — open the action menu (⋯) on the highlighted row.
         Use currentKbRow() (index fallback) rather than guarding on wf.kbKey, so rows whose node has
         no data-key (e.g. discipline groups + sheet rows under a Drawing Sheets section) still open
         their menu. Save position so render() after a menu action can restore it. */
      if (e.key === 'a') {
        var _entRow = currentKbRow();
        if (_entRow) {
          _kbSavedKey = wf.kbKey;   // may be null for keyless rows — harmless
          var _entMenuBtn = _entRow.querySelector('.wf-menu-btn');
          if (_entMenuBtn) _entMenuBtn.click();
        }
      }

      /* → — zoom into the highlighted row (clicks its zoomTo bullet if it has one).
         Save the current position so ← can restore the view to this exact row. */
      if (e.key === 'ArrowRight' && wf.kbKey) {
        var _rightRow = currentKbRow();
        if (_rightRow) {
          /* zoomTo bullets are the outline engine's; React-rendered zoom bullets (drawing sheets,
             discipline groups) mark themselves with data-kbzoom so → can drive them too (bug 3). */
          var _rightBullet = _rightRow.querySelector('.wf-bullet[data-fn="zoomTo"], .wf-bullet[data-kbzoom]');
          if (_rightBullet) {
            _navBackKey = wf.kbKey; /* remember where we came from */
            wf.kbKey = null; wf.kbIdx = -1;
            _kbSavedKey = null;
            _rightBullet.click();
          }
        }
      }

      /* ← — navigate one level up the hierarchy */
      if (e.key === 'ArrowLeft') {
        clearKbHighlight(getKbItems());
        wf.kbKey = null; wf.kbIdx = -1;
        // Land on the row we stepped up from (the node we were zoomed into) so → re-enters it.
        _kbSavedKey = _navBackKey || zoomedFromKey() || null;
        _navBackKey = null;
        window.navLevelUp();
      }

      /* Space — expand/collapse the highlighted row's branch, staying on it.
         Shift+Space — expand/collapse ALL descendants under it (recursively), via the SAME
         expandCollapseDescendants the action-menu Expand all / Collapse all buttons use. Toggle:
         if the node is currently expanded, collapse all; otherwise expand all. */
      if (e.key === ' ' && wf.kbKey) {
        if (e.shiftKey) {
          var _expandAll = !(wf.collapsed[wf.kbKey] === false); // expanded → collapse; else expand
          _kbSavedKey = wf.kbKey; /* preserve selection through render() */
          expandCollapseDescendants(wf.kbKey, _expandAll);
        } else {
          var _spRow = currentKbRow();
          if (_spRow) {
            var _spTri = _spRow.querySelector('button.wf-triangle');
            if (_spTri) {
              _kbSavedKey = wf.kbKey; /* preserve selection through render() */
              _spTri.click();
            }
          }
        }
      }

      /* x — mark the highlighted checklist item complete (equivalent to opening its ⋯ menu and
         clicking Complete/Mark incomplete — always a toggle). Works for milestone checklist items
         (ItemNode, identified by its .wf-menu-btn[data-type="item"]), Drawing Sheet checklist
         items (SheetClItemNode, identified by data-cl-kind="sheet" on its .wf-node — it has no
         ES5 data-fn menu button, so this reads attributes added specifically for this), and BIM
         criterion rows (BimCriterionRow, data-cl-kind="bim-criterion", same reasoning).
         No-ops on skipped ("Not Required") items — the ⋯ menu doesn't offer Complete there either. */
      if (e.key === 'x') {
        var _xRow = currentKbRow();
        if (_xRow) {
          var _xMenuBtn = _xRow.querySelector('.wf-menu-btn[data-type="item"]');
          if (_xMenuBtn) {
            if (_xMenuBtn.getAttribute('data-isskip') !== 'true') {
              window.ajScCompleteItem(_xMenuBtn.getAttribute('data-itemid'), _xMenuBtn.getAttribute('data-msid'));
            }
          } else {
            var _xNode = _xRow.closest('.wf-node[data-cl-kind="sheet"]');
            if (_xNode && _xNode.getAttribute('data-cl-skip') !== 'true' && window.ajScToggleSheetClItem) {
              window.ajScToggleSheetClItem(
                _xNode.getAttribute('data-cl-item'),
                _xNode.getAttribute('data-cl-sheetid'),
                _xNode.getAttribute('data-cl-phase')
              );
            } else {
              var _xBimNode = _xRow.closest('.wf-node[data-cl-kind="bim-criterion"]');
              if (_xBimNode && window.ajScToggleBimCriterion) {
                window.ajScToggleBimCriterion(
                  _xBimNode.getAttribute('data-cl-systemid'),
                  _xBimNode.getAttribute('data-cl-elementid'),
                  _xBimNode.getAttribute('data-cl-phase'),
                  _xBimNode.getAttribute('data-cl-item')
                );
              }
            }
          }
        }
      }

      /* Esc — clear highlight and reset navigation stack */
      if (e.key === 'Escape' && wf.kbKey) {
        clearKbHighlight(getKbItems());
        wf.kbKey = null; wf.kbIdx = -1;
        _kbSavedKey = null;
        _navBackKey = null;
      }
    }
  });


  window.ajScSetPrimary = function(idx) { var ms=state.milestones[idx]; if(ms){wf.collapsed[msKey(ms.id)]=false; render();} };
  window.ajScBannerAction = function(){};


  function getCurrentMemberId() {
    return wf.currentMemberId || '';
  }
  /* ============================================================
   * Shared modal factory — all modals use this for consistency
   * Returns { overlay, box, head, body, foot, title, closeBtn }
   * Caller appends content to .body and buttons to .foot
   * ============================================================ */
  function createModal(id, titleText, width) {
    var old = document.getElementById(id);
    if (old) old.parentNode.removeChild(old);
    width = width || 400;
    var overlay = document.createElement('div');
    overlay.id = id;
    overlay.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(7,39,45,0.45);z-index:99999;display:flex;align-items:center;justify-content:center;padding:16px;';
    var box = document.createElement('div');
    box.style.cssText = 'background:#fff;border-radius:10px;border:0.5px solid rgba(7,39,45,0.12);width:'+width+'px;max-width:100%;max-height:calc(100vh - 32px);max-height:calc(100dvh - 32px);display:flex;flex-direction:column;overflow:hidden;box-shadow:0 8px 40px rgba(7,39,45,0.18);';
    /* Header */
    var head = document.createElement('div');
    head.style.cssText = 'display:flex;align-items:center;padding:14px 18px;border-bottom:0.5px solid rgba(7,39,45,0.10);flex-shrink:0;';
    var title = document.createElement('span');
    title.style.cssText = 'flex:1;font-family:"DM Sans",sans-serif;font-size:13px;font-weight:500;color:#07272D;';
    title.textContent = titleText;
    var closeBtn = document.createElement('button');
    closeBtn.style.cssText = 'background:none;border:none;cursor:pointer;color:#7a9aaa;font-size:18px;line-height:1;padding:6px 8px;margin:-6px -8px -6px 0;transition:color 0.1s;';
    closeBtn.innerHTML = '&#215;';
    closeBtn.addEventListener('mouseover', function(){ closeBtn.style.color='#07272D'; });
    closeBtn.addEventListener('mouseout', function(){ closeBtn.style.color='#7a9aaa'; });
    head.appendChild(title);
    head.appendChild(closeBtn);
    /* Body */
    var body = document.createElement('div');
    body.style.cssText = 'padding:18px;overflow-y:auto;flex:1;-webkit-overflow-scrolling:touch;';
    /* Footer */
    var foot = document.createElement('div');
    foot.style.cssText = 'display:flex;justify-content:flex-end;gap:8px;padding:12px 18px;border-top:0.5px solid rgba(7,39,45,0.10);flex-shrink:0;';
    box.appendChild(head);
    box.appendChild(body);
    box.appendChild(foot);
    overlay.appendChild(box);
    document.body.appendChild(overlay);
    var m = { overlay:overlay, box:box, head:head, title:title, body:body, foot:foot };
    m.close = function() { var el=document.getElementById(id); if(el) el.parentNode.removeChild(el); };
    closeBtn.addEventListener('click', function() { m.close(); });
    overlay.addEventListener('click', function(e){ if(e.target===overlay) m.close(); });
    return m;
  }

  function modalBtn(label, primary) {
    var b = document.createElement('button');
    b.textContent = label;
    if (primary) {
      b.style.cssText = 'background:#07272D;border:none;border-radius:4px;padding:8px 20px;font-family:"DM Sans",sans-serif;font-size:13px;font-weight:500;color:white;cursor:pointer;';
    } else {
      b.style.cssText = 'background:transparent;border:0.5px solid rgba(7,39,45,0.18);border-radius:4px;padding:8px 16px;font-family:"DM Sans",sans-serif;font-size:13px;color:#3a5260;cursor:pointer;';
    }
    return b;
  }

  function modalLabel(text) {
    var l = document.createElement('div');
    l.style.cssText = 'font-family:"DM Sans",sans-serif;font-size:11px;font-weight:500;color:#7a9aaa;letter-spacing:0.05em;text-transform:uppercase;margin-bottom:6px;';
    l.textContent = text;
    return l;
  }

  function loadTeamContacts(cb) {
    if (teamContacts.length) { cb(); return; }
    var qs = 'filterByFormula=' + encodeURIComponent('{Contact Type}="Team"') + '&fields[]=Name&pageSize=100';
    apiGet(TBL_CONTACTS, qs, function(d) {
      teamContacts = d ? d.records.map(function(r) { return { id: r.id, name: r.fields['Name'] || r.id }; }) : [];
      teamContacts.sort(function(a,b) { return a.name < b.name ? -1 : a.name > b.name ? 1 : 0; });
      cb();
      /* Re-render expanded milestones so support names resolve */
      if (wf.zoomMsId) { render(); }
      else {
        state.milestones.forEach(function(ms) {
          if (wf.collapsed[msKey(ms.id)] === false) refreshCats(ms.id);
        });
      }
    });
  }

  /* MEMBER ROSTER / SKIP */
  function loadMembers(cb) {
    if (membersCache) { cb(membersCache); return; }
    apiGet('tblzax2FPEul41fjN', 'fields[]=Member+Name&sort[0][field]=Member+Name&sort[0][direction]=asc&maxRecords=50', function(d) {
      membersCache = d ? d.records.map(function(r) { return { id: r.id, name: r.fields['Member Name'] || '' }; }) : [];
      cb(membersCache);
      /* Re-render any expanded milestones so item names resolve */
      if (wf.zoomMsId) { render(); }
      else {
        state.milestones.forEach(function(ms) {
          if (wf.collapsed[msKey(ms.id)] === false) refreshCats(ms.id);
        });
      }
    });
  }

  window.ajScSkipItem = function(itemId, msId) {
    if (refuseBlocked(msId)) return;
    var items = clCache[msId] || [], record = null, i;
    for (i=0; i<items.length; i++) { if (items[i].id === itemId) { record = items[i]; break; } }
    if (!record) return;
    var wasSkipped = record.fields['Milestone Not Required'] === true;
    record.fields['Milestone Not Required'] = !wasSkipped;
    refreshCats(msId);
    showPill(!wasSkipped ? 'Marked not required' : 'Unmarked', false);
    apiPatch(TBL_CL, itemId, { 'Milestone Not Required': !wasSkipped }, function(ok) {
      if (!ok) { record.fields['Milestone Not Required'] = wasSkipped; refreshCats(msId); showPill('Save failed', true); return; }
      resyncMilestoneRollup(msId);
    });
  };

  function loadChecklist(msId) {
    if (clCache[msId]) { refreshCats(msId); return; }
    var qs='filterByFormula='+encodeURIComponent('FIND("'+msId+'",ARRAYJOIN({Record ID (from Deliverable)},","))')+'&sort[0][field]=Category+Sort&sort[0][direction]=asc&sort[1][field]=Sort+Order+(from+Deliverable+Checklist+Item+Template)&sort[1][direction]=asc&maxRecords=100';
    apiGet(TBL_CL, qs, function(d){
      clCache[msId]=d?d.records:[];
      /* Full render if zoomed anywhere into this milestone's checklist */
      if (wf.zoomMsId === msId) {
        render();
      } else if (wf.collapsed[msKey(msId)] === false) {
        refreshCats(msId);
      }
    });
  }

  /* Check if hide-done is active for any ancestor of the given keys */
  function shouldHideDone() {
    var keys = wf.hideDoneKeys;
    if (!keys) return false;
    /* Check global, phase, ms, cat, section keys that are currently in scope */
    var checkKeys = ['global'];
    if (wf.zoomPhaseId) checkKeys.push('ph:' + wf.zoomPhaseId);
    else {
      /* Check all phase keys */
      state.phases.forEach(function(ph) { checkKeys.push('ph:' + ph.id); });
    }
    for (var i=0; i<checkKeys.length; i++) { if (keys[checkKeys[i]]) return true; }
    return false;
  }

  function shouldHideDoneForMs(msId, phId) {
    var keys = wf.hideDoneKeys;
    if (!keys) return false;
    return !!(keys['global'] || (phId && keys['ph:'+phId]) || keys['ms:'+msId]);
  }

  function shouldHideDoneForCat(msId, phId, cat) {
    var keys = wf.hideDoneKeys;
    if (!keys) return false;
    return !!(keys['global'] || (phId && keys['ph:'+phId]) || keys['ms:'+msId] || keys['cat:'+msId+':'+cat]);
  }

  function toggleHideDoneKey(key) {
    if (wf.hideDoneKeys[key]) { delete wf.hideDoneKeys[key]; }
    else { wf.hideDoneKeys[key] = true; }
    render();
  }

  function processItems(items) {
    items = items.slice().sort(function(a,b){
      var af=a.fields, bf=b.fields;
      var aCat=getFirst(af['Category (from Deliverable Checklist Item Template)'])||'';
      var bCat=getFirst(bf['Category (from Deliverable Checklist Item Template)'])||'';
      var aCS=CAT_SORT_ORDER[aCat]||9, bCS=CAT_SORT_ORDER[bCat]||9;
      if(aCS!==bCS) return aCS-bCS;
      var aSO=parseFloat(getFirst(af['Sort Order (from Deliverable Checklist Item Template)'])||0);
      var bSO=parseFloat(getFirst(bf['Sort Order (from Deliverable Checklist Item Template)'])||0);
      return aSO-bSO;
    });
    var itemById={}, i;
    for(i=0;i<items.length;i++) itemById[items[i].id]=items[i];
    var blocked=false;
    return items.map(function(r){
      var f=r.fields;
      var isComplete=f['Complete']===true||f['Milestone Not Required']===true;
      var condQ=getFirst(f['Condition Question (from Deliverable Checklist Item Template)']);
      var condA=getFirst(f['Condition Answer (from Deliverable Checklist Item Template)']);
      var condPIds=f['Condition Parent (from Deliverable Checklist Item Template)']||[];
      var isBlocker=f['Blocks Progression']===true||(condQ&&!f['Response']);
      var isCondChild=condPIds.length>0;
      var condVisible=true;
      if(isCondChild){ var pid=Array.isArray(condPIds)?condPIds[0]:condPIds; var par=itemById[pid]; condVisible=par?(par.fields['Response']===condA):false; }
      var hiddenByBlocker=blocked;
      if(!blocked&&condVisible&&!isCondChild&&isBlocker&&!isComplete) blocked=true;
      return {record:r,fields:f,isComplete:isComplete,condQ:condQ,condA:condA,isBlocker:isBlocker,isCondChild:isCondChild,condVisible:condVisible,hiddenByBlocker:hiddenByBlocker};
    });
  }

  function renderNextAction(msId) {
    var zone=document.getElementById('aj-sc-next-zone'); if(!zone) return;
    var items=(clCache[msId]||[]).filter(function(item) {
      var cat=getFirst(item.fields['Category (from Deliverable Checklist Item Template)'])||'Other';
      return HIDDEN_CL_CATEGORIES.indexOf(cat)===-1;
    });
    var processed=processItems(items);
    var visible=processed.filter(function(i){ return !i.hiddenByBlocker&&i.condVisible; });
    var incomplete=visible.filter(function(i){ return !i.isComplete; });
    var doneCount=visible.filter(function(i){ return i.isComplete; }).length;
    var locked=processed.some(function(i){ return i.hiddenByBlocker&&i.condVisible; });
    if(!incomplete.length&&items.length>0) {
      var ms=state.milestones[primaryIdx], msName=ms?(ms.fields['Full Deliverable Name']||ms.fields['Name']||''):'';
      zone.innerHTML='<div class="aj-sc-all-done"><div class="aj-sc-all-done-text">'+ckSvg('#1D9E75')+' All checklist items complete.</div>'+(locked?'<div style="font-size:11px;color:#7a9aaa;margin-bottom:10px;">Some items hidden by gate logic.</div>':'')+
        '<button class="aj-sc-post-btn" data-fn="openPost" data-msid="'+msId+'" data-msname="'+msName+'">Post milestone</button></div>';
      return;
    }
    if(!incomplete.length&&!items.length) { zone.innerHTML='<div style="padding:14px 16px;font-size:12px;color:#7a9aaa;">No checklist items for this milestone.</div>'; return; }
    var next=incomplete[0], f=next.fields;
    var name=getFirst(f['Name for List (from Deliverable Checklist Item Template)'])||'Next item';
    var cat=getFirst(f['Category (from Deliverable Checklist Item Template)'])||'';
    var summary=getFirst(f['Summary (from Deliverable Checklist Item Template)'])||'';
    var response=f['Response']||'', isQ=!!next.condQ;
    var cardCls='aj-sc-next-item-card'+(next.isBlocker&&!isQ?' is-blocker':'')+(isQ?' is-question':'');
    var h='<div class="aj-sc-next-action"><div class="aj-sc-next-label">Next action <span class="aj-sc-next-count">'+doneCount+' of '+visible.length+' done'+(locked?' &middot; more locked':'')+'</span></div>';
    h+='<div class="'+cardCls+'">';
    if(cat) h+='<div class="aj-sc-next-item-cat">'+cat+'</div>';
    h+='<div class="aj-sc-next-item-name">'+name+'</div>';
    if(summary) h+='<div class="aj-sc-next-item-summary">'+summary+'</div>';
    if(isQ) {
      h+='<div style="font-size:13px;font-weight:500;color:#07272D;margin:6px 0 4px;">'+next.condQ+'</div>';
      h+='<div class="aj-sc-response-wrap"><button class="aj-sc-response-btn'+(response==='Yes'?' sel-yes':'')+'" data-fn="setResp" data-id="'+next.record.id+'" data-resp="Yes" data-msid="'+msId+'">'+ckSvg(response==='Yes'?'white':'#3a5260')+' Yes</button>';
      h+='<button class="aj-sc-response-btn'+(response==='No'?' sel-no':'')+'" data-fn="setResp" data-id="'+next.record.id+'" data-resp="No" data-msid="'+msId+'">'+( response==='No'?ckSvg('white'):'')+' No</button></div>';
    } else {
      var isSkipped   = next.fields['Milestone Not Required']===true;
      if (isSkipped) {
        h+='<div class="aj-sc-skipped-badge">Marked not required</div>';
      } else {
        h+='<button class="aj-sc-complete-btn" data-fn="completeItem" data-id="'+next.record.id+'" data-msid="'+msId+'">'+ckSvg('white')+' Mark complete</button>';
        h+='<div class="aj-sc-item-actions">';
        h+='<button class="aj-sc-skip-btn" data-fn="skipItem" data-id="'+next.record.id+'" data-msid="'+msId+'">Not required</button>';
        h+='</div>';
      }
      if(incomplete.length>1) h+='<div style="font-size:11px;color:#7a9aaa;margin-top:8px;text-align:center;">'+(incomplete.length-1)+' more item'+(incomplete.length>1?'s':'')+' after this one</div>';
    }
    h+='</div></div>';
    zone.innerHTML=h;
  }

  function renderFullChecklist(msId) {
    var panel=document.getElementById('aj-sc-full-cl'); if(!panel) return;
    var items=(clCache[msId]||[]).filter(function(item) {
      var cat=getFirst(item.fields['Category (from Deliverable Checklist Item Template)'])||'Other';
      return HIDDEN_CL_CATEGORIES.indexOf(cat)===-1;
    });
    var processed=processItems(items);
    var sections={}, sOrder=[];
    processed.forEach(function(item){
      if(!item.condVisible||item.hiddenByBlocker) return;
      var cat=getFirst(item.fields['Category (from Deliverable Checklist Item Template)'])||'Other';
      if(!sections[cat]){ sections[cat]=[]; sOrder.push(cat); }
      sections[cat].push(item);
    });
    sOrder.sort(function(a,b){ var ai=CAT_ORDER.indexOf(a),bi=CAT_ORDER.indexOf(b); if(ai===-1)ai=99; if(bi===-1)bi=99; return ai-bi; });
    var hasLocked=processed.some(function(i){ return i.hiddenByBlocker&&i.condVisible; });
    var h='';
    sOrder.forEach(function(cat){
      var sec=sections[cat], done=sec.filter(function(i){ return i.isComplete; }).length;
      h+='<div class="aj-sc-cl-section-head">'+catLabel(cat)+'<span class="aj-sc-cl-section-count">'+done+'/'+sec.length+'</span></div>';
      sec.forEach(function(item){
        var f=item.fields, name=getFirst(f['Name for List (from Deliverable Checklist Item Template)'])||'Item';
        var response=f['Response']||'', isQ=!!item.condQ;
        var isSkip = item.fields['Milestone Not Required']===true;
        var rowCls='aj-sc-cl-row'+(item.isComplete||isSkip?' done':'');
        var ckCls='aj-sc-cl-check'+(isQ?' is-q'+(response?' answered':''):'')+(item.isComplete||isSkip||response?' checked':'');
        h+='<div class="'+rowCls+'">';
        if(!isQ) h+='<div class="'+ckCls+'" data-fn="completeItem" data-id="'+item.record.id+'" data-msid="'+msId+'">'+( item.isComplete?ckSvg('white'):'')+' </div>';
        else h+='<div class="'+ckCls+'">'+(response?ckSvg('white'):'')+'</div>';
        h+='<div class="aj-sc-cl-row-name">'+name+'</div></div>';
      });
    });
    if(hasLocked) h+='<div class="aj-sc-cl-locked-row"><span style="color:#b8540a;margin-right:4px;">'+lkSvg()+'</span><span class="aj-sc-cl-locked-text">More items unlock when the current gate is complete.</span></div>';
    panel.innerHTML=h;
  }

  window.ajScToggleFull = function() {
    var panel=document.getElementById('aj-sc-full-cl'), btn=document.getElementById('aj-sc-cl-toggle'); if(!panel) return;
    clOpen=!panel.classList.contains('open');
    if(clOpen){ panel.classList.add('open'); if(btn) btn.innerHTML='<svg width="10" height="10" viewBox="0 0 16 16" fill="none"><polyline points="2,11 8,5 14,11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg> Hide checklist'; var ms=state.milestones[primaryIdx]; if(ms) renderFullChecklist(ms.id); }
    else { panel.classList.remove('open'); if(btn) btn.innerHTML='<svg width="10" height="10" viewBox="0 0 16 16" fill="none"><polyline points="2,5 8,11 14,5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg> Full checklist'; }
  };

  window.ajScTogglePinDates = function(msId, currentlyPinned) {
    var ms = getMsById(msId);
    if (!ms) return;
    var newVal = !currentlyPinned;
    ms.fields['Date Pinned'] = newVal;
    render();
    showPill(newVal ? 'Dates pinned' : 'Dates unpinned', false);
    apiPatch(TBL_DEL, msId, {'Date Pinned': newVal}, function(ok){ if(!ok){ ms.fields['Date Pinned']=currentlyPinned; render(); showPill('Save failed',true); } });
  };

  window.ajScCopyMsLink = function(url) {
    if (!url) return;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(function(){ showPill('Link copied', false); });
    } else {
      var ta = document.createElement('textarea');
      ta.value = url; ta.style.position='fixed'; ta.style.top='-9999px';
      document.body.appendChild(ta); ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      showPill('Link copied', false);
    }
  };

  /* The milestone row itself (MilestoneRow in ScheduleSection.jsx) reads its own checklist
     progress off 'Completed Checklist Items' / 'Incomplete Checklist Items' — Airtable rollup
     fields ON THE MILESTONE RECORD, not on the checklist item. Toggling an item's Complete/Not
     Required status only PATCHes the item, so the milestone's cached rollup goes stale until a
     full reload re-fetches it — a hard refresh "fixes" it because that's the only thing that
     re-fetches the milestone list. Re-fetch just this one milestone record and swap it into
     state.milestones so the row re-renders with current data, no reload required. */
  function resyncMilestoneRollup(msId) {
    apiGet(TBL_DEL + '/' + msId, '', function(msRec) {
      if (!msRec || !msRec.id) return;
      var ms = (state.milestones || []).find(function(m) { return m.id === msId; });
      if (ms) { ms.fields = msRec.fields; window.dispatchEvent(new Event('outline-render')); }
    });
  }

  window.ajScMarkRequired = function(itemId, msId) {
    if (refuseBlocked(msId)) return;
    var items=clCache[msId]||[], record=null, i;
    for(i=0;i<items.length;i++){ if(items[i].id===itemId){ record=items[i]; break; } }
    if(!record) return;
    record.fields['Milestone Not Required'] = false;
    record.fields['Milestone Not Required Reason'] = '';
    record.fields['Milestone Not Required Member'] = [];
    wf.dirty[msId] = true;
    refreshCats(msId);
    showPill('Marked required', false);
    apiPatch(TBL_CL, itemId, {'Milestone Not Required': false, 'Milestone Not Required Reason': '', 'Milestone Not Required Member': []}, function(ok){
      if(!ok) { showPill('Save failed',true); return; }
      resyncMilestoneRollup(msId);
    });
  };

  window.ajScCompleteItem = function(itemId, msId) {
    if (refuseBlocked(msId)) return;
    var items=clCache[msId]||[], record=null, i;
    for(i=0;i<items.length;i++){ if(items[i].id===itemId){ record=items[i]; break; } }
    if(!record) return;
    var newVal=!record.fields['Complete'];
    /* CLIENT ONBOARDING HOMEWORK (Matt, 2026-10-27): ticking an item asks the day it actually came in, so
       the homework posts itself on the real date once the last item is in (api/_homework.js). */
    var msRec = getMsById(msId);
    var isHomework = newVal && msRec && ((msRec.fields && msRec.fields['Deliverable']) || []).indexOf(HOMEWORK_TYPE_ID) >= 0;
    if (isHomework && !arguments[2]) {
      var itemName = getFirst(record.fields['Name for List (from Deliverable Checklist Item Template)']) || 'This item';
      promptDialog({ title: 'When did it come in?', message: itemName + ' The homework posts itself on the day the last item came in.', inputType: 'date', defaultValue: todayStr(), required: true, confirmLabel: 'It came in' })
        .then(function(d){
          if (!d) return;
          window.ajScCompleteItem(itemId, msId, d);
          // The file itself goes to the project's Reference Documents (docs/REFERENCE_DOCUMENTS.md), so the
          // team finds it there instead of in someone's email. Not offered for the questionnaire (it lives on the Hub).
          if (/questionnaire/i.test(itemName)) return;
          var projId = (((msRec.fields || {})['Project']) || [])[0] || ctx.recordId || wf.zoomProjectId;
          if (!projId) return;
          confirmDialog({ title: 'Attach the file?', message: 'Keep it in this project’s Reference Documents so the team can find it.', confirmLabel: 'Choose file…', cancelLabel: 'Not now' })
            .then(function(yes){ if (!yes) return null; return pickFiles(); })
            .then(function(files){
              if (!files || !files.length) return;
              var me = ctx.currentMember, nm = me && me.fields ? (me.fields['Member Name'] || me.fields['Name'] || null) : null;
              return files.reduce(function(p, file){ return p.then(function(){ return uploadReferenceDocument({ projectId: projId, file: file, memberId: me && me.id, memberName: nm }); }); }, Promise.resolve())
                .then(function(){ announceReferenceDocumentsChanged(projId); showPill(files.length > 1 ? files.length + ' files filed' : 'File filed', false); });
            })
            .catch(function(e){ alertDialog({ title: 'The upload did not finish', message: String(e && e.message || e) }); });
        });
      return;
    }
    var receivedOn = isHomework ? arguments[2] : null;
    record.fields['Complete']=newVal;
    var memberId = getCurrentMemberId();
    var patchFields = {'Complete': newVal};
    if (receivedOn) patchFields['Received On'] = receivedOn;
    if (newVal && memberId) {
      record.fields['Completed by'] = [memberId];
      patchFields['Completed by'] = [memberId];
      /* Completed Date is an Airtable lastModifiedTime field — it won't be
         known client-side until the next full fetch. Set it optimistically
         so the "done" badge shows a date immediately instead of only after
         a refresh (the server's own value, once persisted, matches the same
         moment anyway). */
      record.fields['Completed Date'] = receivedOn ? receivedOn + 'T12:00:00Z' : new Date().toISOString();
    } else if (!newVal) {
      record.fields['Completed by'] = [];
      patchFields['Completed by'] = [];
    }
    refreshCats(msId);
    showPill(newVal?'Saved':'Unchecked',false);
    apiPatch(TBL_CL, itemId, patchFields, function(ok){
      if(!ok){ record.fields['Complete']=!newVal; refreshCats(msId); showPill('Save failed',true); return; }
      resyncMilestoneRollup(msId);
      // The last homework item in posts the homework (server side): show it posted.
      if (receivedOn && items.every(function(it){ return it.fields['Complete'] || it.fields['Milestone Not Required']; })) {
        showPill('All in. Homework posted', false);
        var pid = ctx.recordId || wf.zoomProjectId;
        if (ctx.reloadProjectData && pid) setTimeout(function(){ ctx.reloadProjectData(pid); }, 800);
      }
    });
  };

  window.ajScSetResp = function(itemId, response, msId) {
    if (refuseBlocked(msId)) return;
    var items=clCache[msId]||[], record=null, i;
    for(i=0;i<items.length;i++){ if(items[i].id===itemId){ record=items[i]; break; } }
    if(!record) return;
    var prev=record.fields['Response']||'';
    record.fields['Response']=response;
    refreshCats(msId);
    apiPatch(TBL_CL, itemId, {'Response':response}, function(ok){ if(!ok){ record.fields['Response']=prev; refreshCats(msId); showPill('Save failed',true); } });
  };

  /* ajScOpenPost removed — both post paths now dispatch open-post-ms-modal event */
  /* Phase edit modal - built dynamically and appended to body to escape Softr containment */
  function openPhaseEditModal(phId) {
    var ph = getPhaseById(phId);
    if (!ph) return;
    var f = ph.fields;
    var curType = f['Contract Type'] || 'Fixed Fee';
    var curFee = f['Contracted Fee'] ? parseFloat(f['Contracted Fee']).toFixed(2) : '';
    var curCoreId = f['BQE Core Record ID'] || '';

    var m = createModal('wf-phase-modal-overlay', f['Phase Name'] || 'Edit Phase', 380);

    /* Contract Type */
    m.body.appendChild(modalLabel('Contract Type'));
    var toggle = document.createElement('div');
    toggle.style.cssText = 'display:flex;border:0.5px solid rgba(7,39,45,0.20);border-radius:4px;overflow:hidden;margin-bottom:14px;';
    var typeFixed = document.createElement('button');
    var typeHourly = document.createElement('button');
    [typeFixed, typeHourly].forEach(function(b, i) {
      b.textContent = i===0 ? 'Fixed Fee' : 'Hourly';
      b.style.cssText = 'flex:1;padding:8px;background:none;border:none;font-family:"DM Sans",sans-serif;font-size:14px;cursor:pointer;transition:all 0.1s;';
    });
    function setType(t) {
      typeFixed.style.background = t==='Fixed Fee'?'#07272D':'none';
      typeFixed.style.color = t==='Fixed Fee'?'white':'#7a9aaa';
      typeHourly.style.background = t==='Hourly'?'#07272D':'none';
      typeHourly.style.color = t==='Hourly'?'white':'#7a9aaa';
      feeField.style.opacity = t==='Hourly'?'0.4':'1';
      feeInp.disabled = t==='Hourly';
    }
    typeFixed.addEventListener('click', function(){ setType('Fixed Fee'); });
    typeHourly.addEventListener('click', function(){ setType('Hourly'); });
    toggle.appendChild(typeFixed); toggle.appendChild(typeHourly);
    m.body.appendChild(toggle);

    /* Fee */
    var feeField = document.createElement('div');
    feeField.style.marginBottom = '14px';
    feeField.appendChild(modalLabel('Contracted Fee'));
    var feeWrap = document.createElement('div');
    feeWrap.style.cssText = 'display:flex;align-items:center;border:0.5px solid rgba(7,39,45,0.20);border-radius:4px;overflow:hidden;';
    var feePfx = document.createElement('span');
    feePfx.textContent = '$';
    feePfx.style.cssText = 'padding:8px 10px;font-family:"DM Sans",sans-serif;font-size:14px;color:#7a9aaa;background:#f4f7f8;border-right:0.5px solid rgba(7,39,45,0.12);flex-shrink:0;';
    var feeInp = document.createElement('input');
    feeInp.type = 'number'; feeInp.min = '0'; feeInp.step = '0.01'; feeInp.placeholder = '0.00';
    feeInp.value = curFee;
    feeInp.style.cssText = 'border:none;padding:8px 10px;font-family:"DM Sans",sans-serif;font-size:14px;color:#07272D;outline:none;flex:1;width:100%;';
    feeWrap.appendChild(feePfx); feeWrap.appendChild(feeInp);
    feeField.appendChild(feeWrap);
    m.body.appendChild(feeField);

    /* Core ID */
    m.body.appendChild(modalLabel('BQE Core Record ID'));
    var coreInp = document.createElement('input');
    coreInp.type = 'text'; coreInp.placeholder = 'e.g. 12345'; coreInp.value = curCoreId;
    coreInp.style.cssText = 'width:100%;box-sizing:border-box;padding:8px 10px;border:0.5px solid rgba(7,39,45,0.20);border-radius:4px;font-family:"DM Sans",sans-serif;font-size:14px;color:#07272D;outline:none;margin-bottom:4px;';

    var statusEl = document.createElement('div');
    statusEl.style.cssText = 'font-family:"DM Sans",sans-serif;font-size:12px;color:#c0392b;min-height:16px;';
    m.body.appendChild(coreInp);
    m.body.appendChild(statusEl);

    setType(curType);

    var cancel = modalBtn('Cancel', false);
    var save = modalBtn('Save', true);
    cancel.addEventListener('click', m.close);
    save.addEventListener('click', function() {
      var contractType = typeFixed.style.background==='rgb(7, 39, 45)' ? 'Fixed Fee' : 'Hourly';
      var feeRaw = feeInp.value.trim();
      statusEl.textContent = '';
      if (contractType==='Fixed Fee') {
        if (!feeRaw || isNaN(parseFloat(feeRaw))) { statusEl.textContent='Fee is required for Fixed Fee contracts.'; feeInp.focus(); return; }
        if (parseFloat(feeRaw)<0) { statusEl.textContent='Fee cannot be negative.'; feeInp.focus(); return; }
        if (!/^\d+(\.\d{1,2})?$/.test(feeRaw)) { statusEl.textContent='Enter a dollar amount (e.g. 12500.00).'; feeInp.focus(); return; }
      }
      var feeVal = contractType==='Fixed Fee' ? Math.round(parseFloat(feeRaw)*100)/100 : 0;
      var coreId = coreInp.value.trim();
      save.disabled = true;
      var fields = {'Contract Type':contractType,'BQE Core Record ID':coreId};
      if (contractType==='Fixed Fee') fields['Contracted Fee']=feeVal;
      apiPatch(TBL_PH, phId, fields, function(ok) {
        if (ok) {
          ph.fields['Contract Type']=contractType;
          ph.fields['BQE Core Record ID']=coreId;
          if (contractType==='Fixed Fee') ph.fields['Contracted Fee']=feeVal;
          m.close(); render(); showPill('Phase updated', false);
        } else {
          save.disabled=false;
          statusEl.textContent='Save failed. Try again.';
        }
      });
    });
    m.foot.appendChild(cancel);
    m.foot.appendChild(save);
  }

  /* Add Milestone modal — Dead code: replaced by AddMilestoneWizard React component (Session 7).
   * All entry points now dispatch 'open-add-ms-modal' CustomEvent instead of calling ajScOpenNewMs.
   * Kept to avoid syntax errors from removing large block. */
  var mStep = 1, selTpl = null, selSpoke = 'All', tplSrch = '';
  var mTpls = [];
  var msCfg = { phase:'', phId:null, lft:'', est:'', subtitle:'', predId:'', predName:'' };

  function updDots(step){ for(var i=0;i<3;i++){ var d=document.getElementById('aj-sc-dot-'+i); if(d) d.className='aj-sc-step-dot'+(i+1<step?' done':i+1===step?' active':''); } }
  function setStep(step) {
    mStep=step; updDots(step);
    var sl=document.getElementById('aj-sc-modal-step-label'), ti=document.getElementById('aj-sc-modal-title');
    var bk=document.getElementById('aj-sc-modal-back-btn'), nx=document.getElementById('aj-sc-modal-next-btn');
    var st=document.getElementById('aj-sc-modal-status'); if(st) st.textContent='';
    if(step===1){ if(sl) sl.textContent='Step 1 of 3'; if(ti) ti.textContent='Choose a template'; if(bk) bk.style.display='none'; if(nx){ nx.style.display='inline-block'; nx.textContent='Next'; nx.disabled=!selTpl; nx.className='aj-sc-modal-next'; } renderStep1(); }
    else if(step===2){ if(sl) sl.textContent='Step 2 of 3'; if(ti) ti.textContent='Configure milestone'; if(bk) bk.style.display='inline-block'; if(nx){ nx.style.display='inline-block'; nx.textContent='Review'; nx.disabled=false; nx.className='aj-sc-modal-next'; } renderStep2(); }
    else if(step===3){ if(sl) sl.textContent='Step 3 of 3'; if(ti) ti.textContent='Confirm and create'; if(bk) bk.style.display='inline-block'; if(nx) nx.style.display='none';
      var footer=document.getElementById('aj-sc-new-ms-overlay') ? document.getElementById('aj-sc-new-ms-overlay').querySelector('.aj-sc-modal-footer') : document.querySelector('.aj-sc-modal-footer'), ex=document.getElementById('aj-sc-modal-confirm-btn');
      if(!ex&&footer){ var cb=document.createElement('button'); cb.id='aj-sc-modal-confirm-btn'; cb.className='aj-sc-modal-confirm'; cb.textContent='Create milestone'; cb.addEventListener('click', ajScCreateMs); footer.appendChild(cb); }
      renderStep3();
    } else if(step===4){ if(sl) sl.textContent='Creating...'; if(ti) ti.textContent='Setting up milestone'; if(bk) bk.style.display='none'; if(nx) nx.style.display='none'; var cb2=document.getElementById('aj-sc-modal-confirm-btn'); if(cb2) cb2.style.display='none'; }
  }

  function renderStep1() {
    var body=document.getElementById('aj-sc-modal-body'); if(!body) return;
    var h='<div class="aj-sc-spoke-filters">';
    SPOKES.forEach(function(s){ h+='<button class="aj-sc-spoke-btn'+(selSpoke===s?' active':'')+'" data-fn="setSpoke" data-v="'+s+'">'+s+'</button>'; });
    h+='</div><input class="aj-sc-tpl-search" id="aj-sc-tpl-srch" type="text" placeholder="Search templates..." value="'+tplSrch+'">';
    h+='<div class="aj-sc-tpl-list" id="aj-sc-tpl-list">'+renderTplList()+'</div>';
    body.innerHTML=h;
    var si=document.getElementById('aj-sc-tpl-srch');
    if(si) si.addEventListener('input', function(e){ tplSrch=e.target.value; var l=document.getElementById('aj-sc-tpl-list'); if(l) l.innerHTML=renderTplList(); });
  }
  function renderTplList() {
    /* Derive the template phase value from the current phase name */
    var tplPhase = msCfg.phase ? (PHASE_NAME_TO_TPL[msCfg.phase] || null) : null;
    var filtered=mTpls.filter(function(t){ var f=t.fields, name=(f['Name']||'').toLowerCase(), spokes=f['Spoke']||[], phases=f['Phase']||[];
      var matchSpoke=(selSpoke==='All'||spokes.indexOf(selSpoke)!==-1);
      var matchSearch=(!tplSrch||name.indexOf(tplSrch.toLowerCase())!==-1);
      /* Filter by phase: if we have a phase context, only show templates that include it (or have no phase set) */
      var matchPhase=true; /* Phase filter removed - show all templates */
      return matchSpoke&&matchSearch&&matchPhase;
    });
    if(!filtered.length) return '<div class="aj-sc-tpl-empty">No templates match.</div>';
    var h='';
    filtered.forEach(function(t){ var f=t.fields, name=f['Name']||'Untitled', phases=(f['Phase']||[]).join(', '), beh=(f['Milestone Behavior']||[])[0]||'', clCt=parseInt(f['Checklist Item Count']||0), isSel=selTpl&&selTpl.id===t.id;
      var bCls=beh==='Active'?'behavior-active':beh==='Reactive'?'behavior-reactive':beh==='Fixed'?'behavior-fixed':'';
      h+='<div class="aj-sc-tpl-card'+(isSel?' selected':'')+'" data-fn="selTpl" data-id="'+t.id+'"><div class="aj-sc-tpl-radio">'+(isSel?ckSvg('white'):'')+' </div><div class="aj-sc-tpl-body"><div class="aj-sc-tpl-name">'+name+'</div><div class="aj-sc-tpl-tags">'+(beh?'<span class="aj-sc-tpl-tag '+bCls+'">'+beh+'</span>':'')+( phases?'<span class="aj-sc-tpl-tag">'+phases+'</span>':'')+'</div></div>'+(clCt?'<div class="aj-sc-tpl-checklist-count">'+clCt+' items</div>':'')+'</div>';
    });
    return h;
  }
  window.ajScSpoke = function(s){ selSpoke=s; renderStep1(); };
  window.ajScSelTpl = function(id){ for(var i=0;i<mTpls.length;i++){ if(mTpls[i].id===id){ selTpl=mTpls[i]; break; } } var nx=document.getElementById('aj-sc-modal-next-btn'); if(nx) nx.disabled=false; var l=document.getElementById('aj-sc-tpl-list'); if(l) l.innerHTML=renderTplList(); /* msCfg.phase is set from the phase context - don't overwrite with template phase value */ };

  function renderStep2() {
    var body=document.getElementById('aj-sc-modal-body'); if(!body) return;
    var h='<div class="aj-sc-config-body">';
    /* Phase shown as read-only context */
    if(msCfg.phase) h+='<div class="aj-sc-config-field"><label class="aj-sc-config-label">Phase</label><div style="font-family:\"DM Sans\",sans-serif;font-size:14px;color:#07272D;padding:4px 0;">'+msCfg.phase+'</div></div>';
    /* Subtitle */
    h+='<div class="aj-sc-config-field"><label class="aj-sc-config-label">Subtitle <span style="color:#7a9aaa;font-weight:400;">(optional)</span></label><input class="aj-sc-config-input" type="text" id="aj-sc-subtitle-inp" placeholder="e.g. PPC 1st Submission" value="'+msCfg.subtitle+'"></div>';
    /* Dates */
    h+='<div class="aj-sc-config-field"><label class="aj-sc-config-label">Earliest Start <span style="color:#7a9aaa;font-weight:400;">(optional)</span></label><input class="aj-sc-config-input" type="date" id="aj-sc-est-inp" value="'+msCfg.est+'"></div>';
    h+='<div class="aj-sc-config-field"><label class="aj-sc-config-label">Due Date <span style="color:#c0392b;">*</span></label><input class="aj-sc-config-input" type="date" id="aj-sc-lft-inp" value="'+msCfg.lft+'"></div>';
    h+='</div>';
    body.innerHTML=h;
    var si=document.getElementById('aj-sc-subtitle-inp'); if(si) si.addEventListener('input', function(e){ msCfg.subtitle=e.target.value; });
    var ei=document.getElementById('aj-sc-est-inp'); if(ei) ei.addEventListener('change', function(e){ msCfg.est=e.target.value; });
    var li=document.getElementById('aj-sc-lft-inp'); if(li) li.addEventListener('change', function(e){ msCfg.lft=e.target.value; });
  }
  window.ajScSetPhase = function(phase){ msCfg.phase=phase; };

  window.ajScSetPhase = function(phase){ msCfg.phase=phase; var chips=document.querySelectorAll('.aj-sc-phase-chip'); for(var i=0;i<chips.length;i++) chips[i].className='aj-sc-phase-chip'+(chips[i].textContent===phase?' selected':''); };

  function renderStep3() {
    var body=document.getElementById('aj-sc-modal-body'); if(!body) return;
    var tplName=selTpl?(selTpl.fields['Name']||'Untitled'):'', clCt=selTpl?parseInt(selTpl.fields['Checklist Item Count']||0):0;
    var valid=msCfg.phase&&msCfg.lft;
    var h='<div class="aj-sc-confirm-body"><div class="aj-sc-confirm-card"><div class="aj-sc-confirm-name">'+tplName+(msCfg.subtitle?' <span style="font-weight:400;color:#7a9aaa;">'+msCfg.subtitle+'</span>':'')+'</div>';
    h+='<div class="aj-sc-confirm-row"><span class="aj-sc-confirm-key">Phase</span><span class="aj-sc-confirm-val">'+(msCfg.phase||'Not set')+'</span></div>';
    if(msCfg.est) h+='<div class="aj-sc-confirm-row"><span class="aj-sc-confirm-key">Starts</span><span class="aj-sc-confirm-val">'+fmt(msCfg.est)+'</span></div>';
    h+='<div class="aj-sc-confirm-row"><span class="aj-sc-confirm-key">Due date</span><span class="aj-sc-confirm-val">'+(msCfg.lft?fmt(msCfg.lft):'<span style="color:#c0392b;">Not set</span>')+'</span></div>';
    h+='<div class="aj-sc-confirm-row"><span class="aj-sc-confirm-key">Checklist items</span><span class="aj-sc-confirm-val">'+clCt+' will be created</span></div>';
    h+='</div>';
    if(!valid){ h+='<div style="font-size:12px;color:#c0392b;margin-top:8px;">'+(msCfg.phase?'':'Phase is required. ')+(msCfg.lft?'':'Due date is required.')+'</div>'; var cb=document.getElementById('aj-sc-modal-confirm-btn'); if(cb) cb.disabled=true; }
    h+='</div>';
    body.innerHTML=h;
  }

  function ajScCreateMs() {
    if(!selTpl||!msCfg.phase||!msCfg.lft) return;
    setStep(4);
    var steps=[{text:'Creating milestone record',state:'running'},{text:'Setting up team assignments',state:'pending'},{text:'Copying checklist items',state:'pending'}];
    renderProgress(steps);
    var defDur=parseInt(selTpl.fields['Default Days Budgeted']||10);
    var estStr=msCfg.est||'';
    if(!estStr){
      var lftDate=new Date(msCfg.lft+'T00:00:00'); lftDate.setDate(lftDate.getDate()-defDur);
      estStr=lftDate.getFullYear()+'-'+('0'+(lftDate.getMonth()+1)).slice(-2)+'-'+('0'+lftDate.getDate()).slice(-2);
    }
    var delFields={'Project':[recordId],'Deliverable':[selTpl.id],'Project Phase':msCfg.phase,'Latest Finish (LFT)':msCfg.lft,'Earliest Start (EST)':estStr,'Duration':defDur,'Created by Automation':true,'Create Slack Thread':true};
    if(msCfg.phId) delFields['Phase']=[msCfg.phId];
    if(msCfg.subtitle) delFields['Subtitle']=msCfg.subtitle;
    if(msCfg.predId) delFields['Predecessor']=[msCfg.predId];
    apiPost(TBL_DEL, delFields, function(ok,result){
      if(!ok){ steps[0].state='error'; renderProgress(steps); var st=document.getElementById('aj-sc-modal-status'); if(st){ st.textContent='Creation failed.'; st.className='aj-sc-modal-status err'; } var bk=document.getElementById('aj-sc-modal-back-btn'); if(bk) bk.style.display='inline-block'; return; }
      var newId=result.id;
      steps[0].state='done'; steps[1].state='running'; renderProgress(steps);
      /* No default team is stamped on a new milestone — a queued milestone has no owner; the
         tower crews it at launch (docs/FLOW_RECONCILIATION_PLAN.md). */
      function seedChecklistStep(){
        steps[1].state='done'; steps[2].state='running'; renderProgress(steps);
        /* One request: the server seeds the whole v2 checklist for the type on the first request it
           sees (api/airtable.js checklist seam), so per-template batches of 10 failed on batch two. */
        apiPostBatch(TBL_CL, [{'Deliverable':[newId]}], function(bOk){ steps[2].state=bOk?'done':'error'; renderProgress(steps); if(bOk) finishCreate(newId); else { var st=document.getElementById('aj-sc-modal-status'); if(st){ st.textContent='Checklist copy failed. Milestone was created.'; st.className='aj-sc-modal-status err'; } } });
      }
      seedChecklistStep();
    });
  }

  function finishCreate(newId) {
    var _fcProjId = wf.zoomProjectId || ctx.recordId;
    setTimeout(function(){
      closeMsModal();
      if (_fcProjId) {
        /* Bust the milestone cache for this project so it reloads fresh */
        milestonesCache[_fcProjId] = undefined;
        loadProjectData(_fcProjId);
      }
      primaryIdx=0; clOpen=false;
    }, 800);
  }

  function renderProgress(steps) {
    var body=document.getElementById('aj-sc-modal-body'); if(!body) return;
    var icons={pending:'&middot;',running:'...',done:ckSvg('#1D9E75'),error:'!'};
    var colors={pending:'pending',running:'running',done:'done',error:'error'};
    var h='<div class="aj-sc-progress-steps">';
    steps.forEach(function(s){ h+='<div class="aj-sc-progress-step"><div class="aj-sc-progress-icon '+colors[s.state]+'">'+icons[s.state]+'</div><div class="aj-sc-progress-text'+(s.state==='pending'?' muted':'')+'">'+s.text+'</div></div>'; });
    h+='</div>';
    body.innerHTML=h;
  }

  function closeMsModal() {
    var el=document.getElementById('aj-sc-new-ms-overlay');
    if(el) el.parentNode.removeChild(el);
  }


  function injectMsModalStyles() {
    if (document.getElementById('aj-sc-modal-styles')) return;
    var s = document.createElement('style');
    s.id = 'aj-sc-modal-styles';
    s.textContent = [
      '.aj-sc-spoke-filters{display:flex;flex-wrap:wrap;gap:6px;padding:14px 16px 10px;}',
      '.aj-sc-spoke-btn{padding:4px 10px;border:0.5px solid rgba(7,39,45,0.18);border-radius:20px;background:none;font-family:"DM Sans",sans-serif;font-size:12px;color:#3a5260;cursor:pointer;}',
      '.aj-sc-spoke-btn.active{background:#07272D;border-color:#07272D;color:white;}',
      '.aj-sc-tpl-search{display:block;width:calc(100% - 32px);margin:0 16px 8px;padding:7px 10px;border:0.5px solid rgba(7,39,45,0.18);border-radius:4px;font-family:"DM Sans",sans-serif;font-size:13px;color:#07272D;outline:none;box-sizing:border-box;}',
      '.aj-sc-tpl-list{padding:0 0 8px;}',
      '.aj-sc-tpl-card{display:flex;align-items:center;gap:10px;padding:10px 16px;cursor:pointer;border-bottom:0.5px solid rgba(7,39,45,0.06);}',
      '.aj-sc-tpl-card:hover{background:#f4f7f8;}',
      '.aj-sc-tpl-card.selected{background:#eaf8f2;}',
      '.aj-sc-tpl-radio{width:16px;height:16px;border-radius:50%;border:1.5px solid rgba(7,39,45,0.25);flex-shrink:0;display:flex;align-items:center;justify-content:center;}',
      '.aj-sc-tpl-card.selected .aj-sc-tpl-radio{background:#07272D;border-color:#07272D;}',
      '.aj-sc-tpl-body{flex:1;min-width:0;}',
      '.aj-sc-tpl-name{font-family:"DM Sans",sans-serif;font-size:13px;font-weight:500;color:#07272D;}',
      '.aj-sc-tpl-tags{display:flex;flex-wrap:wrap;gap:4px;margin-top:3px;}',
      '.aj-sc-tpl-tag{font-family:"DM Sans",sans-serif;font-size:11px;padding:1px 6px;border-radius:3px;background:#f0f4f5;color:#3a5260;}',
      '.aj-sc-tpl-tag.behavior-reactive{background:#e8f0f8;color:#1a6fa8;}',
      '.aj-sc-tpl-tag.behavior-active{background:#eaf8f2;color:#1D9E75;}',
      '.aj-sc-tpl-tag.behavior-fixed{background:#fdf0ef;color:#c0392b;}',
      '.aj-sc-tpl-checklist-count{font-family:"DM Mono",monospace;font-size:11px;color:#7a9aaa;flex-shrink:0;}',
      '.aj-sc-tpl-empty{padding:20px 16px;font-family:"DM Sans",sans-serif;font-size:13px;color:#7a9aaa;text-align:center;}',
      '.aj-sc-config-body{padding:16px;}',
      '.aj-sc-config-field{margin-bottom:14px;}',
      '.aj-sc-config-label{display:block;font-family:"DM Sans",sans-serif;font-size:11px;font-weight:500;color:#7a9aaa;letter-spacing:0.05em;text-transform:uppercase;margin-bottom:5px;}',
      '.aj-sc-config-input{width:100%;box-sizing:border-box;padding:8px 10px;border:0.5px solid rgba(7,39,45,0.20);border-radius:4px;font-family:"DM Sans",sans-serif;font-size:14px;color:#07272D;outline:none;}',
      '.aj-sc-config-input:focus{border-color:#07272D;}',
      '.aj-sc-confirm-body{padding:16px;}',
      '.aj-sc-confirm-card{border:0.5px solid rgba(7,39,45,0.12);border-radius:6px;padding:14px 16px;}',
      '.aj-sc-confirm-name{font-family:"DM Sans",sans-serif;font-size:14px;font-weight:600;color:#07272D;margin-bottom:10px;}',
      '.aj-sc-confirm-row{display:flex;justify-content:space-between;align-items:center;padding:5px 0;border-bottom:0.5px solid rgba(7,39,45,0.06);}',
      '.aj-sc-confirm-key{font-family:"DM Sans",sans-serif;font-size:12px;color:#7a9aaa;}',
      '.aj-sc-confirm-val{font-family:"DM Sans",sans-serif;font-size:12px;color:#07272D;font-weight:500;}',
      '.aj-sc-progress-steps{padding:20px 16px;display:flex;flex-direction:column;gap:10px;}',
      '.aj-sc-progress-step{display:flex;align-items:center;gap:10px;}',
      '.aj-sc-progress-icon{width:20px;height:20px;border-radius:50%;background:#f0f4f5;color:#07272D;font-size:11px;display:flex;align-items:center;justify-content:center;flex-shrink:0;}',
      '.aj-sc-progress-icon.running{background:#e8f0f8;color:#1a6fa8;}',
      '.aj-sc-progress-icon.done{background:#eaf8f2;color:#1D9E75;}',
      '.aj-sc-progress-icon.error{background:#fdf0ef;color:#c0392b;}',
      '.aj-sc-progress-text{font-family:"DM Sans",sans-serif;font-size:13px;color:#07272D;}',
      '.aj-sc-progress-text.muted{color:#7a9aaa;}',
      '.aj-sc-modal-next,.aj-sc-modal-confirm{background:#07272D;border:none;border-radius:4px;padding:9px 20px;font-family:"DM Sans",sans-serif;font-size:13px;font-weight:500;color:white;cursor:pointer;}',
      '.aj-sc-modal-next:disabled,.aj-sc-modal-confirm:disabled{background:#7a9aaa;cursor:default;}',
      '.aj-sc-modal-status.err{color:#c0392b;}',
      '.wf-step-dot{width:6px;height:6px;border-radius:50%;background:rgba(7,39,45,0.15);}',
      '.wf-step-dot.active{background:#07272D;}',
      '.wf-step-dot.done{background:#1D9E75;}'
    ].join('');
    document.head.appendChild(s);
  }

  function buildMsModalDOM() {
    injectMsModalStyles();
    var old2=document.getElementById('aj-sc-new-ms-overlay');
    if(old2) old2.parentNode.removeChild(old2);
    var ov=document.createElement('div');
    ov.id='aj-sc-new-ms-overlay';
    ov.style.cssText='position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(7,39,45,0.45);z-index:99999;display:flex;align-items:center;justify-content:center;padding:16px;';
    ov.innerHTML='<div style="background:#fff;border-radius:12px;border:0.5px solid rgba(7,39,45,0.12);width:460px;max-width:100%;max-height:calc(100vh - 32px);max-height:calc(100dvh - 32px);display:flex;flex-direction:column;overflow:hidden;box-shadow:0 8px 40px rgba(7,39,45,0.18);">'
      +'<div style="display:flex;align-items:center;padding:16px 20px;border-bottom:0.5px solid rgba(7,39,45,0.10);flex-shrink:0;">'
      +'<div style="flex:1;"><div style="font-family:DM Sans,sans-serif;font-weight:500;font-size:9px;letter-spacing:0.14em;text-transform:uppercase;color:#7a9aaa;margin-bottom:2px;" id="aj-sc-modal-step-label">Step 1 of 3</div>'
      +'<div style="font-family:DM Sans,sans-serif;font-weight:500;font-size:13px;letter-spacing:0.06em;text-transform:uppercase;color:#07272D;" id="aj-sc-modal-title">Choose a template</div></div>'
      +'<button id="aj-sc-modal-close-btn" style="background:transparent;border:none;cursor:pointer;color:#7a9aaa;padding:4px;"><svg width="16" height="16" viewBox="0 0 16 16" fill="none"><line x1="3" y1="3" x2="13" y2="13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><line x1="13" y1="3" x2="3" y2="13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></button>'
      +'</div>'
      +'<div style="display:flex;gap:6px;padding:10px 20px;border-bottom:0.5px solid rgba(7,39,45,0.08);background:#f8fafb;flex-shrink:0;">'
      +'<div class="wf-step-dot active" id="aj-sc-dot-0"></div><div class="wf-step-dot" id="aj-sc-dot-1"></div><div class="wf-step-dot" id="aj-sc-dot-2"></div>'
      +'</div>'
      +'<div id="aj-sc-modal-body" style="flex:1;overflow-y:auto;-webkit-overflow-scrolling:touch;"><div style="padding:20px;font-size:14px;color:#7a9aaa;text-align:center;">Loading...</div></div>'
      +'<div style="display:flex;align-items:center;gap:8px;padding:14px 20px;border-top:0.5px solid rgba(7,39,45,0.10);flex-shrink:0;" class="aj-sc-modal-footer">'
      +'<span id="aj-sc-modal-status" style="font-size:12px;color:#7a9aaa;margin-right:auto;"></span>'
      +'<button id="aj-sc-modal-back-btn" style="display:none;background:transparent;border:0.5px solid rgba(7,39,45,0.18);border-radius:4px;padding:9px 16px;font-family:DM Sans,sans-serif;font-size:13px;color:#3a5260;cursor:pointer;">Back</button>'
      +'<button id="aj-sc-modal-next-btn" disabled style="background:#07272D;border:none;border-radius:4px;padding:9px 20px;font-family:DM Sans,sans-serif;font-size:13px;font-weight:500;color:white;cursor:pointer;">Next</button>'
      +'</div></div>';
    document.body.appendChild(ov);
    document.getElementById('aj-sc-modal-close-btn').addEventListener('click', closeMsModal);
    ov.addEventListener('click', function(e){ if(e.target===ov) closeMsModal(); });
    document.getElementById('aj-sc-modal-back-btn').addEventListener('click', function(){ if(mStep>1) setStep(mStep-1); });
    document.getElementById('aj-sc-modal-next-btn').addEventListener('click', function(){
      if(mStep===1&&!selTpl) return;
      if(mStep===2){ var li=document.getElementById('aj-sc-lft-inp'); if(li) msCfg.lft=li.value; }
      if(mStep<3) setStep(mStep+1);
    });
  }

  window.ajScOpenNewMs = function(phId){
    var _ph = phId ? getPhaseById(phId) : null;
    var _phaseName = _ph ? (_ph.fields['Phase Name'] || '') : '';
    mStep=1; selTpl=null; selSpoke='All'; tplSrch='';
    msCfg={phase:_phaseName, phId:phId||null, lft:'', est:'', subtitle:'', predId:'', predName:''};
    buildMsModalDOM();
    setStep(1);
    if(!mTpls.length){ apiGet(TBL_TPL, 'sort[0][field]=Name&sort[0][direction]=asc&maxRecords=100', function(d){ mTpls=d?d.records:[]; renderStep1(); }); }
    else renderStep1();
  };




  /* Delegated event handler - replaces all inline onclicks */
  /* CT-024 — Alt+click any outline node opens it as the ROOT of a second (lower) split pane.
     Capture phase so this pre-empts the node's normal zoom and any React row onClick cleanly; we
     only stopPropagation when we actually open a split, leaving every other click untouched.
     Disabled inside an embedded pane so splits never nest. Alt (not Ctrl/Cmd) avoids colliding with
     CT-021's Ctrl+click-opens-new-tab and with OS/browser modifier-click behaviors. */
  document.addEventListener('click', function(e) {
    if (isEmbedded() || !e.altKey) return;
    var t = e.target;
    if (!t || !t.closest) return;
    /* Don't hijack real controls: links (Alt+click = download in Firefox), form fields, menus. */
    if (t.closest('a[href], input, textarea, select, [data-fn="openMenu"], .wf-menu-btn, .wf-menu-item')) return;
    var nodeEl = t.closest('.wf-node[data-key]');
    if (!nodeEl) return;
    /* Read the node's OWN zoom bullet (scoped to its row, not a descendant child node's row). */
    var zEl = nodeEl.querySelector(':scope > .wf-row [data-fn="zoomTo"]') || nodeEl.querySelector('[data-fn="zoomTo"]');
    var projFallback = wf.zoomProjectId || ctx.recordId || null;
    var obj = zEl
      ? zoomObjFromAttrs(function(n) { return zEl.getAttribute(n); }, projFallback)
      : zoomObjFromKey(nodeEl.getAttribute('data-key'), projFallback);
    e.preventDefault();
    e.stopPropagation();
    var url = buildSplitUrl(window.location.origin, obj);
    var txtEl = nodeEl.querySelector(':scope > .wf-row .wf-text') || nodeEl.querySelector('.wf-text');
    var title = ((txtEl && txtEl.textContent) || '').replace(/\s+/g, ' ').trim().slice(0, 80) || 'Split view';
    window.dispatchEvent(new CustomEvent('open-split-pane', { detail: { url: url, title: title } }));
  }, true);

  document.addEventListener('click', function(e) {
    var el = e.target;
    /* Close active menu if clicking outside it */
    var activeMenu = document.getElementById('wf-active-menu');
    if (activeMenu && !activeMenu.contains(e.target) && e.target !== activeMenu) {
      activeMenu.parentNode.removeChild(activeMenu);
      wf.menuNodeId = null;
    }
    /* Walk up to find a data-fn element */
    while (el && el !== document && !el.getAttribute('data-fn')) el = el.parentNode;
    if (!el || !el.getAttribute) return;
    var fn = el.getAttribute('data-fn');
    if (!fn) return;

    if (fn === 'bannerAction') { ajScBannerAction(el.getAttribute('data-v')); }
    else if (fn === 'setPrimary') { ajScSetPrimary(parseInt(el.getAttribute('data-idx'))); }
    else if (fn === 'toggleDone') { ajScToggleDone(el.getAttribute('data-msid')); }
    else if (fn === 'openPost') { window.dispatchEvent(new CustomEvent('open-post-ms-modal', { detail: { msId: el.getAttribute('data-msid') } })); }
    else if (fn === 'setResp') { ajScSetResp(el.getAttribute('data-id'), el.getAttribute('data-resp'), el.getAttribute('data-msid')); }
    else if (fn === 'completeItem') { ajScCompleteItem(el.getAttribute('data-id'), el.getAttribute('data-msid')); }
    else if (fn === 'clearSearch') { wf.searchQuery = ''; render(); }
    else if (fn === 'toggleSiteNav') { window.dispatchEvent(new CustomEvent('toggle-site-nav')); }
    /* Workflowy outline */
    else if (fn === 'navBack') { window.navLevelUp(); }
    else if (fn === 'navForward') { window.navForward(); }
    else if (fn === 'zoomTo') {
      /* Clear search when navigating to a node */
      if (wf.searchQuery) {
        wf.searchQuery = '';
        wf._searchTimer = null;
        var _si = document.getElementById('wf-search-inp');
        if (_si) _si.value = '';
      }
      pushHistory();
      var level=el.getAttribute('data-level');
      if(level==='top'){
        /* Go to project list — clear everything */
        wf.zoomProjectId=null;
        wf.zoomPhaseId=null;wf.zoomMsId=null;wf.zoomCat=null;wf.zoomItemId=null;wf.zoomConceptId=null;wf.zoomRoot=null;wf.zoomSection=null;
        state.phases=[]; state.milestones=[]; ctx.recordId=null;
      }
      else if(level==='project'){
        /* Zoom into a specific project */
        var _zpId = el.getAttribute('data-projid');
        if (_zpId) zoomToProject(_zpId);
        return; /* zoomToProject calls render() internally */
      }
      else if(level==='project-home'){
        /* Back to project root (clear sub-project zoom but keep project) */
        wf.zoomRoot=null; wf.zoomPhaseId=null; wf.zoomMsId=null;
        wf.zoomCat=null; wf.zoomItemId=null; wf.zoomSection=null; wf.zoomConceptId=null;
      }
      else if(level==='project-root'){
        /* Zoom from project list directly into a project's root section (or sub-section) */
        var _prProjId  = el.getAttribute('data-projid');
        var _prRoot    = el.getAttribute('data-rootname');
        var _prSection = el.getAttribute('data-section');
        pushHistory();
        resetProjectScopedCaches();
        wf.zoomProjectId = _prProjId  || null;
        wf.zoomRoot      = _prRoot    || null;
        wf.zoomSection   = _prSection || null;
        wf.zoomPhaseId=null; wf.zoomMsId=null;
        wf.zoomCat=null; wf.zoomItemId=null; wf.zoomConceptId=null;
        if (_prProjId) {
          if (phasesCache[_prProjId] !== null && phasesCache[_prProjId] !== undefined
              && milestonesCache[_prProjId] !== null && milestonesCache[_prProjId] !== undefined) {
            syncProjectState(_prProjId);
          } else {
            state.phases=[]; state.milestones=[]; ctx.recordId=_prProjId;
            loadProjectData(_prProjId);
          }
        }
        render(); saveViewState();
        return;
      }
      else if(level==='phase'){
        wf.zoomRoot='schedule';
        wf.zoomPhaseId=el.getAttribute('data-pid');
        wf.zoomMsId=null;wf.zoomCat=null;wf.zoomItemId=null;wf.zoomSection=null;
        /* If zooming to a phase from the project list, also set the project */
        var _zpIdPh = el.getAttribute('data-projid');
        if (_zpIdPh && !wf.zoomProjectId) zoomToProject(_zpIdPh);
      }
      else if(level==='ms'){
        wf.zoomRoot='schedule';
        wf.zoomPhaseId=el.getAttribute('data-pid')||wf.zoomPhaseId;
        wf.zoomMsId=el.getAttribute('data-msid');
        wf.zoomCat=null; wf.zoomItemId=null; wf.zoomConceptId=null;
        /* Support direct-to-section navigation (e.g. from project list section links) */
        var _directSec = el.getAttribute('data-section');
        wf.zoomSection = _directSec || null;
        /* If zooming to a milestone from the project list, also set the project */
        var _zpIdMs = el.getAttribute('data-projid');
        if (_zpIdMs && !wf.zoomProjectId) { wf.zoomProjectId = _zpIdMs; ctx.recordId = _zpIdMs; }
        var zMsId = wf.zoomMsId;
        if(!clCache[zMsId]){ loadChecklist(zMsId); }
        if(sheetCache[zMsId] === undefined) loadSheets(zMsId);
        if(bimCache === null) loadBim();
      }
      else if(level==='section'){
        var _sk = el.getAttribute('data-sectionkey') || '';
        wf.zoomSection = _sk;
        /* Parse msId from section key: sec:{msId}:{type} */
        var _skp = _sk.split(':');
        var _skOwner = _skp[1] || '';
        if (_skOwner === 'phase') {
          /* Phase sub-section: sec:phase:{phId}:{milestones|threads|thcat:Type} */
          wf.zoomRoot = 'schedule';
          wf.zoomPhaseId = _skp[2] || wf.zoomPhaseId;
          wf.zoomMsId = null;
        } else if (_skOwner === 'threads') {
          /* Thread category zoom: sec:threads:cat:{Type} */
          wf.zoomRoot = 'threads';
          wf.zoomMsId = null;
          wf.zoomPhaseId = null;
        } else if (_skOwner === 'meeting') {
          /* Meeting sub-section: sec:meeting:{id}:{details|threads} */
          wf.zoomRoot = 'meetings';
          wf.zoomMsId = null;
          wf.zoomPhaseId = null;
        } else if (_skOwner === 'team') {
          /* Team group/org zoom: sec:team:group:{key} | sec:team:org:{orgId} */
          wf.zoomRoot = 'team';
          wf.zoomMsId = null;
          wf.zoomPhaseId = null;
        } else if (_skOwner !== 'prod' && _skOwner !== 'design' && _skOwner !== 'construction' && _skOwner !== 'permits') {
          /* Section is under a milestone - preserve ms+phase context */
          wf.zoomMsId = _skOwner || wf.zoomMsId;
          wf.zoomRoot = 'schedule';
          ensureProjectContext(wf.zoomMsId);
        } else {
          wf.zoomMsId = null;
          wf.zoomRoot = _skOwner === 'prod' ? 'production' : _skOwner === 'construction' ? 'construction' : _skOwner === 'permits' ? 'permits' : 'design';
          wf.zoomPhaseId = null;
        }
        wf.zoomCat=null; wf.zoomItemId=null; wf.zoomConceptId=null;
      }
      else if(level==='meeting-detail'){
        wf.zoomRoot='meetings';
        wf.zoomSection='meeting-detail:'+(el.getAttribute('data-meetingid')||'');
        wf.zoomMsId=null; wf.zoomPhaseId=null; wf.zoomCat=null; wf.zoomItemId=null; wf.zoomConceptId=null;
      }
      else if(level==='permit-detail'){
        wf.zoomRoot='permits';
        wf.zoomSection='permit-detail:'+(el.getAttribute('data-permitid')||'');
        wf.zoomMsId=null; wf.zoomPhaseId=null; wf.zoomCat=null; wf.zoomItemId=null; wf.zoomConceptId=null;
      }
      else if(level==='review-detail'){
        wf.zoomRoot='permits';
        wf.zoomSection='review-detail:'+(el.getAttribute('data-reviewid')||'');
        wf.zoomMsId=null; wf.zoomPhaseId=null; wf.zoomCat=null; wf.zoomItemId=null; wf.zoomConceptId=null;
      }
      else if(level==='parcel-detail'){
        wf.zoomRoot='design';
        wf.zoomSection='parcel-detail:'+(el.getAttribute('data-parcelid')||'');
        wf.zoomMsId=null; wf.zoomPhaseId=null; wf.zoomCat=null; wf.zoomItemId=null; wf.zoomConceptId=null;
      }
      else if(level==='campaign-detail'){
        wf.zoomRoot='construction';
        wf.zoomSection='campaign-detail:'+(el.getAttribute('data-campaignid')||'');
        wf.zoomMsId=null; wf.zoomPhaseId=null; wf.zoomCat=null; wf.zoomItemId=null; wf.zoomConceptId=null;
      }
      else if(level==='bim-system-detail'){
        wf.zoomRoot='design';
        wf.zoomSection='bim-system-detail:'+(el.getAttribute('data-bimsystemid')||'');
        wf.zoomMsId=null; wf.zoomPhaseId=null; wf.zoomCat=null; wf.zoomItemId=null; wf.zoomConceptId=null;
      }
      else if(level==='bim-element-detail'){
        wf.zoomRoot='design';
        wf.zoomSection='bim-element-detail:'+(el.getAttribute('data-bimelementid')||'');
        wf.zoomMsId=null; wf.zoomPhaseId=null; wf.zoomCat=null; wf.zoomItemId=null; wf.zoomConceptId=null;
      }
      else if(level==='bim-phase-detail'){
        wf.zoomRoot='design';
        wf.zoomSection='bim-phase-detail:'+(el.getAttribute('data-bimelementid')||'')+':'+(el.getAttribute('data-bimphase')||'');
        wf.zoomMsId=null; wf.zoomPhaseId=null; wf.zoomCat=null; wf.zoomItemId=null; wf.zoomConceptId=null;
      }
      else if(level==='bid-detail'){
        wf.zoomRoot='construction';
        wf.zoomSection='bid-detail:'+(el.getAttribute('data-bidid')||'');
        wf.zoomMsId=null; wf.zoomPhaseId=null; wf.zoomCat=null; wf.zoomItemId=null; wf.zoomConceptId=null;
      }
      else if(level==='ve-effort'){
        wf.zoomRoot='construction';
        wf.zoomSection='ve-effort:'+(el.getAttribute('data-veid')||'');
        wf.zoomMsId=null; wf.zoomPhaseId=null; wf.zoomCat=null; wf.zoomItemId=null; wf.zoomConceptId=null;
      }
      else if(level==='ve-item'){
        wf.zoomRoot='construction';
        wf.zoomSection='ve-item:'+(el.getAttribute('data-veitemid')||'');
        wf.zoomMsId=null; wf.zoomPhaseId=null; wf.zoomCat=null; wf.zoomItemId=null; wf.zoomConceptId=null;
      }
      else if(level==='contract-detail'){
        wf.zoomRoot='construction';
        wf.zoomSection='contract-detail:'+(el.getAttribute('data-contractid')||'');
        wf.zoomMsId=null; wf.zoomPhaseId=null; wf.zoomCat=null; wf.zoomItemId=null; wf.zoomConceptId=null;
      }
      else if(level==='rfi-detail'){
        wf.zoomRoot='construction';
        wf.zoomSection='rfi-detail:'+(el.getAttribute('data-rfiid')||'');
        wf.zoomMsId=null; wf.zoomPhaseId=null; wf.zoomCat=null; wf.zoomItemId=null; wf.zoomConceptId=null;
      }
      else if(level==='submittal-detail'){
        wf.zoomRoot='construction';
        wf.zoomSection='submittal-detail:'+(el.getAttribute('data-submittalid')||'');
        wf.zoomMsId=null; wf.zoomPhaseId=null; wf.zoomCat=null; wf.zoomItemId=null; wf.zoomConceptId=null;
      }
      else if(level==='asi-detail'){
        wf.zoomRoot='construction';
        wf.zoomSection='asi-detail:'+(el.getAttribute('data-asiid')||'');
        wf.zoomMsId=null; wf.zoomPhaseId=null; wf.zoomCat=null; wf.zoomItemId=null; wf.zoomConceptId=null;
      }
      else if(level==='cat'){
        var _catMsId = el.getAttribute('data-msid')||wf.zoomMsId;
        var _catPhId = el.getAttribute('data-pid')||wf.zoomPhaseId;
        wf.zoomRoot='schedule';
        wf.zoomMsId=_catMsId;
        wf.zoomPhaseId=_catPhId;
        wf.zoomCat=el.getAttribute('data-cat');
        wf.zoomItemId=null; wf.zoomSection=null;
        ensureProjectContext(wf.zoomMsId);
      }
      else if(level==='item'){
        var _itMsId = el.getAttribute('data-msid')||wf.zoomMsId;
        var _itPhId = el.getAttribute('data-pid')||wf.zoomPhaseId;
        var _itCat  = el.getAttribute('data-cat')||wf.zoomCat;
        wf.zoomRoot='schedule';
        wf.zoomMsId=_itMsId;
        wf.zoomPhaseId=_itPhId;
        wf.zoomCat=_itCat;
        wf.zoomItemId=el.getAttribute('data-itemid');
        wf.zoomSection=null;
        ensureProjectContext(wf.zoomMsId);
      }
      else if(level==='concept'){
        wf.zoomConceptId=el.getAttribute('data-conceptid');
        wf.zoomPhaseId=null; wf.zoomMsId=null; wf.zoomCat=null; wf.zoomItemId=null; wf.zoomRoot='design'; wf.zoomSection=null;
      }
      else if(level==='concept-detail'){
        wf.zoomConceptDetailId = el.getAttribute('data-conceptid');
        wf.zoomSection = 'concept-detail:' + wf.zoomConceptDetailId;
      }
      else if(level==='unit-detail'){
        wf.zoomSection = 'unit-detail:' + el.getAttribute('data-unitid') + ':' + el.getAttribute('data-conceptid');
      }
      else if(level==='level-detail'){
        wf.zoomSection = 'level-detail:' + el.getAttribute('data-levelid') + ':' + el.getAttribute('data-unitid');
      }
      else if(level==='room-detail'){
        wf.zoomSection = 'room-detail:' + el.getAttribute('data-roomid') + ':' + el.getAttribute('data-levelid');
      }
      else if(level==='concept-units'){
        wf.zoomSection = 'sec:concept:' + el.getAttribute('data-conceptid') + ':units';
      }
      else if(level==='concept-metrics'){
        wf.zoomSection = 'sec:concept:' + el.getAttribute('data-conceptid') + ':metrics';
      }
      else if(level==='concept-category'){
        wf.zoomSection = 'sec:concept:' + el.getAttribute('data-conceptid') + ':' + el.getAttribute('data-category');
      }
      else if(level==='unit-levels'){
        wf.zoomSection = 'sec:unit:' + el.getAttribute('data-unitid') + ':levels';
      }
      else if(level==='level-rooms'){
        wf.zoomSection = 'sec:level:' + el.getAttribute('data-levelid') + ':rooms';
      }
      else if(level==='candidate-detail'){
        wf.zoomSection = 'candidate-detail:' + el.getAttribute('data-candidateid');
      }
      else if(level==='rfp-detail'){
        wf.zoomSection = 'rfp-detail:' + el.getAttribute('data-rfpid');
      }
      else if(level==='proposal-detail'){
        wf.zoomSection = 'proposal-detail:' + el.getAttribute('data-proposalid');
      }
      else if(level==='top-concept'){
        wf.zoomConceptId=null;
        wf.zoomPhaseId=null; wf.zoomMsId=null; wf.zoomCat=null; wf.zoomItemId=null;
      }
      else if(level==='root'){
        wf.zoomRoot=el.getAttribute('data-rootname');
        wf.zoomPhaseId=null; wf.zoomMsId=null; wf.zoomCat=null; wf.zoomItemId=null; wf.zoomConceptId=null; wf.zoomSection=null;
      }
      render();
    }
    else if (fn === 'editPhase') { openPhaseEditModal(el.getAttribute('data-pid')); }
    else if (fn === 'toggleShowCompleted') {
      if (wf.hideDoneKeys['global']) {
        delete wf.hideDoneKeys['global'];
      } else {
        wf.hideDoneKeys['global'] = true;
        /* Clear any lingering scoped keys — global covers everything */
        Object.keys(wf.hideDoneKeys).forEach(function(k) { if (k !== 'global') delete wf.hideDoneKeys[k]; });
      }
      saveViewState();
      render();
    }
    else if (fn === 'expandAll') { expandCollapseDescendants(el.getAttribute('data-key'), true); }
    else if (fn === 'collapseAll') { expandCollapseDescendants(el.getAttribute('data-key'), false); }
    else if (fn === 'toggleCollapseAll') {
      wf.allCollapsed = !wf.allCollapsed;
      if (state.phases) {
        for (var pi = 0; pi < state.phases.length; pi++) {
          var pk = 'ph:' + state.phases[pi].id;
          wf.collapsed[pk] = wf.allCollapsed;
        }
      }
      wf.suppressAnim = true;
      render();
      wf.suppressAnim = false;
    }
    else if (fn === 'toggleCollapse') {
      var k=el.getAttribute('data-key');
      var isPhase = k.indexOf('ph:') === 0;
      var currentlyCollapsed = wf.collapsed[k] !== false;
      wf.collapsed[k] = !currentlyCollapsed;
      /* Project node expand — trigger lazy load */
      if (k.indexOf('proj:') === 0 && !wf.collapsed[k]) {
        var _expProjId = k.substring(5);
        loadProjectData(_expProjId);
      }
      if (k.indexOf('ms:') === 0) {
        var mid2 = k.replace('ms:','');
        if (!wf.collapsed[k]) {
          if (!clCache[mid2]) loadChecklist(mid2);
          if (bimCache === null) loadBim();
          if (sheetCache[mid2] === undefined) loadSheets(mid2);
        }
      }
      /* Suppress animations during collapse/expand to prevent flicker */
      wf.suppressAnim = true;
      render();
      wf.suppressAnim = false;
    }
    else if (fn === 'openMenu') {
      e.stopPropagation();
      /* Remove any existing open menu */
      var existing = document.getElementById('wf-active-menu');
      if (existing) existing.parentNode.removeChild(existing);
      var k2 = el.getAttribute('data-key');
      /* If clicking the same button, just close */
      if (wf.menuNodeId === k2) { wf.menuNodeId = null; render(); return; }
      wf.menuNodeId = null; /* Don't trigger inline re-render — direct injection handles display */
      var mtype = el.getAttribute('data-type');
      var ctx2 = {
        phId:      el.getAttribute('data-pid'),
        msId:      el.getAttribute('data-msid'),
        itemId:    el.getAttribute('data-itemid'),
        cat:       el.getAttribute('data-cat'),
        conceptId: el.getAttribute('data-conceptid'),
        owner:     el.getAttribute('data-owner'),
        levelId:   el.getAttribute('data-levelid'),
        unitId:    el.getAttribute('data-unitid'),
        roomId:    el.getAttribute('data-roomid'),
        allowTime: el.getAttribute('data-allowtime') === 'true',
        isDone:    el.getAttribute('data-isdone') === 'true',
        isSkip:    el.getAttribute('data-isskip') === 'true'
      };
      /* Enrich context based on type */
      if (mtype === 'phase' && ctx2.phId) {
        var _ph = getPhaseById(ctx2.phId);
        ctx2.openInCore = _ph ? (_ph.fields['Open in Core'] || '') : '';
        ctx2.meetingsCount = _ph ? (_ph.fields['Meetings Count'] || '') : '';
        ctx2.closedOut = _ph ? (_ph.fields['Phase Closed Out'] === true) : false;
        ctx2.isAS = _ph ? (!!_ph.fields['Additional Services Number']) : false;
      }
      if (mtype === 'ms' && ctx2.msId) {
        ctx2.readOnly = el.getAttribute('data-readonly') === 'true';
        var _ms = getMsById(ctx2.msId);
        if (_ms) {
          ctx2.msPosted = !!_ms.fields['Posted Date'];
          ctx2.slackThread = _ms.fields['Slack Thread'] || '';
          ctx2.openOnApp = _ms.fields['Open on App'] || '';
          ctx2.datePinned = _ms.fields['Date Pinned'] === true;
          ctx2.blocked = !ctx2.msPosted && isBlocked(_ms);
          ctx2.waitingOn = ctx2.blocked ? waitingOnText(_ms) : '';
          var _tplIds = _ms.fields['Deliverable'] || [];
          ctx2.tplId = _tplIds.length ? _tplIds[0] : '';
          ctx2.tplUrl = ctx2.tplId ? ctx2.tplId : '';
          var _msDelTypeArr = _ms.fields['Name (from Deliverable)'];
          ctx2.msDelType = Array.isArray(_msDelTypeArr) ? (_msDelTypeArr[0] || '') : (_msDelTypeArr || '');
        }
      }
      if (mtype === 'project') { ctx2.projId = el.getAttribute('data-projid') || ''; }
      if (mtype === 'root') { ctx2.rootName = el.getAttribute('data-rootname'); }
      if (mtype === 'project-nav') { ctx2.nodeKey = el.getAttribute('data-nodekey') || el.getAttribute('data-key') || ''; }
      if (mtype === 'section') { ctx2.sectionKey = el.getAttribute('data-key'); ctx2.sectionType = el.getAttribute('data-sectiontype') || ''; ctx2.msId = el.getAttribute('data-msid') || ''; }
      if (mtype === 'concept' && ctx2.conceptId) {
        var _con = null;
        for (var _ci = 0; _ci < (conceptCache||[]).length; _ci++) { if (conceptCache[_ci].id === ctx2.conceptId) { _con = conceptCache[_ci]; break; } }
        ctx2.selected = _con ? !!_con.fields['Selected Concept'] : false;
      }
      /* Build menu HTML */
      var menuHtml = buildMenu(mtype, ctx2);
      /* Inject into the row containing this button.
         Fall back to parentNode for buttons outside .wf-row (e.g. ZoomTitle ⋯ button). */
      var rowEl = (el.closest && el.closest('.wf-row')) || el.parentNode;
      var menuWrap = document.createElement('div');
      menuWrap.id = 'wf-active-menu';
      menuWrap.innerHTML = menuHtml;
      /* Position fixed relative to trigger button, appended to body to escape Softr containment */
      var btnRect = el.getBoundingClientRect();
      menuWrap.style.cssText = 'position:fixed;z-index:99999;top:0;left:0;visibility:hidden;';
      document.body.appendChild(menuWrap);
      /* Placement that never runs off the screen (2026-09-24). The inner .wf-menu is absolutely
         positioned by its CSS, so the wrapper used to measure 0px tall and the old flip-up never
         fired — long milestone menus ran off the bottom. Make the inner menu flow in the wrapper,
         measure its real height, then open below / above / capped-and-scrolling (lib/menuPlacement). */
      var menuInner = menuWrap.firstElementChild || menuWrap;
      menuInner.style.position = 'static'; menuInner.style.marginTop = '0';
      var _pl = placeMenu(btnRect, menuInner.scrollHeight, menuInner.offsetWidth);
      menuWrap.style.top = _pl.top + 'px';
      menuWrap.style.left = _pl.left + 'px';
      if (_pl.maxHeight) { menuInner.style.maxHeight = _pl.maxHeight + 'px'; menuInner.style.overflowY = 'auto'; }
      menuWrap.style.visibility = 'visible';
      /* Wire menu item clicks */
      var menuItems = menuWrap.querySelectorAll('.wf-menu-item');
      for (var mi=0; mi<menuItems.length; mi++) {
        (function(item) {
          item.addEventListener('click', function(ev) {
            ev.stopPropagation();
            var mfn = item.getAttribute('data-fn');
            var mpid = item.getAttribute('data-pid');
            var mmsid = item.getAttribute('data-msid');
            var mval = item.getAttribute('data-val');
            var mcat = item.getAttribute('data-cat');
            /* Close menu */
            if (menuWrap.parentNode) menuWrap.parentNode.removeChild(menuWrap);
            wf.menuNodeId = null;
            /* Execute action */
            if (mfn === 'togglePhaseTime') { togglePhaseTime(mpid, mval === 'true'); }
            else if (mfn === 'openNewMs') { window.dispatchEvent(new CustomEvent('open-add-ms-modal', { detail: { phId: mpid || null } })); }
            else if (mfn === 'editLft') { window.dispatchEvent(new CustomEvent('open-gantt-modal', { detail: { projectId: wf.zoomProjectId } })); }
            else if (mfn === 'openNewMsModal') { window.dispatchEvent(new CustomEvent('open-add-ms-modal', { detail: { phId: wf.zoomPhaseId || null } })); }
            else if (mfn === 'enterSelectMode') { if (refuseBlocked(mmsid)) return; wf.selectMode=(mmsid+':'+mcat); wf.selected=[]; render(); updateSelectFloatBar(); }
            else if (mfn === 'enterSelectModeAll') { if (refuseBlocked(item.getAttribute('data-msid'))) return; wf.selectMode=(item.getAttribute('data-msid')+':ALL'); wf.selected=[]; render(); updateSelectFloatBar(); }
            else if (mfn === 'inlineNotRequired') { inlineNotRequired(item.getAttribute('data-itemid'), mmsid); }
            else if (mfn === 'completeItem') { ajScCompleteItem(item.getAttribute('data-id'), mmsid); }
            else if (mfn === 'editPhase') { openPhaseEditModal(mpid); }
            else if (mfn === 'markRequired') { ajScMarkRequired(item.getAttribute('data-id'), item.getAttribute('data-msid')); }
            else if (mfn === 'togglePinDates') { ajScTogglePinDates(item.getAttribute('data-msid'), item.getAttribute('data-pinned')==='true'); }
            else if (mfn === 'deleteMilestone') { openDeleteMsModal(item.getAttribute('data-msid')); }
            else if (mfn === 'deletePhase') { openDeletePhaseModal(item.getAttribute('data-pid')); }
            else if (mfn === 'moveToPhase') { window.dispatchEvent(new CustomEvent('open-move-phase-modal', { detail: { msId: item.getAttribute('data-msid'), currentPhId: item.getAttribute('data-pid') } })); }
            else if (mfn === 'moveRoomToLevel') { openMoveRoomToLevelModal(item.getAttribute('data-roomid'), item.getAttribute('data-levelid')); }
            else if (mfn === 'addRoom') { openAddRoomModal(item.getAttribute('data-levelid'), item.getAttribute('data-conceptid')); }
            else if (mfn === 'deleteRoom') { openDeleteRoomModal(item.getAttribute('data-roomid'), item.getAttribute('data-levelid')); }
            else if (mfn === 'editUnitType') { openEditUnitTypeModal(item.getAttribute('data-unitid')); }
            else if (mfn === 'postMilestone') { window.dispatchEvent(new CustomEvent('open-post-ms-modal', { detail: { msId: item.getAttribute('data-msid') } })); }
            else if (mfn === 'postWithEarlier') { window.dispatchEvent(new CustomEvent('open-post-ms-modal', { detail: { msId: item.getAttribute('data-msid'), withEarlier: true } })); }
            else if (mfn === 'startAhead') { window.dispatchEvent(new CustomEvent('open-start-ahead', { detail: { msId: item.getAttribute('data-msid') } })); }
            else if (mfn === 'unpostMilestone') { openUnpostMilestoneModal(item.getAttribute('data-msid')); }
            else if (mfn === 'toggleHideDoneKey') {
              toggleHideDoneKey(item.getAttribute('data-hdkey') || '');
            }
            else if (mfn === 'resetChecklist') { resetChecklist(item.getAttribute('data-msid')); }
            else if (mfn === 'resetCategory') { resetCategory(item.getAttribute('data-msid'), decodeURIComponent(item.getAttribute('data-cat')||'')); }
            else if (mfn === 'setupBim') {
              try {
                var _bimExisting2 = (ctx.bimCache||[]).map(function(s){ return s.system_template_id; }).filter(Boolean);
                window.dispatchEvent(new CustomEvent('open-setup-bim-modal', { detail: { projectId: wf.zoomProjectId || ctx.recordId || '', existingSystemTemplateIds: _bimExisting2 } }));
              } catch (err) {
                console.error('[setupBim] dispatch failed', err);
                showPill('Could not open Set Up BIM Systems — ' + (err && err.message || 'unknown error'), true);
              }
            }
            else if (mfn === 'addParcel') { window.dispatchEvent(new CustomEvent('open-add-parcel-modal', { detail: { projectId: wf.zoomProjectId || ctx.recordId || '' } })); }
            else if (mfn === 'openPresentation') { window.dispatchEvent(new CustomEvent('open-presentation', { detail: { msId: mmsid } })); }
            else if (mfn === 'makeDrbPresentation') { window.dispatchEvent(new CustomEvent('make-drb-presentation', { detail: { msId: mmsid } })); }
            else if (mfn === 'previewPreDesignReport') { window.dispatchEvent(new CustomEvent('generate-predesign-report', { detail: { projectId: wf.zoomProjectId || ctx.recordId || '', preview: true } })); }
            else if (mfn === 'openSpecEditor') { openSpecEditorForMs(mmsid); }
            else if (mfn === 'openTemplateEditor') {
              var _tplid = item.getAttribute('data-tplid') || '';
              var _tplname = item.getAttribute('data-tplname') || '';
              if (_tplid) window.dispatchEvent(new CustomEvent('open-templates-workspace', { detail: { templateId: _tplid, templateName: _tplname, templateType: 'milestone' } }));
            }
            else if (mfn === 'openEditMs') { window.dispatchEvent(new CustomEvent('open-edit-ms-modal', { detail: { msId: mmsid } })); }
            else if (mfn === 'addToJourney') {
              var _jms = (ctx.state.milestones || []).find(function (m) { return m.id === mmsid; });
              if (!isMilestonePosted(_jms)) {
                alertDialog({ title: 'Post this milestone first', message: 'Only delivered work can appear on the client’s hub. Post this milestone, then add it here — or add it right from the post dialog.' });
              } else {
                window.dispatchEvent(new CustomEvent('open-journey-entry-modal', { detail: { projectId: wf.zoomProjectId || ctx.recordId || '', presetType: 'milestone', presetMilestoneId: mmsid } }));
              }
            }
            else if (mfn === 'addPhaseThread') { window.dispatchEvent(new CustomEvent('open-create-thread-modal', { detail: { projectId: wf.zoomProjectId, phaseId: item.getAttribute('data-pid') || '' } })); }
            else if (mfn === 'uploadRefDocs') { window.dispatchEvent(new CustomEvent('open-reference-doc-modal', { detail: { projectId: wf.zoomProjectId || ctx.recordId || '' } })); }
            else if (mfn === 'addPermit') { window.dispatchEvent(new CustomEvent('open-add-permit-modal', { detail: {} })); }
            else if (mfn === 'permitReportCard') { window.dispatchEvent(new CustomEvent('open-permit-report-card', { detail: {} })); }
            else if (mfn === 'addBidCampaign') { window.dispatchEvent(new CustomEvent('open-add-bid-campaign-modal', { detail: { projectId: wf.zoomProjectId } })); }
            else if (mfn === 'addBidCampaignToMs') { window.dispatchEvent(new CustomEvent('open-add-bid-campaign-modal', { detail: { projectId: wf.zoomProjectId, milestoneAirtableId: mmsid } })); }
            else if (mfn === 'addVeEffortToMs') { window.dispatchEvent(new CustomEvent('open-add-ve-effort-modal', { detail: { projectId: wf.zoomProjectId, milestoneAirtableId: mmsid } })); }
            else if (mfn === 'openGantt') { window.dispatchEvent(new CustomEvent('open-gantt-modal', { detail: { projectId: wf.zoomProjectId } })); }
            else if (mfn === 'addPhase') { window.dispatchEvent(new CustomEvent('open-add-phase-modal', { detail: { projectId: wf.zoomProjectId || ctx.recordId || '' } })); }
            else if (mfn === 'openAddSheets') {
              window.dispatchEvent(new CustomEvent('open-add-sheets', {
                detail: { msId: item.getAttribute('data-msid') || '' }
              }));
            }
            else if (mfn === 'openProgramModel') { window.dispatchEvent(new CustomEvent('open-program-model-modal', { detail: { conceptId: item.getAttribute('data-conceptid') } })); }
            else if (mfn === 'openModeler') { window.open('/modeler?conceptId=' + encodeURIComponent(item.getAttribute('data-conceptid')) + '&projectId=' + encodeURIComponent(ctx.recordId || wf.zoomProjectId || ''), '_blank'); }
            else if (mfn === 'setSelectedConcept') { openSetSelectedConceptModal(item.getAttribute('data-conceptid'), item.getAttribute('data-owner')); }
            else if (mfn === 'editConceptName') { openEditConceptNameModal(item.getAttribute('data-conceptid'), item.getAttribute('data-owner')); }
            else if (mfn === 'copyConcept') { window.dispatchEvent(new CustomEvent('open-copy-concept-modal', { detail: { conceptId: item.getAttribute('data-conceptid'), projectId: ctx.recordId || wf.zoomProjectId || '' } })); }
            else if (mfn === 'deleteConceptItem') { openDeleteConceptModal(item.getAttribute('data-conceptid'), item.getAttribute('data-owner')); }
            else if (mfn === 'copyShareLink') {
              var _shareId = item.getAttribute('data-msid');
              var _shareUrl = window.location.origin + '/share?id=' + encodeURIComponent(_shareId);
              ajScCopyMsLink(_shareUrl);
            }
            else if (mfn === 'enterSelectSheets') { window.dispatchEvent(new Event('enter-select-sheets')); }
            else if (mfn === 'openActiveConceptModeler') { openActiveConceptModeler(item.getAttribute('data-projid')); }
            else if (mfn === 'projectInfo') { window.dispatchEvent(new CustomEvent('open-project-info-modal', { detail: { projId: item.getAttribute('data-projid') } })); }
            else if (mfn === 'manageProjectHold') { window.dispatchEvent(new CustomEvent('open-project-hold-modal', { detail: { projId: item.getAttribute('data-projid') } })); }
            else if (mfn === 'manageProjectCloseout') { window.dispatchEvent(new CustomEvent('open-project-closeout-modal', { detail: { projId: item.getAttribute('data-projid') } })); }
            else if (mfn === 'toggleClosedProjects') { toggleClosedProjectsView(); }
            else if (mfn === 'toggleOnHoldProjects') { toggleOnHoldProjectsView(); }
            else if (mfn === 'expandAll') { expandCollapseDescendants(item.getAttribute('data-key'), true); }
            else if (mfn === 'collapseAll') { expandCollapseDescendants(item.getAttribute('data-key'), false); }
            else if (mfn === 'closeOutPhase') { window.dispatchEvent(new CustomEvent('phase-closeout-confirm', { detail: { phId: mpid } })); }
            else if (mfn === 'reopenPhase') { window.dispatchEvent(new CustomEvent('phase-reopen', { detail: { phId: mpid } })); }
            else if (mfn === 'editPhaseTitle') { window.dispatchEvent(new CustomEvent('phase-edit-title', { detail: { phId: mpid } })); }
          });
        })(menuItems[mi]);
      }
      return;
    }
    else if (fn === 'closeOutPhase') { wf.menuNodeId = null; window.dispatchEvent(new CustomEvent('phase-closeout-confirm', { detail: { phId: el.getAttribute('data-pid') } })); }
    else if (fn === 'reopenPhase') { wf.menuNodeId = null; window.dispatchEvent(new CustomEvent('phase-reopen', { detail: { phId: el.getAttribute('data-pid') } })); render(); }
    else if (fn === 'togglePhaseTime') { togglePhaseTime(el.getAttribute('data-pid'), el.getAttribute('data-val')==='true'); }
    else if (fn === 'editLft') { wf.menuNodeId=null; window.dispatchEvent(new CustomEvent('open-gantt-modal', { detail: { projectId: wf.zoomProjectId } })); }
    else if (fn === 'openNewMs') { wf.menuNodeId=null; window.dispatchEvent(new CustomEvent('open-add-ms-modal', { detail: { phId: el.getAttribute('data-pid') || null } })); }
    else if (fn === 'openNewMsModal') { wf.menuNodeId=null; window.dispatchEvent(new CustomEvent('open-add-ms-modal', { detail: { phId: wf.zoomPhaseId || null } })); }
    else if (fn === 'setupBim') {
      wf.menuNodeId=null;
      try {
        var _bimExisting = (ctx.bimCache||[]).map(function(s){ return s.system_template_id; }).filter(Boolean);
        window.dispatchEvent(new CustomEvent('open-setup-bim-modal', { detail: { projectId: wf.zoomProjectId || ctx.recordId || '', existingSystemTemplateIds: _bimExisting } }));
      } catch (err) {
        console.error('[setupBim] dispatch failed', err);
        showPill('Could not open Set Up BIM Systems — ' + (err && err.message || 'unknown error'), true);
      }
    }
    else if (fn === 'openProgramModel') { wf.menuNodeId=null; window.dispatchEvent(new CustomEvent('open-program-model-modal', { detail: { conceptId: el.getAttribute('data-conceptid') } })); }
    else if (fn === 'openModeler') { wf.menuNodeId=null; window.open('/modeler?conceptId=' + encodeURIComponent(el.getAttribute('data-conceptid')) + '&projectId=' + encodeURIComponent(ctx.recordId || wf.zoomProjectId || ''), '_blank'); }
    else if (fn === 'setSelectedConcept') { wf.menuNodeId=null; openSetSelectedConceptModal(el.getAttribute('data-conceptid'), el.getAttribute('data-owner')); }
    else if (fn === 'editConceptName') { wf.menuNodeId=null; openEditConceptNameModal(el.getAttribute('data-conceptid'), el.getAttribute('data-owner')); }
    else if (fn === 'copyConcept') { wf.menuNodeId=null; window.dispatchEvent(new CustomEvent('open-copy-concept-modal', { detail: { conceptId: el.getAttribute('data-conceptid'), projectId: ctx.recordId || wf.zoomProjectId || '' } })); }
    else if (fn === 'deleteConceptItem') { wf.menuNodeId=null; openDeleteConceptModal(el.getAttribute('data-conceptid'), el.getAttribute('data-owner')); }
    else if (fn === 'moveToPhase') { wf.menuNodeId=null; window.dispatchEvent(new CustomEvent('open-move-phase-modal', { detail: { msId: el.getAttribute('data-msid'), currentPhId: el.getAttribute('data-pid') } })); }
    else if (fn === 'moveRoomToLevel') { wf.menuNodeId=null; openMoveRoomToLevelModal(el.getAttribute('data-roomid'), el.getAttribute('data-levelid')); }
    else if (fn === 'addRoom') { wf.menuNodeId=null; openAddRoomModal(el.getAttribute('data-levelid'), el.getAttribute('data-conceptid')); }
    else if (fn === 'deleteRoom') { wf.menuNodeId=null; openDeleteRoomModal(el.getAttribute('data-roomid'), el.getAttribute('data-levelid')); }
    else if (fn === 'editUnitType') { wf.menuNodeId=null; openEditUnitTypeModal(el.getAttribute('data-unitid')); }
    else if (fn === 'postMilestone') { wf.menuNodeId=null; window.dispatchEvent(new CustomEvent('open-post-ms-modal', { detail: { msId: el.getAttribute('data-msid') } })); }
    else if (fn === 'previewPreDesignReport') { wf.menuNodeId=null; window.dispatchEvent(new CustomEvent('generate-predesign-report', { detail: { projectId: wf.zoomProjectId || ctx.recordId || '', preview: true } })); }
    else if (fn === 'unpostMilestone') { wf.menuNodeId=null; openUnpostMilestoneModal(el.getAttribute('data-msid')); }
    else if (fn === 'inlineNotRequired') { wf.menuNodeId=null; inlineNotRequired(el.getAttribute('data-itemid'), el.getAttribute('data-msid')); }
    else if (fn === 'toggleItem') { var ik=el.getAttribute('data-key'); wf.panels[ik]=wf.panels[ik]?null:true; render(); }
    else if (fn === 'enterSelectMode') { if (refuseBlocked(el.getAttribute('data-msid'))) { wf.menuNodeId=null; render(); return; } wf.selectMode=(el.getAttribute('data-msid')+':'+el.getAttribute('data-cat')); wf.selected=[]; wf.menuNodeId=null; render(); updateSelectFloatBar(); }
    else if (fn === 'enterSelectModeAll') { if (refuseBlocked(el.getAttribute('data-msid'))) { wf.menuNodeId=null; render(); return; } wf.selectMode=(el.getAttribute('data-msid')+':ALL'); wf.selected=[]; wf.menuNodeId=null; render(); updateSelectFloatBar(); }
    else if (fn === 'cancelSelect') { wf.selectMode=null; wf.selected=[]; updateSelectFloatBar(); render(); }
    else if (fn === 'bulkNotRequired') { bulkNotRequired(el.getAttribute('data-msid'), el.getAttribute('data-cat')); }
    else if (fn === 'bulkComplete') { bulkComplete(el.getAttribute('data-msid'), el.getAttribute('data-cat')); }
    else if (fn === 'bulkUncomplete') { bulkUncomplete(el.getAttribute('data-msid'), el.getAttribute('data-cat')); }
    else if (fn === 'bulkMarkRequired') { bulkMarkRequired(el.getAttribute('data-msid'), el.getAttribute('data-cat')); }
    else if (fn === 'setSpoke') { ajScSpoke(el.getAttribute('data-v')); }
    else if (fn === 'selTpl') { ajScSelTpl(el.getAttribute('data-id')); }
    else if (fn === 'setPhase') { ajScSetPhase(el.getAttribute('data-v')); }
    else if (fn === 'signalAction') {
      var si = parseInt(el.getAttribute('data-si'));
      var sig = window._ajSignals && window._ajSignals[si];
      if (sig) {
        if (sig.action === 'focusMs') { focusMsIdx = sig.msIdx; render(); }
      }
    }
    else if (fn === 'focusFromQueue') { var qi = parseInt(el.getAttribute('data-idx')); focusMsIdx = qi; clOpen=false; render(); }
    else if (fn === 'resetToTop') { focusMsIdx = 0; render(); }
    else if (fn === 'backToSignals') { focusMsIdx = null; render(); }
    else if (fn === 'openPlanning') { openPlanningModal(el.getAttribute('data-msid')); }
    else if (fn === 'openNewMs') { window.dispatchEvent(new CustomEvent('open-add-ms-modal', { detail: { phId: null } })); }
    else if (fn === 'skipItem') { ajScSkipItem(el.getAttribute('data-id'), el.getAttribute('data-msid')); }

    else if (fn === 'bcPhase') { outlineState.focusPhaseId=el.getAttribute('data-pid'); outlineState.focusMsId=null; render(); }
    else if (fn === 'togglePhaseTime') { togglePhaseTime(el.getAttribute('data-pid'), el.getAttribute('data-val')==='true'); }
    else if (fn === 'undoPhaseToggle') { var ph2=getPhaseById(el.getAttribute('data-pid')); if(ph2){ph2.fields['Allow Time Entries']=el.getAttribute('data-prev')==='true'; outlineState.resolvedPhases[el.getAttribute('data-pid')]=false; apiPatch(TBL_PH,el.getAttribute('data-pid'),{'Allow Time Entries':el.getAttribute('data-prev')==='true'},function(){}); render();} }
    else if (fn === 'completeItem') { ajScCompleteItem(el.getAttribute('data-id'), el.getAttribute('data-msid')); }
    else if (fn === 'setResp') { ajScSetResp(el.getAttribute('data-id'), el.getAttribute('data-resp'), el.getAttribute('data-msid')); }
    else if (fn === 'selectItem') { /* handled by wireOutline checkbox listener */ }
  });

}
